import { buildEndOfDayLoadTargetSuspensionDialog } from "../utils/endOfDayLoadTargetSuspension.js";
import { buildCompartmentSharingConfirmationContent } from "../utils/compartmentSharingChallenge.js";
import DataManagerTruckPeriodControls from "./DataManagerTruckPeriodControls.jsx";
import { DataManagerFleetFilter, DataManagerNewTruckKind, DataManagerNewUnitNote, DataManagerRetirementNotes, DataManagerTypeBadge } from "./DataManagerFleetUnits.jsx";
import { DataManagerFleetRetirementButton, DataManagerFleetRetirementDialog } from "./DataManagerFleetRetirement.jsx";
import { fleetFilterCounts, isDompeurTruck, localDateKey, retirementDateInTab, tonsLabel, trailerSelectionDeletion, truckSelectionDeletion, truckTypeChangeNotice, truckUnitKind, trucksForTab } from "../utils/dataManagerFleetUnits.js";
import DataManagerClientTypeFilter from "./DataManagerClientTypeFilter.jsx";
import { CLIENT_TYPE_URL_PARAM, countClientTypes, matchesClientType, resolveClientTypeFilter } from "../utils/clientCorrespondenceTypes.js";
import { buildClientRenameImpactItems, isClientInUse } from "../utils/clientRenameImpact.js";
import { truckColumnsForPeriod, truckIsActiveForView } from "../utils/dataManagerTruckViews.js";
import { applyNewTruckDraftChange, TRUCK_CREATION_FIELDS, truckCreationDefaults } from "../utils/truckCreationDefaults.js";
import useConsolidationOrderNavigation from "../hooks/useConsolidationOrderNavigation.jsx";
import { orderNavigationScope, readOrdersReturn } from "../utils/consolidationOrderNavigation.js";
import DriverAssignmentEditor, { DriverEmployeeSelect } from "./DriverEmployeeSelect.jsx";
import { MISSING_TRACTOR_LABEL } from "../lib/tractorAssignmentsApi.js";
import OrderDuplicateConfirmationDialog from "./OrderDuplicateConfirmationDialog.jsx";
import { addressValidationLabel } from "../utils/addressRoutingValidation.js";
import React, { startTransition, useDeferredValue } from "react";
import useMediaQuery, { MOBILE_QUERY } from "../hooks/useMediaQuery.js";
import MobileRecordCard from "./MobileRecordCard.jsx";
import DataManagerProductSelect from "./DataManagerProductSelect.jsx";
import DataManagerProductCatalogTab from "./DataManagerProductCatalogTab.jsx";
import { useNavigate, useSearchParams } from "react-router";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Copy,
  Database,
  Download,
  ExternalLink,
  History,
  LoaderCircle,
  Package,
  Pin,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Tag,
  Trash2,
  Truck,
  Upload,
} from "lucide-react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import ColumnHeaderDragOverlay from "./ColumnHeaderDragOverlay.jsx";
import DataManagerDispatchSavesImportDialog from "./DataManagerDispatchSavesImportDialog.jsx";
import DataManagerOrdersTab from "./DataManagerOrdersTab.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import "./DataManagerPage.css";
import "./TraceabilityBanner.css";
import useAccess from "../hooks/useAccess.jsx";
import {
  canAccessDataManagerAdminTransfer as canAccessDataManagerAdminTransferForRole,
  canExportDataManagerTable as canExportDataManagerTableForRole,
  canBulkDeleteOrders as canBulkDeleteOrdersForRole,
  canClearDeliveredOrders as canClearDeliveredOrdersForRole,
  canDeleteDeliveredOrders as canDeleteDeliveredOrdersForRole,
  canEditOrders as canEditOrdersForRole,
  canMarkOrdersDelivered as canMarkOrdersDeliveredForRole,
  canManageDataManagerImports as canManageDataManagerImportsForRole,
  canWriteOrderActualTonnage as canWriteOrderActualTonnageForRole,
  canWriteOrderDeliveryDriver as canWriteOrderDeliveryDriverForRole,
  hasPermission,
} from "../lib/accessControl.js";
import { previewClientCorrespondenceRename } from "../lib/dataManagerApi.js";
import { HoverCard, useToast } from "./ResultsPrimitives.jsx";
import BillToSelect, { BillToCompaniesContext } from "./BillToSelect.jsx";
import { shellControlPastilleClassName } from "./resultsPrimitives/shared.js";
import useDataManagerPage from "../hooks/useDataManagerPage.js";
import useDataManagerColumnPreferences from "../hooks/useDataManagerColumnPreferences.js";
import useDataManagerGridFrozenPanes from "../hooks/useDataManagerGridFrozenPanes.js";
import DataManagerOrdersColumnContextMenu from "./DataManagerOrdersColumnContextMenu.jsx";
import {
  buildFrozenColumnCellStyle,
  DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME,
  DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME,
  DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME,
  DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH,
} from "../utils/dataManagerFrozenPanes.js";
import "./DataManagerFrozenGrid.css";
import useDispatchDayCalendarContext from "../hooks/useDispatchDayCalendarContext.js";
import useDataManagerTableSortState from "../hooks/useDataManagerTableSortState.js";
import useDataManagerTableScrollRestore from "../hooks/useDataManagerTableScrollRestore.js";
import useSortableColumnHeaderDrag from "../hooks/useSortableColumnHeaderDrag.js";
import { buildCoopClientNames, buildOrderGridColumnStyle, DATA_MANAGER_RECENT_BROWSE_ORDERS_LIMIT, normalizeClientCorrespondenceImportKey, isMissingClientCorrespondenceClientCode, addressHasCivicNumber, isMissingCivicNumberConfirmationCancelledError, MissingCivicNumberConfirmationCancelledError, isPointerInsideConfirmationModal } from "../hooks/useDataManagerOrdersUtils.js";
import useMissingCivicNumberConfirmation from "../hooks/useMissingCivicNumberConfirmation.jsx";
import { isDiagnosticsActive, recordDiagnosticsEvent } from "../hooks/useDiagnostics.js";
import {
  DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME,
  DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME,
  DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME,
  resolveDataManagerActiveSortState,
  resolveDataManagerHeaderAriaSort,
  resolveNextDataManagerSortState,
  sortDataManagerRows,
} from "../utils/dataManagerTableSort.js";
import {
  enrichResultsUrlWithStoredDispatchContext,
  normalizeDispatchPeriod,
  readLastDispatchContext,
  writeLastDispatchContext,
} from "../utils/dispatchDateUrls.js";
import { readDispatchSavesExportFile } from "../utils/dispatchSavesImport.js";
import { DATA_MANAGER_ORDER_APPROVAL_VIEW_PENDING } from "../utils/dataManagerOrderApprovals.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { matchOrderSearch, resolveActiveOrderSearchQuery } from "../utils/dataManagerOrderSearch.js";
import {
  buildDataManagerGridRowClassName,
  DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME,
  handleDataManagerGridRowClick,
  handleEditableGridKeyDown,
} from "../utils/dataManagerGridRowStyles.js";
import {
  buildDataManagerGridTableStyle,
  DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH,
  DATA_MANAGER_GRID_COMPARTMENT_POSITION_COLUMN_WIDTH,
  DATA_MANAGER_GRID_SELECTION_COLUMN_KEY,
  DATA_MANAGER_GRID_SELECTION_COLUMN_WIDTH,
  DATA_MANAGER_RESIZABLE_SELECTION_COLUMN,
  getDataManagerGridTableWidth,
  resolveDataManagerGridColumnWidth,
} from "../utils/dataManagerGridTableLayout.js";
import {
  DATA_MANAGER_HISTORY_FIELD_LABELS,
  formatDataManagerAuditFieldLabel,
} from "../utils/dataManagerAuditLabels.js";
import {
  formatDataManagerAuditOrderNumberLabel,
  formatDataManagerAuditSourceLabel,
  resolveDataManagerClientAuditAction,
  resolveDataManagerClientAuditSummaryChange,
  resolveDataManagerCreatedOrderCount,
  resolveDataManagerAuditFieldChanges,
  resolveDataManagerAuditFieldNames,
  resolveDataManagerHistorySortState,
} from "../utils/dataManagerAuditPresentation.js";

const PAGE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("data_manager");
const PANEL_CLASS_NAME = "min-w-0 rounded-[20px] border border-black/6 bg-white px-3.5 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)]";
const PRIMARY_ACTION_CLASS_NAME = "inline-flex items-center gap-1.5 rounded-full bg-accent px-3.5 py-1.75 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_ACTION_CLASS_NAME = `inline-flex items-center gap-1.5 rounded-full ${shellControlPastilleClassName} bg-white px-3.5 py-1.75 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60`;
const DANGER_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.75 text-[11px] font-semibold text-rose-700 transition-colors duration-150 hover:border-rose-300 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT_CLASS_NAME = `h-10 w-full rounded-[14px] ${shellControlPastilleClassName} bg-white px-3 text-[12.5px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent`;
const COMPACT_TOOLBAR_BUTTON_CLASS_NAME = `inline-flex h-8 items-center gap-1.5 rounded-full ${shellControlPastilleClassName} bg-white px-3 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60`;
const COMPACT_TAB_BUTTON_CLASS_NAME = "inline-flex h-8 items-center rounded-[10px] border px-3 text-[12px] font-semibold transition-colors duration-150";
const GRID_INPUT_CLASS_NAME = `h-7 w-full min-w-0 rounded-[8px] ${shellControlPastilleClassName} bg-white px-2 text-[11px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent`;
const GRID_COMPARTMENT_POSITION_CELL_CLASS_NAME = "border-b border-slate-100 px-1 py-1.5 align-middle text-center";
const GRID_COMPARTMENT_POSITION_INPUT_CLASS_NAME = `h-7 w-full min-w-0 rounded-[4px] ${shellControlPastilleClassName} bg-white text-center text-[11px] tabular-nums text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent`;
const GRID_VALUE_CLASS_NAME = "block max-w-full truncate text-[11px] leading-5 text-slate-700";
const GRID_SELECTION_INPUT_CLASS_NAME = "h-3.5 w-3.5 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40";
const GRID_HEADER_CELL_CLASS_NAME = `border-b ${shellControlPastilleClassName} px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-800`;
const GRID_CELL_CLASS_NAME = "border-b border-slate-100 px-2 py-1.5 align-middle";
const GRID_PRIMARY_ACTION_CLASS_NAME = "inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const GRID_SECONDARY_ACTION_CLASS_NAME = `inline-flex items-center gap-1 rounded-full ${shellControlPastilleClassName} bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60`;
const GRID_ACTIONS_ROW_CLASS_NAME = "flex flex-nowrap items-center gap-1 whitespace-nowrap";
const GRID_SCROLL_WRAP_CLASS_NAME = "data-manager-viewport-grid max-w-full overflow-auto rounded-[4px] border-[var(--shell-control-pastille-border,#e2e8f0)]";
const GRID_SCROLL_INNER_CLASS_NAME = "min-w-full";
const DATA_MANAGER_GRID_SCROLL_CONTAINER_CLASS_NAME = "data-manager-viewport-grid max-h-[min(52rem,calc(100dvh-19rem))] min-w-0 overflow-auto overscroll-contain rounded-lg border border-slate-200/90";
const DATA_MANAGER_GRID_STICKY_HEADER_CLASS_NAME = "sticky top-0 z-10 bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.95))] shadow-[0_1px_0_rgba(226,232,240,0.95)]";
const SORTABLE_HEADER_BUTTON_CLASS_NAME = "inline-flex w-full items-center gap-1 text-left transition-colors duration-150";
const ANCHORED_ADDRESS_TOAST_MARGIN_PX = 12;
const ANCHORED_ADDRESS_TOAST_ESTIMATED_HEIGHT_PX = 148;
const ANCHORED_ADDRESS_TOAST_ESTIMATED_WIDTH_PX = 548;
const CLIENT_ADDRESS_HOVERCARD_DELAY_MS = 350;
const VEHICLE_DEFAULT_FOCUS_KEY = "truck_name";
const CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY = "client_code";
const EMPLOYEE_DEFAULT_FOCUS_KEY = "employee_name";
const PRODUCT_DEFAULT_FOCUS_KEY = "name";
const COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY = "compartment_name";
const CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY = "address";
const CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL = "Adresse";
const CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_KEY = "client_code";
const CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_LABEL = "# Client";
const CLIENT_CORRESPONDENCE_MEUNERIE_DEPENDENT_FIELD_KEYS = new Set([
  "meunerie_loading_extra_minutes",
  "loading_opening_time",
  "loading_closing_time",
]);
const CLIENT_CORRESPONDENCE_MEUNERIE_FIELDS_DISABLED_TITLE = "Disponible uniquement si Meunerie est Oui.";
const DATA_MANAGER_EXPORT_URLS = {
  client_correspondences: "/api/data-manager/export/client-correspondences",
  compartments: "/api/data-manager/export/compartments",
  dispatch_saves: "/api/data-manager/export/dispatch-saves",
  employees: "/api/data-manager/export/employees",
  products: "/api/data-manager/export/products",
  orders: "/api/data-manager/export/orders",
  trucks: "/api/data-manager/export/trucks",
};
const DATA_MANAGER_EXPORT_FILE_NAMES = {
  client_correspondences: "data-manager-client-correspondences-export.xlsx",
  compartments: "data-manager-remorques-export.xlsx",
  dispatch_saves: "data-manager-dispatch-saves-export.json",
  employees: "data-manager-chauffeurs-export.xlsx",
  products: "data-manager-products-export.xlsx",
  orders: "data-manager-orders-export.xlsx",
  trucks: "data-manager-camions-export.xlsx",
};
const RAILWAY_SYNC_TABLE_ORDER = ["products", "client_correspondences", "sollio_delivery_addresses", "employees", "trucks", "compartments", "orders"];
const RAILWAY_SYNC_TABLE_LABELS = {
  products: "Codes produit",
  client_correspondences: "Clients",
  sollio_delivery_addresses: "Adresses variables Sollio",
  employees: "Chauffeurs",
  trucks: "Camions",
  compartments: "Remorques",
  orders: "Commandes",
};
const DRIVERS_TAB_KEY = "drivers";
const ACTIVE_TAB_STORAGE_KEY = "grain-saas:data-manager:active-tab:v1";
const PRIMARY_TAB_ITEMS = [
  {
    badge: "Phase 1A",
    description: "Création, modification, annulation et réouverture des commandes sans changer le shell Data Manager.",
    icon: ClipboardList,
    key: "orders",
    label: "Commandes",
  },
  {
    badge: "CRUD inline",
    description: "Création, modification et suppression des camions sans revenir dans Dispatch.",
    icon: Truck,
    key: "vehicles",
    label: "Camions",
  },
  {
    badge: "Profil cuves",
    description: "Édition groupée des remorques et de leurs compartiments, avec stale et historique conservés.",
    icon: Package,
    key: "compartments",
    label: "Remorques",
  },
  {
    badge: "Heures",
    description: "Fiches des chauffeurs : courriel, quart et heures Jour et Soir ; une heure vide reprend la valeur par défaut des Réglages.",
    icon: ShieldCheck,
    key: DRIVERS_TAB_KEY,
    label: "Chauffeurs",
  },
  {
    badge: "Catalogue",
    description: "Noms des produits proposés dans les commandes et les codes produit.",
    icon: Tag,
    key: "product_catalog",
    label: "Produits",
  },
  {
    badge: "Sollio",
    description: "Clients utilisés par la conversion et éditables directement dans le Data Manager.",
    icon: Database,
    key: "correspondences",
    label: "Clients",
  },
  {
    badge: "Sollio",
    description: "Correspondances Description Sollio vers produit utilisées lors de la conversion.",
    icon: Tag,
    key: "products",
    label: "Codes produit",
  },
];
// Camions : ce qui suit le camion lors d'un échange et son chauffeur par défaut. Les heures sont dans
// Chauffeurs (valeur par défaut des Réglages) ; les champs de la remorque (la ligne TRK), dont la capacité et les débits, dans Remorques.
// Actif Jour/Soir : disponibilité du camion seulement ; la remorque qu'il tire ce jour-là passe à « Camion à choisir » s'il est inactif.
// Dompeur (une seule pièce) : ses champs de remorque se modifient sur sa ligne de Camions ; vides et non modifiables ailleurs.
// Un dompeur n'a ni aspirateur ni produit associé : le serveur les garde à « non », 0 min et vide.
const DOMPEUR_GRID_COLUMNS = [
  { key: "capacity_tons", label: "Capacité t", min: "0", step: "0.1", type: "number", dompeurOnly: true, widthClassName: "min-w-22", defaultWidth: 88, minWidth: 56 },
  { key: "is_initial_preload_enabled", label: "Pool preload", type: "boolean", dompeurOnly: true, widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "evening_radius_km", label: "Rayon Soir (km)", min: "0.1", step: "0.1", type: "number", dompeurOnly: true, widthClassName: "min-w-24", defaultWidth: 96, minWidth: 80 },
  { key: "loading_rate_tons_per_hour", label: "Charg. t/h", min: "0.1", step: "0.1", type: "number", dompeurOnly: true, widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
  { key: "unloading_rate_tons_per_hour", label: "Déch. t/h", min: "0.1", step: "0.1", type: "number", dompeurOnly: true, widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
];

const VEHICLE_GRID_COLUMNS = [
  { key: "truck_name", label: "Nom", placeholder: "1110", type: "text", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 72 },
  { key: "compartment_name", label: "Remorque habituelle", type: "readonly", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 72 },
  { key: "is_active", label: "Actif Jour", title: "Non : ce camion ne roule pas le Jour. La remorque qu'il tire passe à « Camion à choisir » ; vide, elle sort du Dispatch.", type: "boolean", widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
  { key: "evening_is_active", label: "Actif Soir", title: "Non : ce camion ne roule pas le Soir. La remorque qu'il tire passe à « Camion à choisir » ; vide, elle sort du Dispatch.", type: "boolean", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 64 },
  { key: "driver_employee_id", label: "Chauffeur par défaut", type: "employee", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "evening_driver_employee_id", label: "Défaut Soir", type: "employee", widthClassName: "min-w-20", defaultWidth: 80, minWidth: 64 },
  { key: "axle_count", label: "Essieux", min: "1", step: "1", placeholder: "4", type: "number", widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
  { key: "ontario", label: "Ontario", type: "text", options: ["non", "oui"], widthClassName: "min-w-16", defaultWidth: 64, minWidth: 56 },
  ...DOMPEUR_GRID_COLUMNS,
  { key: "fuel_cost_per_km", label: "$/km", min: "0", step: "0.01", type: "number", widthClassName: "min-w-12", defaultWidth: 48, minWidth: 48 },
  { key: "waiting_cost_per_min", label: "$/min", min: "0", step: "0.01", type: "number", widthClassName: "min-w-12", defaultWidth: 48, minWidth: 48 },
  { key: "avg_speed_kmh", label: "Vitesse", min: "0", step: "0.1", type: "number", widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
  { key: "inspection_time_minutes", label: "Inspect. min", min: "0", step: "1", type: "number", widthClassName: "min-w-14", defaultWidth: 56, minWidth: 48 },
  { key: "departure_address", label: "Adresse départ", placeholder: "Adresse", type: "text", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
];

// Colonnes visibles seulement avec leur droit de lecture (admin par défaut).
const GRID_COLUMN_READ_PERMISSION_BY_KEY = {
  avg_speed_kmh: "data_manager.truck_speed.read",
  loading_rate_tons_per_hour: "data_manager.truck_load_rates.read",
  unloading_rate_tons_per_hour: "data_manager.truck_load_rates.read",
  meunerie_loading_extra_minutes: "data_manager.meunerie_extra_time.read",
};

function withoutUnauthorizedColumns(columns, access) {
  return columns.filter((column) => {
    const permissionKey = GRID_COLUMN_READ_PERMISSION_BY_KEY[column.key];
    return !permissionKey || hasPermission(access, permissionKey);
  });
}

const DATA_MANAGER_TAB_WRITE_PERMISSIONS = {
  vehicles: "data_manager.trucks.write",
  compartments: "data_manager.compartments.write",
  employees: "data_manager.employees.write",
  products: "data_manager.products.write",
  correspondences: "data_manager.client_correspondences.write",
};

const VEHICLE_GRID_SELECTION_COLUMN_WIDTH = 44;
const VEHICLE_GRID_ACTIONS_COLUMN_WIDTH = 112;
// Type (Citerne ou Dompeur) en tête, quelles que soient les préférences de colonnes enregistrées (une nouvelle
// colonne irait à la fin). Le type se change ici, dans la vue Jour. Le code TRK de la ligne n'est pas affiché.
const TYPE_LEADING_COLUMN = { key: "truck_type", label: "Type", width: 136, fixed: true, widthClassName: "min-w-32" };
const VEHICLE_GRID_LEADING_COLUMNS = [
  TYPE_LEADING_COLUMN,
];

const DATA_MANAGER_RESIZABLE_FIXED_COLUMNS = [DATA_MANAGER_RESIZABLE_SELECTION_COLUMN];

function getVehicleGridTableWidth(resolvedColumns = [], selectionColumnWidth = DATA_MANAGER_GRID_SELECTION_COLUMN_WIDTH) {
  return getDataManagerGridTableWidth(resolvedColumns, [
    selectionColumnWidth,
    TYPE_LEADING_COLUMN.width,
    VEHICLE_GRID_ACTIONS_COLUMN_WIDTH,
  ]);
}

function buildDataManagerGridSelectionColumnStyle(width = DATA_MANAGER_GRID_SELECTION_COLUMN_WIDTH) {
  return buildOrderGridColumnStyle(Math.max(0, Math.round(Number(width) || DATA_MANAGER_GRID_SELECTION_COLUMN_WIDTH)));
}

function resolveSelectionColumnWidth(columnPrefs) {
  return columnPrefs.resolvedFixedColumnWidths?.[DATA_MANAGER_GRID_SELECTION_COLUMN_KEY]
    || DATA_MANAGER_GRID_SELECTION_COLUMN_WIDTH;
}

const CLIENT_CORRESPONDENCE_GRID_COLUMNS = [
  { key: "client_code", label: "# Client", placeholder: "EXE01", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 72 },
  { key: "client_name", label: "Nom client", placeholder: "FERME EXEMPLE", widthClassName: "min-w-32", defaultWidth: 128, minWidth: 96 },
  { key: "correspondence_name", label: "Nom correspondance", placeholder: "FERME EXEMPLE INC.", widthClassName: "min-w-40", defaultWidth: 160, minWidth: 120 },
  { key: "courriel", label: "Courriel", placeholder: "contact@exemple.com", widthClassName: "min-w-36", defaultWidth: 144, minWidth: 108 },
  { key: "telephone", label: "Téléphone", placeholder: "514-555-0100", widthClassName: "min-w-28", defaultWidth: 112, minWidth: 84 },
  { key: "indication", label: "Indication", placeholder: "Sonner à la réception", widthClassName: "min-w-40", defaultWidth: 160, minWidth: 120 },
  { key: "address", label: "Adresse", placeholder: "123 rang Exemple, Ville", widthClassName: "min-w-80", defaultWidth: 320, minWidth: 240 },
  { key: "is_meunerie", label: "Meunerie", type: "boolean", widthClassName: "min-w-16", defaultWidth: 64, minWidth: 48 },
  { key: "is_coop", label: "Entreprise facturée", type: "boolean", widthClassName: "min-w-16", defaultWidth: 80, minWidth: 48 },
  { key: "bill_to", label: "Facturer à", type: "bill_to", widthClassName: "min-w-40", defaultWidth: 176, minWidth: 120 },
  { key: "meunerie_loading_extra_minutes", label: "Surtemps meunerie (min)", type: "number", widthClassName: "min-w-15", defaultWidth: 60, minWidth: 48 },
  { key: "loading_opening_time", label: "Heure ouverture chargement", type: "time", widthClassName: "min-w-20", defaultWidth: 80, minWidth: 64 },
  { key: "loading_closing_time", label: "Heure fermeture chargement", type: "time", widthClassName: "min-w-20", defaultWidth: 80, minWidth: 64 },
];

const EMPLOYEE_GRID_COLUMNS = [
  // Onglet Chauffeurs : toute la fiche employé ; les nouvelles fiches sont des chauffeurs.
  { key: "employee_name", label: "Nom", placeholder: "Julie Roy", widthClassName: "min-w-32", defaultWidth: 160, minWidth: 96 },
  { key: "courriel", label: "Courriel", placeholder: "nom@exemple.com", widthClassName: "min-w-24", defaultWidth: 176, minWidth: 72 },
  { key: "shift", label: "Quart", type: "shift", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "start_time", label: "Début Jour", type: "time", widthClassName: "min-w-28", defaultWidth: 112, minWidth: 96 },
  { key: "max_work_hours", label: "Heures max Jour", type: "number", min: "0.1", step: "0.1", unit: "h", placeholder: "défaut", widthClassName: "min-w-32", defaultWidth: 136, minWidth: 112 },
  { key: "evening_start_time", label: "Début Soir", type: "time", widthClassName: "min-w-28", defaultWidth: 112, minWidth: 96 },
  { key: "evening_max_work_hours", label: "Heures max Soir", type: "number", min: "0.1", step: "0.1", unit: "h", placeholder: "défaut", widthClassName: "min-w-32", defaultWidth: 136, minWidth: 112 },
  // Pause dîner du Dispatch ; vide : l'heure par défaut de Paramètres système.
  { key: "lunch_time", label: "Heure du dîner", type: "time", widthClassName: "min-w-28", defaultWidth: 112, minWidth: 96 },
  // Compte du portail chauffeur, lu sur la fiche : il se gère dans « Comptes chauffeurs ».
  { key: "driver_user_id", label: "Compte portail", type: "readonly", widthClassName: "min-w-28", defaultWidth: 112, minWidth: 96 },
];

const PRODUCT_GRID_COLUMNS = [
  { key: "name", label: "Nom", placeholder: "Croissance #2", widthClassName: "min-w-32", defaultWidth: 128, minWidth: 96 },
  { key: "correspondence", label: "Correspondance", placeholder: "CROIS #2 GRIL POD MON-BMD C VRAC", widthClassName: "min-w-56", defaultWidth: 224, minWidth: 168 },
  { key: "article_number", label: "No Article", placeholder: "12345", widthClassName: "min-w-20", defaultWidth: 80, minWidth: 64 },
  { key: "product_value", label: "Produit", placeholder: "Poulet", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 72 },
  { key: "density", label: "Densité", min: "0", step: "0.01", type: "number", widthClassName: "min-w-16", defaultWidth: 64, minWidth: 56 },
];

// Remorques (que des citernes) : la ligne TRK et ses compartiments ; ces champs restent avec la remorque lors d'un échange
// de camion. L'attelage se lit dans « Camion habituel ». Le code TRK de la ligne n'est pas affiché.
const COMPARTMENT_MATRIX_BASE_COLUMNS = [
  { key: "compartment_name", label: "Remorque", placeholder: "W-20", type: "text", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "truck_name", label: "Camion habituel", type: "readonly", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "aspirator", label: "Aspirateur", type: "text", options: ["non", "oui"], widthClassName: "min-w-16", defaultWidth: 64, minWidth: 56 },
  { key: "aspirator_service_minutes", label: "Temps aspirateur (min)", min: "0", step: "1", type: "number", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "preferred_grain_type", label: "Produit associé", type: "product", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "is_initial_preload_enabled", label: "Pool preload", type: "boolean", widthClassName: "min-w-18", defaultWidth: 72, minWidth: 56 },
  { key: "evening_radius_km", label: "Rayon Soir (km)", min: "0.1", step: "0.1", type: "number", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 80 },
  { key: "capacity_tons", label: "Capacité t", min: "0", step: "0.1", type: "number", widthClassName: "min-w-22", defaultWidth: 88, minWidth: 56 },
  { key: "loading_rate_tons_per_hour", label: "Charg. t/h", min: "0.1", step: "0.1", type: "number", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 48 },
  { key: "unloading_rate_tons_per_hour", label: "Déch. t/h", min: "0.1", step: "0.1", type: "number", widthClassName: "min-w-24", defaultWidth: 96, minWidth: 48 },
];
const COMPARTMENT_MATRIX_EDITABLE_KEYS = COMPARTMENT_MATRIX_BASE_COLUMNS
  .filter((column) => column.type && column.type !== "readonly")
  .map((column) => column.key);

// Une commande prend des compartiments entiers : la remorque ne porte pas plus que leur total, même sous sa capacité.
function compartmentTotalTons(truck) {
  const total = (Array.isArray(truck?.compartments) ? truck.compartments : [])
    .reduce((sum, compartment) => sum + (Number(compartment?.capacity_tons) || 0), 0);
  return Math.round(total * 100) / 100;
}

const HISTORY_ACTION_LABELS = {
  approve_for_billing: "Approbation facturation",
  create: "Création",
  delete: "Suppression",
  delete_tractor: "Suppression du camion",
  execution: "Tonnages réels",
  import_merge: "Import fusion",
  import_replace: "Import remplacement",
  reinstate: "Remise dans la flotte",
  retire: "Retrait de la flotte",
  update: "Mise à jour",
};
// Actions nommées devant chaque champ de la ligne de traçabilité.
const HISTORY_PREFIXED_ACTIONS = new Set(["create", "delete", "delete_tractor", "execution", "reinstate", "retire"]);

const HISTORY_PREVIEW_LIMIT = 10;

const HISTORY_FIELD_LABELS = DATA_MANAGER_HISTORY_FIELD_LABELS;

function readSavedActiveTab(userId) {
  try {
    return userId ? window.sessionStorage.getItem(`${ACTIVE_TAB_STORAGE_KEY}:${userId}`) || "" : "";
  } catch {
    return "";
  }
}

function saveActiveTab(userId, tab) {
  try {
    if (userId) window.sessionStorage.setItem(`${ACTIVE_TAB_STORAGE_KEY}:${userId}`, tab);
  } catch {
    // Le lien courant reste utilisable si le stockage du navigateur est bloqué.
  }
}

function resolveActiveTab(searchParams, savedTab = "") {
  const explicitTab = typeof searchParams?.get === "function"
    ? String(searchParams.get("tab") || "").trim().toLowerCase()
    : "";
  const requestedTab = explicitTab || String(savedTab || "").trim().toLowerCase();

  // L'ancien onglet Employés (?tab=employees) ouvre Chauffeurs : même table.
  if (requestedTab === DRIVERS_TAB_KEY || requestedTab === "employees") {
    return "employees";
  }

  return PRIMARY_TAB_ITEMS.some((item) => item.key === requestedTab) ? requestedTab : "orders";
}

function resolveHistoryView(searchParams) {
  const requestedView = typeof searchParams?.get === "function"
    ? String(searchParams.get("history") || "").trim().toLowerCase()
    : "";

  return requestedView === "full" ? "full" : "preview";
}

function resolveOrdersSearchQuery(searchParams) {
  return typeof searchParams?.get === "function"
    ? String(searchParams.get("orderSearch") || "")
    : "";
}

function resolveOrderBrowseDateQuery(searchParams) {
  return typeof searchParams?.get === "function"
    ? String(searchParams.get("orderDate") || "").trim()
    : "";
}

function resolveOrderScopeQuery(searchParams) {
  const requestedScope = typeof searchParams?.get === "function"
    ? String(searchParams.get("orderScope") || "").trim().toLowerCase()
    : "";

  if (requestedScope === "browse" || requestedScope === "dispatch") {
    return requestedScope;
  }

  return "";
}

function resolveContextualDispatchDate(searchParams) {
  const dispatchDateFromSearch = typeof searchParams?.get === "function"
    ? String(searchParams.get("dispatch_date") || "").trim()
    : "";

  if (dispatchDateFromSearch) {
    return dispatchDateFromSearch;
  }

  return readLastDispatchContext().dispatch_date;
}

function resolveContextualDispatchPeriod(searchParams) {
  const dispatchPeriodFromSearch = typeof searchParams?.get === "function"
    ? String(searchParams.get("dispatch_period") || "").trim()
    : "";

  if (dispatchPeriodFromSearch) {
    return normalizeDispatchPeriod(dispatchPeriodFromSearch);
  }

  return readLastDispatchContext().dispatch_period;
}

function resolveMutationErrorMessage(error, fallbackMessage) {
  return error instanceof Error && error.message
    ? error.message
    : fallbackMessage;
}

function updateGridSelectionState(currentSelection, rowId, isSelected) {
  if (!rowId) {
    return currentSelection;
  }

  if (isSelected) {
    if (currentSelection[rowId]) {
      return currentSelection;
    }

    return {
      ...currentSelection,
      [rowId]: true,
    };
  }

  if (!currentSelection[rowId]) {
    return currentSelection;
  }

  const nextSelection = { ...currentSelection };
  delete nextSelection[rowId];
  return nextSelection;
}

function toggleVisibleGridSelections(currentSelection, rowIds, shouldSelect) {
  if (!Array.isArray(rowIds) || rowIds.length === 0) {
    return currentSelection;
  }

  if (!shouldSelect) {
    const nextSelection = { ...currentSelection };
    rowIds.forEach((rowId) => {
      delete nextSelection[rowId];
    });
    return nextSelection;
  }

  const nextSelection = { ...currentSelection };
  rowIds.forEach((rowId) => {
    nextSelection[rowId] = true;
  });
  return nextSelection;
}

function pruneGridSelections(currentSelection, allowedIds) {
  let hasChanged = false;
  const nextSelection = {};

  Object.entries(currentSelection).forEach(([rowId, isSelected]) => {
    if (!isSelected) {
      return;
    }

    if (allowedIds.has(rowId)) {
      nextSelection[rowId] = true;
      return;
    }

    hasChanged = true;
  });

  return hasChanged ? nextSelection : currentSelection;
}

function stopGridSelectionPropagation(event) {
  event.stopPropagation();
}

function toTimeInputValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const plainHourMatch = trimmedValue.match(/^(\d{1,2})$/);
    if (plainHourMatch) {
      const hours = Number(plainHourMatch[1]);
      if (Number.isInteger(hours) && hours >= 0 && hours < 24) {
        return `${String(hours).padStart(2, "0")}:00`;
      }
    }

    const plainTimeMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (plainTimeMatch) {
      const hours = Number(plainTimeMatch[1]);
      const minutes = Number(plainTimeMatch[2]);
      if (Number.isInteger(hours) && Number.isInteger(minutes) && hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }
    }

    const embeddedTimeMatch = trimmedValue.match(/[T\s](\d{1,2}):(\d{2})(?::\d{2})?/);
    if (embeddedTimeMatch) {
      const hours = Number(embeddedTimeMatch[1]);
      const minutes = Number(embeddedTimeMatch[2]);
      if (Number.isInteger(hours) && Number.isInteger(minutes) && hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }
    }
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "";
  }

  return `${String(parsedDate.getHours()).padStart(2, "0")}:${String(parsedDate.getMinutes()).padStart(2, "0")}`;
}

function createTruckDraft(truck = null, referenceTrucks = []) {
  const defaults = truckCreationDefaults(truck?.truck_type, referenceTrucks);
  return {
    ...(!truck ? { automatic_creation_fields: [...TRUCK_CREATION_FIELDS] } : {}),
    accepted_grain_types: Array.isArray(truck?.accepted_grain_types) ? truck.accepted_grain_types.join(", ") : "",
    avg_speed_kmh: truck?.avg_speed_kmh ?? (truck ? "" : defaults.avg_speed_kmh),
    cabin_size: truck?.cabin_size ?? "",
    capacity_tons: truck?.capacity_tons ?? "",
    departure_address: truck?.departure_address ?? "",
    driver_employee_id: truck?.driver_employee_id ?? "",
    evening_driver_employee_id: truck?.evening_driver_employee_id ?? "",
    compartment_name: truck?.compartment_name ?? "",
    end_time: truck?.end_time ?? "",
    fuel_cost_per_km: truck?.fuel_cost_per_km ?? "",
    inspection_time_minutes: truck?.inspection_time_minutes ?? 0,
    inter_delivery_buffer_min: truck?.inter_delivery_buffer_min ?? "",
    is_active: truck?.is_active ?? true,
    evening_is_active: truck?.evening_is_active ?? false,
    evening_radius_km: truck?.evening_radius_km ?? 40,
    is_initial_preload_enabled: truck?.is_initial_preload_enabled ?? true,
    axle_count: truck?.axle_count ?? "",
    aspirator: normalizeAspiratorDraftValue(truck?.aspirator),
    ontario: normalizeOuiNonDraftValue(truck?.ontario),
    aspirator_service_minutes: truck?.aspirator_service_minutes ?? 0,
    truck_type: truck?.truck_type || "citerne",
    unit_kind: truck ? truck.unit_kind || "ensemble" : "camion",
    loading_rate_tons_per_hour: truck?.loading_rate_tons_per_hour ?? truck?.loading_time_minutes ?? (truck ? "" : defaults.loading_rate_tons_per_hour),
    max_volume_m3: truck?.max_volume_m3 ?? "",
    max_weight_tons: truck?.max_weight_tons ?? "",
    preferred_grain_type: truck?.preferred_grain_type ?? "",
    restrictions: truck?.restrictions ?? "",
    tags: Array.isArray(truck?.tags) ? truck.tags.join(", ") : "",
    truck_name: truck?.truck_name ?? "",
    unloading_rate_tons_per_hour: truck?.unloading_rate_tons_per_hour ?? truck?.unloading_time_minutes ?? (truck ? "" : defaults.unloading_rate_tons_per_hour),
    waiting_cost_per_min: truck?.waiting_cost_per_min ?? "",
  };
}

function isClientCorrespondenceMeunerieDependentField(fieldKey) {
  return CLIENT_CORRESPONDENCE_MEUNERIE_DEPENDENT_FIELD_KEYS.has(fieldKey);
}

function areClientCorrespondenceMeunerieFieldsEnabled(draft) {
  return Boolean(draft?.is_meunerie);
}

function clearClientCorrespondenceMeunerieDependentFields(draft) {
  return {
    ...draft,
    meunerie_loading_extra_minutes: 0,
    loading_opening_time: "",
    loading_closing_time: "",
  };
}

function applyClientCorrespondenceDraftFieldChange(draft, field, value) {
  const nextDraft = {
    ...draft,
    [field]: value,
  };

  if (field === "is_meunerie" && !value) {
    return clearClientCorrespondenceMeunerieDependentFields(nextDraft);
  }

  return nextDraft;
}

function createClientCorrespondenceDraft(correspondence = null) {
  const draft = {
    address: correspondence?.address ?? "",
    client_code: correspondence?.client_code ?? "",
    client_name: correspondence?.client_name ?? "",
    correspondence_name: correspondence?.correspondence_name ?? "",
    courriel: correspondence?.courriel ?? "",
    telephone: correspondence?.telephone ?? "",
    indication: correspondence?.indication ?? "",
    is_meunerie: correspondence?.is_meunerie ?? false,
    is_coop: correspondence?.is_coop ?? false,
    bill_to: correspondence?.bill_to ?? "",
    meunerie_loading_extra_minutes: correspondence?.meunerie_loading_extra_minutes ?? 0,
    loading_opening_time: toTimeInputValue(correspondence?.loading_opening_time) ?? "",
    loading_closing_time: toTimeInputValue(correspondence?.loading_closing_time) ?? "",
  };

  return areClientCorrespondenceMeunerieFieldsEnabled(draft)
    ? draft
    : clearClientCorrespondenceMeunerieDependentFields(draft);
}

// A Coop record without Meunerie may stay without address: it is never a place.
function isCoopOnlyClientCorrespondenceDraft(draft) {
  return Boolean(draft?.is_coop) && !draft?.is_meunerie && !normalizeOptionalString(draft?.address);
}

function createEmployeeDraft(employee = null) {
  return {
    employee_name: employee?.employee_name ?? "",
    courriel: employee?.courriel ?? "",
    is_driver: employee ? Boolean(employee.is_driver) : true,
    shift: employee?.shift ?? "day",
    start_time: employee?.start_time ?? "",
    max_work_hours: employee?.max_work_hours ?? "",
    evening_start_time: employee?.evening_start_time ?? "",
    evening_max_work_hours: employee?.evening_max_work_hours ?? "",
    lunch_time: employee?.lunch_time ?? "",
  };
}

function createProductDraft(product = null) {
  return {
    name: product?.name ?? "",
    correspondence: product?.correspondence ?? "",
    article_number: product?.article_number ?? "",
    product_value: product?.product_value ?? "",
    density: product?.density ?? "",
  };
}

function createCompartmentDraft(compartment = null) {
  return {
    capacity_tons: compartment?.capacity_tons ?? "",
    compartment_id: compartment?.compartment_id ?? "",
    position: compartment?.position ?? "",
  };
}

function createCompartmentMatrixDraft(truck, positions) {
  const valuesByPosition = {};

  positions.forEach((position) => {
    const matchingCompartment = Array.isArray(truck?.compartments)
      ? truck.compartments.find((compartment) => Number(compartment?.position) === Number(position))
      : null;
    valuesByPosition[position] = matchingCompartment?.capacity_tons ?? "";
  });

  // Mêmes champs que l'onglet Camions : la remorque se sauvegarde avec la fiche complète de la ligne.
  return {
    ...createTruckDraft(truck),
    truckId: truck?.truck_id ?? "",
    valuesByPosition,
  };
}

function resolveFirstAvailableCompartmentPosition(truck, positions) {
  return positions.find((position) => !Array.isArray(truck?.compartments)
    || !truck.compartments.some((compartment) => Number(compartment?.position) === Number(position))) ?? null;
}

function normalizeOptionalString(value) {
  const resolvedValue = typeof value === "string" ? value.trim() : "";
  return resolvedValue || null;
}

function normalizeOuiNonDraftValue(value) {
  return normalizeOptionalString(value) === "oui" ? "oui" : "non";
}

// Mêmes alias que l'import : « yes », « 1 » ou « long » valent « oui ».
function normalizeAspiratorDraftValue(value) {
  return ["oui", "yes", "true", "1", "active", "actif", "long"].includes(String(value ?? "").trim().toLowerCase()) ? "oui" : "non";
}

function isOuiGridValue(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalized === "oui";
}

function isOuiNonGridValue(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalized === "oui" || normalized === "non";
}

function GridOuiNonPill({ value }) {
  const isOui = isOuiGridValue(value);

  return (
    <span
      className={[
        `inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]`,
        isOui ? "border border-green-200 bg-green-50 text-green-700" : "border border-slate-200 bg-slate-100 text-slate-600",
      ].join(" ")}
    >
      {isOui ? "Oui" : "Non"}
    </span>
  );
}

function getCreateClientCorrespondenceAddressValidationKey() {
  return "client-correspondence:create:address";
}

function getClientCorrespondenceAddressValidationKey(correspondenceId) {
  return `client-correspondence:${correspondenceId}:address`;
}

function getCreateClientCorrespondenceClientCodeValidationKey() {
  return "client-correspondence:create:client_code";
}

function getClientCorrespondenceClientCodeValidationKey(correspondenceId) {
  return `client-correspondence:${correspondenceId}:client_code`;
}

function resolveClientCorrespondenceFieldValidationKey(columnKey, { correspondenceId = null, isCreate = false } = {}) {
  if (columnKey === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY) {
    return isCreate
      ? getCreateClientCorrespondenceAddressValidationKey()
      : getClientCorrespondenceAddressValidationKey(correspondenceId);
  }

  if (columnKey === CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_KEY) {
    return isCreate
      ? getCreateClientCorrespondenceClientCodeValidationKey()
      : getClientCorrespondenceClientCodeValidationKey(correspondenceId);
  }

  return null;
}

function findClientCorrespondenceClientCodeConflict(correspondences, clientCode, excludeCorrespondenceId = null) {
  const importKey = normalizeClientCorrespondenceImportKey(clientCode);
  if (!importKey) {
    return null;
  }

  return (Array.isArray(correspondences) ? correspondences : []).find((entry) => {
    if (excludeCorrespondenceId && entry?.correspondence_id === excludeCorrespondenceId) {
      return false;
    }

    return normalizeClientCorrespondenceImportKey(entry?.client_code) === importKey;
  }) || null;
}

function resolveClientCorrespondenceClientCodeConflictLabel(conflict) {
  return normalizeOptionalString(conflict?.client_name)
    || normalizeOptionalString(conflict?.correspondence_name)
    || conflict?.correspondence_id
    || "un autre client";
}

function clampNumber(value, minValue, maxValue) {
  return Math.min(Math.max(value, minValue), maxValue);
}

function resolveAddressValidationFailureMessage(fieldLabel, validationResult) {
  if (!validationResult) {
    return `${fieldLabel} invalide ou introuvable.`;
  }

  if (validationResult.status !== "success") {
    return normalizeOptionalString(validationResult.error_message) || `${fieldLabel} invalide ou introuvable.`;
  }

  if (!validationResult.coordinates_available) {
    return normalizeOptionalString(validationResult.error_message) || `${fieldLabel} introuvable ou non routable.`;
  }

  if (!normalizeOptionalString(validationResult.resolved_address)) {
    return `${fieldLabel} introuvable ou non routable.`;
  }

  return "";
}

function buildAddressValidationToast({ fieldLabel, inputAddress, resolvedAddress, validationResult }) {
  const normalizedInputAddress = normalizeOptionalString(inputAddress) || "Adresse non fournie.";
  const normalizedResolvedAddress = normalizeOptionalString(resolvedAddress);
  const isSuccess = validationResult?.status === "success";

  if (isSuccess) {
    return {
      type: "success",
      title: `${fieldLabel} validée`,
      message: normalizedResolvedAddress && normalizedResolvedAddress !== normalizedInputAddress
        ? `Saisie: ${normalizedInputAddress}. Résolution retenue: ${normalizedResolvedAddress}.`
        : `Adresse confirmée: ${normalizedInputAddress}.`,
    };
  }

  const errorMessage = normalizeOptionalString(validationResult?.error_message) || `${fieldLabel} invalide ou introuvable.`;
  return {
    type: "error",
    title: `${fieldLabel} à vérifier`,
    message: normalizedResolvedAddress
      ? `Saisie: ${normalizedInputAddress}. Correspondance rejetée: ${normalizedResolvedAddress}. ${errorMessage}`
      : `Saisie: ${normalizedInputAddress}. ${errorMessage}`,
  };
}

function resolveAddressValidationToastPositionStyle(anchorElement) {
  if (!(anchorElement instanceof HTMLElement)) {
    return undefined;
  }

  const rect = anchorElement.getBoundingClientRect();
  if (!rect.width && !rect.height) {
    return undefined;
  }

  const viewportWidth = window.innerWidth;
  const maxLeft = Math.max(
    ANCHORED_ADDRESS_TOAST_MARGIN_PX,
    viewportWidth - ANCHORED_ADDRESS_TOAST_ESTIMATED_WIDTH_PX - ANCHORED_ADDRESS_TOAST_MARGIN_PX,
  );
  const left = clampNumber(rect.left, ANCHORED_ADDRESS_TOAST_MARGIN_PX, maxLeft);
  const anchoredTop = Math.max(
    ANCHORED_ADDRESS_TOAST_MARGIN_PX + ANCHORED_ADDRESS_TOAST_ESTIMATED_HEIGHT_PX,
    rect.top - ANCHORED_ADDRESS_TOAST_MARGIN_PX,
  );

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(anchoredTop)}px`,
    transform: "translateY(-100%)",
  };
}

function preventAddressInputDragEvent(event) {
  event.preventDefault();
}

function hasClientCorrespondenceAddressChanged(correspondence, draft) {
  const baselineDraft = createClientCorrespondenceDraft(correspondence);
  return normalizeOptionalString(draft?.address) !== normalizeOptionalString(baselineDraft.address);
}

function isClientCorrespondenceAddressFocusKey(focusKey) {
  return focusKey === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY;
}

function normalizeRequiredNumber(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizeOptionalNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizePositiveRequiredNumber(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function normalizeOptionalInteger(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return parsedValue;
}

function normalizeStringList(value) {
  if (typeof value !== "string") {
    return [];
  }

  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function buildTruckPayload(draft, { isCreation = false, expectedUpdatedAt = null } = {}) {
  const automaticFields = new Set(isCreation ? draft.automatic_creation_fields || [] : []);
  // Validation par genre : la capacité pour une ligne qui a une remorque (un camion seul qui devient dompeur aussi), la
  // vitesse pour une ligne qui a un camion.
  const unitKind = draft.unit_kind || "ensemble";
  const hasTrailer = unitKind !== "camion" || isDompeurTruck(draft);
  const hasTractor = unitKind !== "remorque";
  const normalizedCapacityTons = hasTrailer ? normalizePositiveRequiredNumber(draft.capacity_tons) : null;
  if (hasTrailer && normalizedCapacityTons === null) {
    throw new Error("La capacité doit être supérieure à 0 t.");
  }
  // Les heures ne sont pas envoyées : elles viennent des fiches des chauffeurs (valeur par défaut des Réglages).
  const normalizedAvgSpeedKmh = !hasTractor || automaticFields.has("avg_speed_kmh") ? null : normalizePositiveRequiredNumber(draft.avg_speed_kmh);
  if (hasTractor && normalizedAvgSpeedKmh === null && !automaticFields.has("avg_speed_kmh")) {
    throw new Error("La vitesse moyenne du camion est obligatoire et doit être supérieure à 0 km/h.");
  }
  const normalizedAxleCount = normalizeOptionalInteger(draft.axle_count);
  if (normalizedAxleCount === null && String(draft.axle_count ?? "").trim() !== "") {
    throw new Error("Essieux : nombre entier de 1 ou plus.");
  }
  const payload = {
    accepted_grain_types: normalizeStringList(draft.accepted_grain_types),
    avg_speed_kmh: normalizedAvgSpeedKmh,
    aspirator: normalizeOptionalString(draft.aspirator),
    ontario: normalizeOuiNonDraftValue(draft.ontario),
    aspirator_service_minutes: Math.max(0, Math.trunc(normalizeOptionalNumber(draft.aspirator_service_minutes) ?? 0)),
    axle_count: normalizedAxleCount,
    cabin_size: normalizeOptionalString(draft.cabin_size),
    truck_type: normalizeOptionalString(draft.truck_type),
    capacity_tons: normalizedCapacityTons,
    departure_address: normalizeOptionalString(draft.departure_address),
    ...(isCreation ? { driver_employee_id: normalizeOptionalString(draft.driver_employee_id), evening_driver_employee_id: normalizeOptionalString(draft.evening_driver_employee_id) } : {}),
    compartment_name: normalizeOptionalString(draft.compartment_name),
    end_time: normalizeOptionalString(draft.end_time),
    fuel_cost_per_km: normalizeOptionalNumber(draft.fuel_cost_per_km),
    inspection_time_minutes: normalizeOptionalNumber(draft.inspection_time_minutes) ?? 0,
    inter_delivery_buffer_min: normalizeOptionalNumber(draft.inter_delivery_buffer_min),
    is_active: Boolean(draft.is_active),
    evening_is_active: Boolean(draft.evening_is_active),
    evening_radius_km: normalizeOptionalNumber(draft.evening_radius_km) ?? 40,
    is_initial_preload_enabled: Boolean(draft.is_initial_preload_enabled),
    loading_rate_tons_per_hour: automaticFields.has("loading_rate_tons_per_hour") ? null : normalizeOptionalNumber(draft.loading_rate_tons_per_hour),
    max_volume_m3: normalizeOptionalNumber(draft.max_volume_m3),
    max_weight_tons: normalizeOptionalNumber(draft.max_weight_tons),
    preferred_grain_type: normalizeOptionalString(draft.preferred_grain_type),
    restrictions: normalizeOptionalString(draft.restrictions),
    tags: normalizeStringList(draft.tags),
    truck_name: normalizeOptionalString(draft.truck_name),
    unloading_rate_tons_per_hour: automaticFields.has("unloading_rate_tons_per_hour") ? null : normalizeOptionalNumber(draft.unloading_rate_tons_per_hour),
    waiting_cost_per_min: normalizeOptionalNumber(draft.waiting_cost_per_min),
  };

  if (!hasTrailer) {
    delete payload.capacity_tons;
  }
  // Une remorque seule garde ses débits (champs de la remorque) ; elle n'a pas de vitesse.
  if (!hasTractor) {
    delete payload.avg_speed_kmh;
  }

  // Le code TRK de la ligne est attribué par le serveur à la création.
  if (isCreation) {
    payload.unit_kind = unitKind;
  }

  if (expectedUpdatedAt) {
    payload.expected_updated_at = expectedUpdatedAt;
  }

  return payload;
}

function buildClientCorrespondencePayload(draft, { expectedUpdatedAt = null } = {}) {
  const isMeunerie = Boolean(draft.is_meunerie);
  const payload = {
    address: normalizeOptionalString(draft.address),
    client_code: normalizeOptionalString(draft.client_code),
    client_name: normalizeOptionalString(draft.client_name),
    correspondence_name: normalizeOptionalString(draft.correspondence_name),
    courriel: normalizeOptionalString(draft.courriel),
    telephone: normalizeOptionalString(draft.telephone),
    indication: normalizeOptionalString(draft.indication),
    is_meunerie: isMeunerie,
    is_coop: Boolean(draft.is_coop),
    bill_to: normalizeOptionalString(draft.bill_to),
    meunerie_loading_extra_minutes: isMeunerie
      ? Number(draft.meunerie_loading_extra_minutes) || 0
      : 0,
    loading_opening_time: isMeunerie
      ? (toTimeInputValue(draft.loading_opening_time) || null)
      : null,
    loading_closing_time: isMeunerie
      ? (toTimeInputValue(draft.loading_closing_time) || null)
      : null,
  };

  if (expectedUpdatedAt) {
    payload.expected_updated_at = expectedUpdatedAt;
  }

  return payload;
}

function buildEmployeePayload(draft, { expectedUpdatedAt = null } = {}) {
  const payload = {
    employee_name: normalizeOptionalString(draft.employee_name),
    courriel: normalizeOptionalString(draft.courriel),
    is_driver: Boolean(draft.is_driver),
    shift: normalizeOptionalString(draft.shift) || "day",
    start_time: normalizeOptionalString(draft.start_time),
    max_work_hours: normalizeOptionalNumber(draft.max_work_hours),
    evening_start_time: normalizeOptionalString(draft.evening_start_time),
    evening_max_work_hours: normalizeOptionalNumber(draft.evening_max_work_hours),
    lunch_time: normalizeOptionalString(draft.lunch_time),
  };

  if (expectedUpdatedAt) {
    payload.expected_updated_at = expectedUpdatedAt;
  }

  return payload;
}

function buildProductPayload(draft, { expectedUpdatedAt = null } = {}) {
  const payload = {
    name: normalizeOptionalString(draft.name),
    correspondence: normalizeOptionalString(draft.correspondence),
    article_number: normalizeOptionalString(draft.article_number),
    product_value: normalizeOptionalString(draft.product_value),
    density: normalizeOptionalNumber(draft.density),
  };

  if (expectedUpdatedAt) {
    payload.expected_updated_at = expectedUpdatedAt;
  }

  return payload;
}

function buildCompartmentPayload(draft, { expectedUpdatedAt = null } = {}) {
  const payload = {
    capacity_tons: normalizeRequiredNumber(draft.capacity_tons),
    compartment_id: normalizeOptionalString(draft.compartment_id),
    position: normalizeRequiredNumber(draft.position),
  };

  if (expectedUpdatedAt) {
    payload.expected_updated_at = expectedUpdatedAt;
  }

  return payload;
}

function formatRelativeDate(value) {
  if (!value) {
    return "À l’instant";
  }

  const resolvedDate = new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) {
    return "Horodatage indisponible";
  }

  return new Intl.DateTimeFormat("fr-CA", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(resolvedDate);
}

function formatGridDisplayValue(value) {
  if (Array.isArray(value)) {
    const joinedValue = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter(Boolean)
      .join(", ");
    return joinedValue || "—";
  }

  if (value === null || value === undefined) {
    return "—";
  }

  const resolvedValue = String(value).trim();
  return resolvedValue || "—";
}

function buildResolvedGridColumnStyle(column) {
  const resolvedWidth = resolveDataManagerGridColumnWidth(column);
  return buildFrozenColumnCellStyle(
    column.frozenLayoutEntry,
    resolvedWidth > 0 ? buildOrderGridColumnStyle(resolvedWidth) : undefined,
  );
}

const DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE = buildOrderGridColumnStyle(DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH);

function DataManagerGridColumnResizeHandle({ columnKey = "", label = "", onResizePointerDown = null }) {
  if (!columnKey || !onResizePointerDown) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={`Redimensionner la colonne ${label}`}
      className="absolute inset-y-0 right-0 z-10 w-3 translate-x-1/2 cursor-col-resize touch-none select-none bg-transparent p-0 after:absolute after:bottom-1 after:left-1/2 after:top-1 after:w-px after:-translate-x-1/2 after:rounded-full after:bg-[rgba(148,163,184,0.38)] hover:after:bg-[rgba(15,118,110,0.72)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/25"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onResizePointerDown(event, columnKey);
      }}
      title={`Redimensionner la colonne ${label}`}
    />
  );
}

function DataManagerGridSelectionHeaderCell({ children, onResizePointerDown = null, style = null }) {
  return (
    <th className={`${GRID_HEADER_CELL_CLASS_NAME} text-center`} style={style}>
      <div className="relative flex min-w-0 items-center justify-center pr-2.5">
        {children}
        <DataManagerGridColumnResizeHandle
          columnKey={DATA_MANAGER_GRID_SELECTION_COLUMN_KEY}
          label="Sélection"
          onResizePointerDown={onResizePointerDown}
        />
      </div>
    </th>
  );
}

function formatEmployeeShiftLabel(shift) {
  const normalizedShift = typeof shift === "string" ? shift.trim().toLowerCase() : "";

  if (normalizedShift === "night" || normalizedShift === "soir") {
    return "Soir";
  }

  if (normalizedShift === "day" || normalizedShift === "jour") {
    return "Jour";
  }

  return "—";
}

function resolveVehicleGridDisplayValue(column, draft, hasDraft, truck) {
  return hasDraft ? draft[column.key] : truck?.[column.key];
}

function formatAuditActionLabel(action) {
  const normalizedAction = typeof action === "string" ? action.trim().toLowerCase() : "";
  return HISTORY_ACTION_LABELS[normalizedAction] || normalizedAction.replace(/_/g, " ") || "Mutation";
}

function formatAuditEntityLabel(entry) {
  const createdOrderCount = resolveDataManagerCreatedOrderCount(entry);
  if (createdOrderCount !== null) {
    return `${createdOrderCount} commande${createdOrderCount > 1 ? "s" : ""}`;
  }

  const entityLabel = String(entry?.entity_label || entry?.entity_id || "Élément").trim() || "Élément";

  return entityLabel;
}

function formatAuditFieldLabel(fieldName) {
  return formatDataManagerAuditFieldLabel(fieldName);
}

function formatAuditImpactLabel(impactLevel) {
  const normalizedImpactLevel = typeof impactLevel === "string" ? impactLevel.trim().toLowerCase() : "";

  if (normalizedImpactLevel === "breaking") {
    return "Dispatch à revalider";
  }
  if (normalizedImpactLevel === "minor") {
    return "Impact mineur";
  }
  if (normalizedImpactLevel === "none") {
    return "Sans impact";
  }

  return normalizedImpactLevel || "—";
}

function resolveHistorySection(entityKind) {
  if (entityKind === "order" || entityKind === "order_import") {
    return "orders";
  }

  if (entityKind === "client_correspondence" || entityKind === "client_correspondence_import") {
    return "correspondences";
  }

  if (entityKind === "employee") {
    return "employees";
  }

  if (entityKind === "product_correspondence") {
    return "products";
  }
  if (entityKind === "catalog_product") return "product_catalog";

  if (entityKind === "truck" || entityKind === "truck_import" || entityKind === "truck_driver_default") {
    return "vehicles";
  }

  // « trailer » : les champs de remorque d'un audit de camion, tels que le serveur les envoie à l'onglet Remorques.
  if (entityKind === "compartment" || entityKind === "compartment_import" || entityKind === "trailer") {
    return "compartments";
  }

  return null;
}

function resolveHistorySectionLabel(activeTab) {
  if (activeTab === "orders") {
    return "Commandes";
  }

  if (activeTab === "correspondences") {
    return "Clients";
  }

  if (activeTab === "employees") {
    return "Chauffeurs";
  }

  if (activeTab === "products") {
    return "Codes produit";
  }
  if (activeTab === "product_catalog") return "Produits";

  if (activeTab === "compartments") {
    return "Remorques";
  }

  return "Camions";
}

function resolveOrderDateFilterValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const match = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "";
  }

  const year = String(parsedDate.getFullYear());
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function collectSearchTokens(value) {
  if (value === null || value === undefined || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectSearchTokens(entry));
  }

  if (typeof value === "object") {
    return Object.values(value).flatMap((entry) => collectSearchTokens(entry));
  }

  if (typeof value === "boolean") {
    return [value ? "oui" : "non", String(value)];
  }

  const resolvedValue = String(value).trim();
  return resolvedValue ? [resolvedValue] : [];
}

function buildSearchHaystack(value) {
  return collectSearchTokens(value).join(" ").toLowerCase();
}

function buildHistorySearchHaystack(row) {
  return buildSearchHaystack({
    actionLabel: row?.actionLabel,
    actorLabel: row?.actorLabel,
    afterValue: row?.afterValue,
    beforeValue: row?.beforeValue,
    dateLabel: row?.dateLabel,
    entityKind: row?.entityKind,
    entityLabel: row?.entityLabel,
    fieldLabel: row?.fieldLabel,
    detailSearchText: row?.detailSearchText,
    orderNumberLabel: row?.orderNumberLabel,
    sourceLabel: row?.sourceLabel,
  });
}

function resolveHistoryActorLabel(entry) {
  if (typeof entry?.actor_email === "string" && entry.actor_email.trim()) {
    return entry.actor_email.trim();
  }
  if (typeof entry?.actor_user_id === "string" && entry.actor_user_id.trim()) {
    return entry.actor_user_id.trim();
  }
  return "Compte système";
}

function matchesHistorySearch(row, query) {
  const normalizedQuery = typeof query === "string" ? query.trim().toLowerCase() : "";
  if (!normalizedQuery) {
    return true;
  }

  return buildHistorySearchHaystack(row).includes(normalizedQuery);
}

function formatAuditValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatAuditValue(entry))
      .filter((entry) => entry !== "—")
      .join(", ") || "—";
  }

  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }

  return String(value).trim() || "—";
}

function formatAuditDetailValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "—";
    }
  }

  return formatAuditValue(value);
}

function buildHistoryRows(history) {
  return history.flatMap((entry) => {
    const normalizedAction = typeof entry?.action === "string" ? entry.action.trim().toLowerCase() : "";
    const entityKind = typeof entry?.entity_kind === "string" ? entry.entity_kind.trim().toLowerCase() : "";
    const section = resolveHistorySection(entityKind);
    const createdAtValue = Number.isFinite(Date.parse(entry?.created_at || ""))
      ? Date.parse(entry.created_at)
      : 0;
    const clientSummaryChange = resolveDataManagerClientAuditSummaryChange(entry);
    const fieldChanges = clientSummaryChange ? [clientSummaryChange] : resolveDataManagerAuditFieldChanges(entry);
    const hadRawFieldChanges = Array.isArray(entry?.field_changes) && entry.field_changes.length > 0;
    const deferredChangedFields = fieldChanges.length === 0 && Array.isArray(entry?.changed_fields)
      ? resolveDataManagerAuditFieldNames(entry.changed_fields, entry)
      : [];
    const actionPresentation = section === "correspondences"
      ? resolveDataManagerClientAuditAction(normalizedAction, formatAuditActionLabel(normalizedAction))
      : {};
    const actionLabelPrefix = section !== "correspondences" && HISTORY_PREFIXED_ACTIONS.has(normalizedAction)
      ? `${formatAuditActionLabel(normalizedAction)} · `
      : "";
    const actorLabel = resolveHistoryActorLabel(entry);
    const sourceLabel = formatDataManagerAuditSourceLabel(entry?.source_kind);
    const orderNumberLabel = formatDataManagerAuditOrderNumberLabel(entry);

    if (fieldChanges.length > 0) {
      return fieldChanges.map((fieldChange, index) => ({
        ...actionPresentation,
        actorLabel,
        afterValue: formatAuditValue(fieldChange?.new_value),
        auditId: entry.audit_id,
        beforeValue: formatAuditValue(fieldChange?.old_value),
        createdAtValue,
        dateLabel: formatRelativeDate(entry.created_at),
        entityLabel: formatAuditEntityLabel(entry),
        entityKind,
        fieldLabel: `${actionLabelPrefix}${formatAuditFieldLabel(fieldChange?.field)}`,
        detailSearchText: clientSummaryChange ? buildSearchHaystack([
          entry.summary, entry.impact_summary,
          resolveDataManagerAuditFieldNames(entry?.changed_fields, entry).map(formatAuditFieldLabel),
          resolveDataManagerAuditFieldChanges(entry).map((change) => [formatAuditFieldLabel(change.field), change.old_value, change.new_value]),
        ]) : "",
        orderNumberLabel,
        rowKey: `${entry.audit_id}:${fieldChange?.field || "field"}:${index}`,
        section,
        sourceLabel,
      }));
    }

    if (deferredChangedFields.length > 0) {
      const deferredAfterValue = formatAuditValue(entry.summary || entry.impact_summary || "Détail à la demande");
      return deferredChangedFields.map((fieldName, index) => ({
        ...actionPresentation,
        actorLabel,
        afterValue: deferredAfterValue,
        auditId: entry.audit_id,
        beforeValue: "—",
        createdAtValue,
        dateLabel: formatRelativeDate(entry.created_at),
        entityLabel: formatAuditEntityLabel(entry),
        entityKind,
        fieldLabel: `${actionLabelPrefix}${formatAuditFieldLabel(fieldName)}`,
        orderNumberLabel,
        rowKey: `${entry.audit_id}:${fieldName || "field"}:${index}`,
        section,
        sourceLabel,
      }));
    }

    if (hadRawFieldChanges) {
      return [];
    }

    return [{
      ...actionPresentation,
      actorLabel,
      afterValue: formatAuditValue(entry.summary || entry.impact_summary),
      auditId: entry.audit_id,
      beforeValue: "—",
      createdAtValue,
      dateLabel: formatRelativeDate(entry.created_at),
      entityLabel: formatAuditEntityLabel(entry),
      entityKind,
      fieldLabel: actionLabelPrefix ? formatAuditActionLabel(normalizedAction) : "Résumé",
      orderNumberLabel,
      rowKey: `${entry.audit_id}:summary`,
      section,
      sourceLabel,
    }];
  });
}

function sortHistoryRows(rows, sortKey, sortDirection) {
  const directionMultiplier = sortDirection === "asc" ? 1 : -1;

  return [...rows].sort((leftRow, rightRow) => {
    let comparison = 0;

    if (sortKey === "action") {
      comparison = leftRow.actionLabel.localeCompare(rightRow.actionLabel, "fr", { sensitivity: "base" });
    } else if (sortKey === "date") {
      comparison = leftRow.createdAtValue - rightRow.createdAtValue;
    } else {
      const leftValue = String(
        sortKey === "actor"
          ? leftRow.actorLabel
          : sortKey === "source"
            ? leftRow.sourceLabel
            : sortKey === "orderNumber"
              ? leftRow.orderNumberLabel
              : sortKey === "entity"
                ? leftRow.entityLabel
                : sortKey === "field"
                  ? leftRow.fieldLabel
                  : sortKey === "before"
                    ? leftRow.beforeValue
                    : leftRow.afterValue
      );
      const rightValue = String(
        sortKey === "actor"
          ? rightRow.actorLabel
          : sortKey === "source"
            ? rightRow.sourceLabel
            : sortKey === "orderNumber"
              ? rightRow.orderNumberLabel
              : sortKey === "entity"
                ? rightRow.entityLabel
                : sortKey === "field"
                  ? rightRow.fieldLabel
                  : sortKey === "before"
                    ? rightRow.beforeValue
                    : rightRow.afterValue
      );
      comparison = leftValue.localeCompare(rightValue, "fr", { sensitivity: "base", numeric: true });
    }

    if (comparison !== 0) {
      return comparison * directionMultiplier;
    }

    return rightRow.createdAtValue - leftRow.createdAtValue;
  });
}

function resolveTruckSortValue(truck, sortKey) {
  if (sortKey === "status") {
    return [
      truck?.is_active ? "Actif" : "Inactif",
      truck?.has_historical_assignments ? "Historique" : "",
    ].filter(Boolean).join(" · ");
  }

  if (sortKey === "compartment_count") {
    return Number(truck?.compartment_count ?? truck?.compartments?.length ?? 0);
  }

  if (sortKey === "is_active") {
    return truck?.is_active ? "Actif" : "Inactif";
  }

  if (sortKey === "is_initial_preload_enabled") {
    return truck?.is_initial_preload_enabled ? "Oui" : "Non";
  }

  return truck?.[sortKey];
}

function resolveClientCorrespondenceSortValue(correspondence, sortKey) {
  return correspondence?.[sortKey];
}

function resolveEmployeeSortValue(employee, sortKey) {
  if (sortKey === "shift") {
    return formatEmployeeShiftLabel(employee?.shift);
  }

  if (sortKey === "driver_user_id") {
    return hasDriverPortalAccount(employee) ? "Oui" : "Non";
  }

  return employee?.[sortKey];
}

// « Compte portail » : un compte rattaché à la fiche, sauf celui en cours de suppression ou dont la création a échoué.
function hasDriverPortalAccount(employee) {
  const status = employee?.driver_account_sync?.status;
  return Boolean(employee?.driver_user_id) && status !== "deletion_pending" && status !== "creation_failed";
}

function resolveProductSortValue(product, sortKey) {
  return product?.[sortKey];
}

function resolveCompartmentTruckSortValue(truck, sortKey) {
  if (sortKey.startsWith("position:")) {
    const position = Number(sortKey.slice("position:".length));
    const matchingCompartment = Array.isArray(truck?.compartments)
      ? truck.compartments.find((compartment) => Number(compartment?.position) === position)
      : null;
    return normalizeOptionalNumber(matchingCompartment?.capacity_tons);
  }

  if (sortKey === "truck_name") {
    return truck?.truck_name || truck?.truck_id;
  }

  if (sortKey === "axle_count") {
    return normalizeOptionalInteger(truck?.axle_count) ?? normalizeOptionalNumber(truck?.axle_count);
  }

  if (sortKey === "aspirator") {
    return truck?.aspirator;
  }

  if (sortKey === "ontario") {
    return normalizeOuiNonDraftValue(truck?.ontario);
  }

  return truck?.[sortKey];
}

function resolveGridFocusKeyFromEvent(event, fallbackKey) {
  const focusNode = typeof event?.target?.closest === "function"
    ? event.target.closest("[data-grid-focus-key]")
    : null;
  const focusKey = typeof focusNode?.getAttribute === "function"
    ? focusNode.getAttribute("data-grid-focus-key")
    : "";

  return focusKey || fallbackKey;
}

function useEditableRowFocus(isEditing, focusKey) {
  const rowRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!isEditing) {
      return undefined;
    }

    let observer;
    const frameId = window.requestAnimationFrame(() => {
      const focusableNodes = Array.from(
        rowRef.current?.querySelectorAll("input[data-grid-focus-key], select[data-grid-focus-key], textarea[data-grid-focus-key]") || []
      );
      const preferredNode = focusableNodes.find((node) => node.getAttribute("data-grid-focus-key") === focusKey);
      const fallbackNode = focusableNodes[0] || null;
      const targetNode = preferredNode || fallbackNode;

      if (!targetNode || typeof targetNode.focus !== "function") {
        return;
      }

      const focusTarget = () => {
        targetNode.focus({ preventScroll: true });
        if (typeof targetNode.select === "function") targetNode.select();
      };
      if (targetNode.disabled) {
        const activeBeforeLoad = document.activeElement;
        observer = new MutationObserver(() => {
          if (targetNode.disabled) return;
          observer.disconnect();
          if (document.activeElement === activeBeforeLoad || document.activeElement === document.body) focusTarget();
        });
        observer.observe(targetNode, { attributes: true, attributeFilter: ["disabled"] });
      } else {
        focusTarget();
      }
    });

    return () => { window.cancelAnimationFrame(frameId); observer?.disconnect(); };
  }, [focusKey, isEditing]);

  return rowRef;
}

function SummaryCard({ label, note, value }) {
  return (
    <article className="rounded-lg border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.045)]">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-1 text-[1.24rem] font-semibold leading-none tracking-[-0.03em] text-slate-950">{value}</p>
      <p className="mt-1.5 text-[10.5px] leading-4 text-slate-500">{note}</p>
    </article>
  );
}

function SectionField({ children, label }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function TabButton({ count, isActive, item, onSelect }) {
  const Icon = item.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(item.key)}
      className={[
        "flex min-w-60 flex-1 items-start gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150",
        isActive
          ? "border-accent bg-accent text-white shadow-[0_10px_24px_rgba(15,23,42,0.08)] hover:border-accent-strong hover:bg-accent-strong"
          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950",
      ].join(" ")}
    >
      <span className={[
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[14px]",
        isActive ? "bg-accent text-white" : "bg-slate-100 text-slate-600",
      ].join(" ")}>
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{item.badge}</span>
        <span className="mt-1 flex items-center gap-1.5 text-[0.96rem] font-semibold tracking-[-0.02em] text-current">
          <span>{item.label}</span>
          <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-white px-1.5 py-px text-[10px] font-bold text-slate-600">{count}</span>
        </span>
        <span className="mt-1 block text-[12px] leading-5 text-slate-600">{item.description}</span>
      </span>
    </button>
  );
}

function PlanningBanner({ planning }) {
  const isStale = planning?.source_data_state === "stale";
  const hasPlanning = planning?.snapshot_active || planning?.manual_overrides_present;

  return (
    <article className={[PANEL_CLASS_NAME, isStale ? "border-amber-200 bg-amber-50/60" : ""].join(" ")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">Rigueur métier</p>
          <h2 className="mt-1 text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">
            {isStale ? "Un stale marker est déjà actif" : "Les mutations passent par le stale et l’historique"}
          </h2>
          <p className="mt-2 text-[12.5px] leading-6 text-slate-600">
            {hasPlanning
              ? "Chaque enregistrement compare l’impact potentiel sur le dispatch courant, marque les snapshots en stale et garde une trace d’audit persistante."
              : "Aucun snapshot actif n’est chargé pour le moment, mais les changements restent historisés et la source PG reste cohérente."}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10.5px]">
          <span className={["inline-flex items-center rounded-full px-2.5 py-1 font-semibold uppercase tracking-[0.14em]", isStale ? "border border-amber-200 bg-amber-100 text-amber-800" : "border border-green-200 bg-green-50 text-green-700"].join(" ")}>
            {isStale ? "Source stale" : "Source cohérente"}
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold uppercase tracking-[0.14em] text-slate-700">
            Snapshot: {planning?.snapshot_active ? "actif" : "inactif"}
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold uppercase tracking-[0.14em] text-slate-700">
            Overrides: {planning?.manual_overrides_present ? "présents" : "aucun"}
          </span>
        </div>
      </div>
    </article>
  );
}

function buildEmployeeDriverSelectOptions(employees = [], { currentValue = "", shift = "" } = {}) {
  const names = [];
  const seen = new Set();

  (Array.isArray(employees) ? employees : []).forEach((employee) => {
    if (employee?.is_driver !== true) {
      return;
    }
    if (shift && employee?.shift !== shift) {
      return;
    }
    const name = typeof employee?.employee_name === "string" ? employee.employee_name.trim() : "";
    if (!name) {
      return;
    }
    const nameKey = name.toLocaleLowerCase("fr-CA");
    if (seen.has(nameKey)) {
      return;
    }
    seen.add(nameKey);
    names.push(name);
  });

  names.sort((leftName, rightName) => leftName.localeCompare(rightName, "fr-CA"));
  const normalizedCurrent = typeof currentValue === "string" ? currentValue.trim() : "";
  if (normalizedCurrent && !names.includes(normalizedCurrent)) {
    return [normalizedCurrent, ...names];
  }
  return names;
}

function isVehicleCellOff(column, truck) {
  return Boolean(column.dompeurOnly) && !isDompeurTruck(truck);
}

function VehicleGridField({ column, draft, employees = [], hasDraft = false, isCreateRow = false, isEditing = false, onCancel = null, onChange, onSubmit = null, truck = null, onDriverDraftStateChange, onDriverSaved, productCatalog = [] }) {
  const hasOptions = Array.isArray(column.options) && column.options.length > 0;

  // Type : Citerne ou Dompeur, avec les dates de retrait ; il se change en modification, sauf sur une ligne dont une partie est retirée.
  if (column.key === "truck_type") {
    if (!isEditing || truck?.tractor_retired_on || truck?.trailer_retired_on) {
      return <DataManagerTypeBadge truck={isCreateRow ? draft : truck} />;
    }
    return (
      <select aria-label="Type" className={GRID_INPUT_CLASS_NAME} data-grid-focus-key="truck_type" value={isDompeurTruck(draft) ? "dompeur" : "citerne"}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })} onChange={(event) => onChange("truck_type", event.target.value)}>
        <option value="citerne">Citerne</option>
        <option value="dompeur">Dompeur</option>
      </select>
    );
  }

  // Champs du dompeur : vides et non modifiables sur les autres lignes. À l'ajout et en modification, ils suivent le
  // type choisi : un camion seul passé en Dompeur ouvre sa capacité.
  if (isVehicleCellOff(column, isEditing || isCreateRow ? draft : truck)) {
    return <span className={`${GRID_VALUE_CLASS_NAME} text-slate-400`}>—</span>;
  }

  if (!isCreateRow && !isEditing) {
    const displayValue = resolveVehicleGridDisplayValue(column, draft, hasDraft, truck);
    const usableTons = column.key === "capacity_tons" ? compartmentTotalTons(truck) : 0;

    // Compartiments sous la capacité : la remorque ne porte pas plus qu'eux.
    if (usableTons > 0 && usableTons < Number(displayValue)) {
      return (
        <span className="inline-flex flex-col items-start gap-0.5">
          <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(displayValue)}</span>
          <span data-usable-tons title={`Les compartiments totalisent ${tonsLabel(usableTons)} t : la remorque ne porte pas plus, même si sa capacité est de ${tonsLabel(displayValue)} t.`}
            className={`inline-flex items-center whitespace-nowrap ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700`}>
            {tonsLabel(usableTons)} t utilisables
          </span>
        </span>
      );
    }

    if (column.type === "boolean") {
      return <GridOuiNonPill value={displayValue ? "oui" : "non"} />;
    }

    if (column.type === "employee") {
      const nameField = column.key === "evening_driver_employee_id" ? "evening_driver_name" : "driver_name";
      const name = truck?.[nameField];
      return <span className={GRID_VALUE_CLASS_NAME}>{name || "Aucun chauffeur"}{!truck?.[column.key] && name ? " — À confirmer" : ""}</span>;
    }

    if (column.key === "aspirator" || column.key === "ontario") {
      if (isOuiNonGridValue(displayValue)) {
        return <GridOuiNonPill value={displayValue} />;
      }
    }

    if (hasOptions) {
      const optionLabel = String(displayValue ?? "").trim() || "—";
      return (
        <span className={`inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600`}>
          {optionLabel}
        </span>
      );
    }

    if (column.key === "aspirator_service_minutes") {
      const minutes = Math.max(0, Math.trunc(Number(displayValue) || 0));
      if (minutes <= 0) {
        return <span className={GRID_VALUE_CLASS_NAME}>—</span>;
      }
      return (
        <span className={`inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-semibold tracking-[0.04em] text-orange-700`}>
          {minutes} min
        </span>
      );
    }

    return <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(displayValue)}</span>;
  }

  if (column.type === "readonly") {
    return (
      <span className={`${GRID_VALUE_CLASS_NAME} text-slate-500`}>
        {isCreateRow ? "—" : formatGridDisplayValue(truck?.[column.key])}
      </span>
    );
  }

  if (column.type === "boolean") {
    return (
      <select
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={draft[column.key] ? "true" : "false"}
        onChange={(event) => onChange(column.key, event.target.value === "true")}
      >
        <option value="true">Oui</option>
        <option value="false">Non</option>
      </select>
    );
  }

  if (column.type === "product") {
    // Un seul produit actif du catalogue, ou aucun ; une ancienne valeur reste visible jusqu'à son remplacement.
    return <DataManagerProductSelect products={productCatalog} optional value={draft[column.key] ?? ""}
      originalValue={truck ? truck[column.key] ?? "" : undefined} className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key} onChange={(value) => onChange(column.key, value)}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })} />;
  }

  if (column.type === "employee") {
    const period = column.key === "evening_driver_employee_id" ? "evening" : "day";
    if (isCreateRow) return <DriverEmployeeSelect scope="defaults" dispatchPeriod={period} value={draft[column.key] || ""} onChange={(employeeId) => onChange(column.key, employeeId || "")} />;
    const nameField = period === "evening" ? "evening_driver_name" : "driver_name";
    const versionField = period === "evening" ? "evening_driver_assignment_version" : "driver_assignment_version";
    return <DriverAssignmentEditor scope="defaults" dispatchPeriod={period} truckId={truck.truck_id} currentName={truck[nameField] || ""}
      onDraftStateChange={onDriverDraftStateChange}
      onSaved={(_result, employeeId) => onDriverSaved?.(column.key, employeeId)}
      assignment={{ driver_employee_id: truck[column.key], driver_assignment_version: truck[versionField] }} />;
  }

  if (hasOptions) {
    const currentValue = String(draft[column.key] ?? column.options[0] ?? "").trim();
    return (
      <select
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={currentValue}
        onChange={(event) => onChange(column.key, event.target.value)}
      >
        {column.options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key}
      inputMode={column.type === "number" ? (String(column.step || "").includes(".") ? "decimal" : "numeric") : undefined}
      onChange={(event) => onChange(column.key, event.target.value)}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
      placeholder={column.placeholder}
      min={column.min}
      step={column.step}
      type={column.type === "time" ? "time" : column.type === "number" ? "number" : "text"}
      value={draft[column.key]}
    />
  );
}

function TruckGridRow({
  period = "day",
  mobile = false,
  draft,
  editingFocusKey,
  employees = [],
  hasDraft,
  isEditing,
  isFocused = false,
  isPending,
  isSelectable = true,
  isSelected = false,
  onCancel,
  onChange,
  onDelete,
  onFocusRow = null,
  onSave,
  onSelectionChange = null,
  onStartEdit,
  resolvedColumns = [...VEHICLE_GRID_LEADING_COLUMNS, ...VEHICLE_GRID_COLUMNS],
  selectionColumnStyle = buildDataManagerGridSelectionColumnStyle(),
  selectionTitle = "Sélectionner ce camion",
  truck,
}) {
  const defaultFocusKey = period === "evening" ? "evening_is_active" : VEHICLE_DEFAULT_FOCUS_KEY;
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);
  const [mobileDriverDrafts, setMobileDriverDrafts] = React.useState({});
  const [savedDriver, setSavedDriver] = React.useState(null);
  // La vue Soir ne modifie que les champs du Soir : le type et le nom (le même le Jour et le Soir) se changent dans la vue Jour.
  const isEditingColumn = (column) => isEditing && (period !== "evening" || !["truck_type", "truck_name"].includes(column.key));
  const cellTruck = isEditing ? draft : truck;

  React.useEffect(() => {
    if (!isEditing || !savedDriver || String(truck?.[savedDriver.field] || "") !== String(savedDriver.employeeId || "")) return;
    const original = createTruckDraft(truck);
    const hasOtherChanges = Object.entries(original).some(([key, value]) =>
      key !== "driver_employee_id" && key !== "evening_driver_employee_id"
      && String(draft[key] ?? "") !== String(value ?? ""));
    setSavedDriver(null);
    if (!hasOtherChanges) onCancel();
  }, [draft, isEditing, onCancel, savedDriver, truck]);

  if (mobile) {
    const changed = Object.entries(createTruckDraft(truck)).some(([key, value]) => String(draft[key] ?? "") !== String(value ?? ""));
    return <MobileRecordCard title={truck.truck_name || `Camion ${truck.truck_id}`} recordId={truck.truck_id}
      summary={<span>{(period === "evening" ? truck.evening_driver_name : truck.driver_name) || "Aucun chauffeur"}</span>}
      editing={isEditing} pending={isPending || Object.values(mobileDriverDrafts).some((state) => state.busy)} dirty={changed || Object.values(mobileDriverDrafts).some((state) => state.dirty)} canSave={changed} canEdit={Boolean(onStartEdit)} onEdit={() => onStartEdit?.(defaultFocusKey)} onCancel={onCancel} onSave={onSave}>
      {resolvedColumns.filter((column) => column.key !== "status").map((column) => <label key={column.key}><span>{column.label}</span>
        <VehicleGridField column={column} draft={draft} employees={employees} hasDraft={hasDraft} isEditing={isEditingColumn(column)} onChange={onChange} truck={truck} onDriverDraftStateChange={(state) => setMobileDriverDrafts((current) => ({ ...current, [column.key]: state }))} />
      </label>)}
    </MobileRecordCard>;
  }
  return (
    <tr
      ref={rowRef}
      data-truck-id={truck.truck_id}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-truck-row-editing={isEditing ? "true" : undefined}
      data-truck-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isPending,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !onStartEdit ? undefined : (event) => onStartEdit(resolveGridFocusKeyFromEvent(event, defaultFocusKey))}
      onKeyDownCapture={isEditing ? (event) => {
        if (event.target.closest?.("[data-driver-employee-select]")) return;
        handleEditableGridKeyDown(event, { onCancel, onSubmit: onSave });
      } : undefined}
      title={isEditing ? undefined : "Double-cliquez pour éditer cette ligne"}
    >
      {period !== "evening" ? <td className={`${GRID_CELL_CLASS_NAME} text-center`} style={selectionColumnStyle}>
        <input
          type="checkbox"
          aria-label={`Sélectionner le camion ${truck?.truck_id || ""}`}
          checked={Boolean(isSelected)}
          disabled={!isSelectable || isPending}
          onChange={(event) => onSelectionChange?.(event.target.checked)}
          onClick={(event) => {
            event.stopPropagation();
            handleDataManagerGridRowClick(onFocusRow);
          }}
          onDoubleClick={stopGridSelectionPropagation}
          title={!isSelectable ? selectionTitle : undefined}
          className={GRID_SELECTION_INPUT_CLASS_NAME}
        />
      </td> : null}
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""} ${isVehicleCellOff(column, cellTruck) ? "data-manager-cell-off" : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          data-grid-focus-key={column.type === "readonly" || isVehicleCellOff(column, cellTruck) ? defaultFocusKey : column.key}
          style={buildResolvedGridColumnStyle(column)}
        >
          <VehicleGridField column={column} draft={draft} employees={employees} hasDraft={hasDraft} isEditing={isEditingColumn(column)} onCancel={onCancel} onChange={onChange} onSubmit={onSave} truck={truck}
            onDriverSaved={(field, employeeId) => setSavedDriver({ field, employeeId })} />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>
        {isEditing ? (
          <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
            <button type="button" onClick={onSave} disabled={isPending} className={GRID_PRIMARY_ACTION_CLASS_NAME}>
              <Save size={13} />
              Enregistrer
            </button>
            <button type="button" onClick={onCancel} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              Fermer
            </button>
            {period !== "evening" ? <button
              type="button"
              onClick={onDelete}
              disabled={isPending || truck.has_historical_assignments}
              className={GRID_SECONDARY_ACTION_CLASS_NAME}
              title={truck.has_historical_assignments ? "Suppression bloquée par l’historique dispatch." : "Supprimer le camion"}
            >
              <Trash2 size={13} />
              Supprimer
            </button> : null}
          </div>
        ) : onStartEdit ? (
          <button type="button" onClick={() => onStartEdit(defaultFocusKey)} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
            Modifier
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function NewTruckGridRow({ mobile = false, pending = false, draft, employees = [], onCancel, onChange, onCreate, resolvedColumns = [...VEHICLE_GRID_LEADING_COLUMNS, ...VEHICLE_GRID_COLUMNS], selectionColumnStyle = buildDataManagerGridSelectionColumnStyle() }) {
  const rowRef = useEditableRowFocus(true, VEHICLE_DEFAULT_FOCUS_KEY);

  if (mobile) return <MobileRecordCard title="Nouveau camion" editing dirty pending={pending} onCancel={onCancel} onSave={onCreate}>
    {resolvedColumns.filter((column) => column.key !== "status").map((column) => <label key={column.key}><span>{column.label}</span>
      <VehicleGridField column={column} draft={draft} employees={employees} isCreateRow onChange={onChange} />
    </label>)}
  </MobileRecordCard>;

  return (
    <tr data-frozen-row-state="create" ref={rowRef} className="bg-slate-100" onKeyDownCapture={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onCreate })}>
      <td className={GRID_CELL_CLASS_NAME} style={selectionColumnStyle} />
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""} ${isVehicleCellOff(column, draft) ? "data-manager-cell-off" : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          style={buildResolvedGridColumnStyle(column)}
        >
          <VehicleGridField column={column} draft={draft} employees={employees} isCreateRow onCancel={onCancel} onChange={onChange} onSubmit={onCreate} />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE} />
    </tr>
  );
}

// Négatif : meunerie plus rapide que le temps fixe par chargement.
function hasClientCorrespondenceMeunerieExtraMinutesValue(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue !== 0;
}

function formatClientAddressCoordinate(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue.toFixed(6) : "";
}

function buildClientAddressGoogleMapsUrl(latitude, longitude) {
  const latitudeLabel = formatClientAddressCoordinate(latitude);
  const longitudeLabel = formatClientAddressCoordinate(longitude);
  if (!latitudeLabel || !longitudeLabel) {
    return "";
  }

  const searchParams = new URLSearchParams({
    api: "1",
    query: `${latitudeLabel},${longitudeLabel}`,
  });
  return `https://www.google.com/maps/search/?${searchParams.toString()}`;
}

function formatClientAddressGeocodingSource(value) {
  const normalizedValue = normalizeOptionalString(value);
  if (!normalizedValue) {
    return "";
  }
  const normalizedKey = normalizedValue.toLowerCase();
  if (normalizedKey === "geogratis") {
    return "GeoGratis";
  }
  if (normalizedKey === "nominatim") {
    return "Nominatim";
  }
  return normalizedValue;
}

function ClientAddressCoordinatesHoverCardContent({ address, validation }) {
  const normalizedAddress = normalizeOptionalString(address);
  const [copyFeedback, setCopyFeedback] = React.useState("");
  const record = validation?.validation;
  if (validation?.status !== "validated" || record?.address !== normalizedAddress) {
    return <p className="m-0 min-w-64 text-[11px] leading-5 text-slate-600">
      {addressValidationLabel(validation)}{validation?.error_message ? ` · ${validation.error_message}` : ""}
    </p>;
  }
  const validationState = {
    latitude: record.latitude, longitude: record.longitude,
    coordinates: `${formatClientAddressCoordinate(record.latitude)}, ${formatClientAddressCoordinate(record.longitude)}`,
    resolvedAddress: record.resolved_address, source: formatClientAddressGeocodingSource(record.provider),
  };

  const googleMapsUrl = buildClientAddressGoogleMapsUrl(
    validationState.latitude,
    validationState.longitude,
  );

  const handleCopyCoordinates = async () => {
    setCopyFeedback("");
    try {
      const clipboard = globalThis?.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== "function") {
        throw new Error("Presse-papiers indisponible dans ce navigateur.");
      }
      await clipboard.writeText(validationState.coordinates);
      setCopyFeedback("Coordonnées copiées.");
    } catch (error) {
      setCopyFeedback(error instanceof Error ? error.message : "Impossible de copier les coordonnées.");
    }
  };

  return (
    <div className="grid min-w-72 gap-2.5">
      <div>
        <p className="m-0 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">Adresse résolue</p>
        <p className="mt-1 mb-0 text-[11px] leading-4 text-slate-700">
          {validationState.resolvedAddress || normalizedAddress}
        </p>
      </div>
      <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2">
        <p className="m-0 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">Latitude, longitude</p>
        <code className="mt-1 block select-all text-[12px] font-semibold text-slate-900">
          {validationState.coordinates}
        </code>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={GRID_SECONDARY_ACTION_CLASS_NAME}
          onClick={handleCopyCoordinates}
        >
          <Copy aria-hidden="true" size={12} />
          Copier
        </button>
        {googleMapsUrl ? (
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noreferrer noopener"
            className={GRID_SECONDARY_ACTION_CLASS_NAME}
            aria-label="Ouvrir la position dans Google Maps"
          >
            <ExternalLink aria-hidden="true" size={12} />
            Ouvrir dans Google Maps
          </a>
        ) : null}
      </div>
      {copyFeedback ? (
        <p className="m-0 text-[10px] leading-4 text-slate-600" aria-live="polite">{copyFeedback}</p>
      ) : null}
      {validationState.source ? (
        <p className="m-0 text-[9px] uppercase tracking-[0.1em] text-slate-400">
          Source&nbsp;: {validationState.source}
        </p>
      ) : null}
    </div>
  );
}

function ClientAddressCoordinatesHoverCard({ address, validation }) {
  const normalizedAddress = normalizeOptionalString(address);
  if (!normalizedAddress) {
    return <span className={GRID_VALUE_CLASS_NAME}>—</span>;
  }

  return (
    <HoverCard
      title="Position géographique"
      content={(
        <ClientAddressCoordinatesHoverCardContent
          address={normalizedAddress}
          validation={validation}
        />
      )}
      delay={CLIENT_ADDRESS_HOVERCARD_DELAY_MS}
      maxWidth={380}
      placement="top-start"
      interactive
      referenceAsChild
    >
      <span
        className={`${GRID_VALUE_CLASS_NAME} cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2`}
      >
        {formatGridDisplayValue(normalizedAddress)}
      </span>
    </HoverCard>
  );
}

function ClientCorrespondenceGridField({ addressValidationKey = null, column, draft, fieldValidationKey = null, hasDraft = false, isEditing = false, onAddressEnterValidation = null, onCancel = null, onChange, onSubmit = null, correspondence = null, registerAddressValidationFieldRef = null, validateAddress = null }) {
  const displayValue = hasDraft ? draft[column.key] : correspondence?.[column.key];

  if (!isEditing) {
    if (column.type === "bill_to") {
      return <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(displayValue)}</span>;
    }
    if (column.type === "boolean") {
      return (
        <span className={[
          `inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]`,
          displayValue ? "border border-green-200 bg-green-50 text-green-700" : "border border-slate-200 bg-slate-100 text-slate-600",
        ].join(" ")}>
          {displayValue ? "Oui" : "Non"}
        </span>
      );
    }

    if (column.key === "meunerie_loading_extra_minutes") {
      if (!hasClientCorrespondenceMeunerieExtraMinutesValue(displayValue)) {
        return <span className={GRID_VALUE_CLASS_NAME}>—</span>;
      }

      return (
        <span className={[
          `inline-flex items-center gap-1 ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[10px] font-semibold tracking-[0.04em]`,
          "border border-amber-200 bg-amber-50 text-amber-700",
        ].join(" ")}>
          {Number(displayValue)} min
        </span>
      );
    }

    if (column.type === "time") {
      const formattedTime = toTimeInputValue(displayValue);
      if (!formattedTime) {
        return <span className={GRID_VALUE_CLASS_NAME}>—</span>;
      }
      return (
        <span className={[
          `inline-flex items-center gap-1 ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]`,
          "border border-sky-200 bg-sky-50 text-sky-700",
        ].join(" ")}>
          {formattedTime}
        </span>
      );
    }

    if (column.key === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY) {
      return (
        <ClientAddressCoordinatesHoverCard
          address={displayValue}
          validation={hasDraft && displayValue !== correspondence?.address ? null : correspondence?.address_validation}
        />
      );
    }

    return <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(displayValue)}</span>;
  }

  const isAddressField = column.key === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY;
  const isClientCodeField = column.key === CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_KEY;
  const validationAnchorKey = fieldValidationKey || addressValidationKey;
  const submitHandler = isAddressField ? (onAddressEnterValidation || onSubmit) : onSubmit;
  const meunerieFieldsEnabled = areClientCorrespondenceMeunerieFieldsEnabled(draft);
  const isMeunerieDependentField = isClientCorrespondenceMeunerieDependentField(column.key);
  const isMeunerieDependentFieldDisabled = isMeunerieDependentField && !meunerieFieldsEnabled;

  if (column.type === "bill_to") {
    // Une fiche ne nomme qu'une entreprise facturée existante (Entreprise facturée = OUI), ou rien.
    return (
      <BillToSelect
        allowEmpty
        allowNew={false}
        ariaLabel={`Facturer à ${draft.client_name || ""}`.trim()}
        className={GRID_INPUT_CLASS_NAME}
        dataGridFocusKey={column.key}
        onChange={(value) => onChange(column.key, value)}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={draft[column.key]}
      />
    );
  }

  if (column.type === "boolean") {
    return (
      <select
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={draft[column.key] ? "true" : "false"}
        onChange={(event) => onChange(column.key, event.target.value === "true")}
      >
        <option value="true">Oui</option>
        <option value="false">Non</option>
      </select>
    );
  }

  if (column.type === "time") {
    return (
      <input
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        disabled={isMeunerieDependentFieldDisabled}
        onFocus={(event) => event.target.select()}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        onChange={(event) => onChange(column.key, event.target.value)}
        placeholder="HH:MM"
        title={isMeunerieDependentFieldDisabled ? CLIENT_CORRESPONDENCE_MEUNERIE_FIELDS_DISABLED_TITLE : undefined}
        type="time"
        value={draft[column.key]}
      />
    );
  }

  return (
    <input
      className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key}
      disabled={isMeunerieDependentFieldDisabled}
      draggable={isAddressField ? false : undefined}
      onChange={(event) => onChange(column.key, event.target.value)}
      onDragOver={isAddressField ? preventAddressInputDragEvent : undefined}
      onDragStart={isAddressField ? preventAddressInputDragEvent : undefined}
      onDrop={isAddressField ? preventAddressInputDragEvent : undefined}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: submitHandler })}
      placeholder={column.placeholder}
      ref={(isAddressField || isClientCodeField) ? (node) => registerAddressValidationFieldRef?.(validationAnchorKey, node) : undefined}
      required={isClientCodeField}
      aria-required={isClientCodeField ? "true" : undefined}
      title={isMeunerieDependentFieldDisabled ? CLIENT_CORRESPONDENCE_MEUNERIE_FIELDS_DISABLED_TITLE : undefined}
      type="text"
      value={draft[column.key]}
    />
  );
}

function ClientCorrespondenceGridRow({
  correspondence,
  draft,
  editingFocusKey,
  hasDraft,
  isEditing,
  isFocused = false,
  isPending,
  isSelectable = true,
  isSelected = false,
  onCancel,
  onChange,
  onDelete,
  onAddressEnterValidation = null,
  onFocusRow = null,
  onSave,
  onSelectionChange = null,
  onStartEdit,
  registerAddressValidationFieldRef = null,
  resolvedColumns = CLIENT_CORRESPONDENCE_GRID_COLUMNS,
  selectionColumnStyle = buildDataManagerGridSelectionColumnStyle(),
  selectionTitle = "Sélectionner ce client",
  validateAddress = null,
}) {
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);

  return (
    <tr
      ref={rowRef}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-client-correspondence-row-editing={isEditing ? "true" : undefined}
      data-client-correspondence-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isPending,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !onStartEdit ? undefined : (event) => onStartEdit(resolveGridFocusKeyFromEvent(event, CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY))}
      onKeyDownCapture={isEditing ? (event) => {
        const focusKey = resolveGridFocusKeyFromEvent(event, "");
        handleEditableGridKeyDown(event, {
          onCancel,
          onSubmit: isClientCorrespondenceAddressFocusKey(focusKey) ? onAddressEnterValidation : onSave,
        });
      } : undefined}
      title={isEditing ? undefined : "Double-cliquez pour éditer ce client"}
    >
      <td className={`${GRID_CELL_CLASS_NAME} text-center`} style={selectionColumnStyle}>
        <input
          type="checkbox"
          aria-label={`Sélectionner le client ${correspondence?.correspondence_id || ""}`}
          checked={Boolean(isSelected)}
          disabled={!isSelectable || isPending}
          onChange={(event) => onSelectionChange?.(event.target.checked)}
          onClick={(event) => {
            event.stopPropagation();
            handleDataManagerGridRowClick(onFocusRow);
          }}
          onDoubleClick={stopGridSelectionPropagation}
          title={!isSelectable ? selectionTitle : undefined}
          className={GRID_SELECTION_INPUT_CLASS_NAME}
        />
      </td>
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          data-grid-focus-key={column.key}
          style={buildResolvedGridColumnStyle(column)}
        >
          <ClientCorrespondenceGridField
            addressValidationKey={resolveClientCorrespondenceFieldValidationKey(column.key, { correspondenceId: correspondence?.correspondence_id })}
            fieldValidationKey={resolveClientCorrespondenceFieldValidationKey(column.key, { correspondenceId: correspondence?.correspondence_id })}
            column={column}
            correspondence={correspondence}
            draft={draft}
            hasDraft={hasDraft}
            isEditing={isEditing}
            onAddressEnterValidation={onAddressEnterValidation}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onSave}
            registerAddressValidationFieldRef={registerAddressValidationFieldRef}
            validateAddress={validateAddress}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>
        {isEditing ? (
          <div className={GRID_ACTIONS_ROW_CLASS_NAME}>
            <button type="button" onClick={onSave} disabled={isPending} className={GRID_PRIMARY_ACTION_CLASS_NAME}>
              <Save size={13} />
              Enregistrer
            </button>
            <button type="button" onClick={onCancel} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              Fermer
            </button>
            <button type="button" onClick={onDelete} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              <Trash2 size={13} />
              Supprimer
            </button>
          </div>
        ) : onStartEdit ? (
          <button type="button" onClick={() => onStartEdit(CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY)} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
            Modifier
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function NewClientCorrespondenceGridRow({ draft, onCancel, onChange, onCreate, onAddressEnterValidation = null, registerAddressValidationFieldRef = null, resolvedColumns = CLIENT_CORRESPONDENCE_GRID_COLUMNS, selectionColumnStyle = buildDataManagerGridSelectionColumnStyle() }) {
  const rowRef = useEditableRowFocus(true, CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY);

  return (
    <tr
      data-frozen-row-state="create"
      ref={rowRef}
      className="bg-slate-100"
      onKeyDownCapture={(event) => {
        const focusKey = resolveGridFocusKeyFromEvent(event, "");
        handleEditableGridKeyDown(event, {
          onCancel,
          onSubmit: isClientCorrespondenceAddressFocusKey(focusKey) ? onAddressEnterValidation : onCreate,
        });
      }}
    >
      <td className={GRID_CELL_CLASS_NAME} style={selectionColumnStyle} />
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          style={buildResolvedGridColumnStyle(column)}
        >
          <ClientCorrespondenceGridField
            addressValidationKey={resolveClientCorrespondenceFieldValidationKey(column.key, { isCreate: true })}
            fieldValidationKey={resolveClientCorrespondenceFieldValidationKey(column.key, { isCreate: true })}
            column={column}
            draft={draft}
            isEditing
            onAddressEnterValidation={onAddressEnterValidation}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onCreate}
            registerAddressValidationFieldRef={registerAddressValidationFieldRef}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE} />
    </tr>
  );
}

function EmployeeGridField({ column, draft, employee = null, hasDraft = false, isEditing = false, onCancel = null, onChange, onSubmit = null }) {
  const displayValue = hasDraft ? draft[column.key] : employee?.[column.key];

  // Compte portail : lu sur la fiche, jamais sur le brouillon.
  if (column.key === "driver_user_id") {
    return <GridOuiNonPill value={hasDriverPortalAccount(employee) ? "oui" : "non"} />;
  }

  if (!isEditing || column.type === "readonly" || column.readOnly) {
    if (column.type === "boolean") {
      return <GridOuiNonPill value={displayValue ? "oui" : "non"} />;
    }

    if (column.type === "shift") {
      const isNightShift = String(displayValue || "").trim().toLowerCase() === "night";

      return (
        <span className={[
          `inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]`,
          isNightShift ? "border border-amber-200 bg-amber-50 text-amber-700" : "border border-sky-200 bg-sky-50 text-sky-700",
        ].join(" ")}>
          {formatEmployeeShiftLabel(displayValue)}
        </span>
      );
    }

    if ((column.type === "time" || column.type === "number") && (displayValue ?? "") === "") {
      return <span className="whitespace-nowrap italic text-slate-400">valeur par défaut</span>;
    }

    return <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(displayValue)}{column.unit ? ` ${column.unit}` : ""}</span>;
  }

  if (column.type === "boolean") {
    return (
      <select
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={draft[column.key] ? "true" : "false"}
        onChange={(event) => onChange(column.key, event.target.value === "true")}
      >
        <option value="true">Oui</option>
        <option value="false">Non</option>
      </select>
    );
  }

  if (column.type === "shift") {
    return (
      <select
        className={GRID_INPUT_CLASS_NAME}
        data-grid-focus-key={column.key}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        value={draft[column.key] || "day"}
        onChange={(event) => onChange(column.key, event.target.value)}
      >
        <option value="day">Jour</option>
        <option value="night">Soir</option>
      </select>
    );
  }

  return (
    <input
      className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key}
      onChange={(event) => onChange(column.key, event.target.value)}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
      inputMode={column.type === "number" ? "decimal" : undefined}
      min={column.min}
      placeholder={column.placeholder}
      step={column.step}
      type={column.type === "time" ? "time" : column.type === "number" ? "number" : "text"}
      value={draft[column.key] ?? ""}
    />
  );
}

function EmployeeGridRow({
  draft,
  editingFocusKey,
  employee,
  hasDraft,
  isEditing,
  isFocused = false,
  isPending,
  isSelectable = true,
  isSelected = false,
  onCancel,
  onChange,
  onDelete,
  onFocusRow = null,
  onSave,
  onSelectionChange = null,
  onStartEdit,
  resolvedColumns = EMPLOYEE_GRID_COLUMNS,
  selectionColumnStyle = buildDataManagerGridSelectionColumnStyle(),
  selectionTitle = "Sélectionner ce chauffeur",
}) {
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);

  return (
    <tr
      ref={rowRef}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-employee-row-editing={isEditing ? "true" : undefined}
      data-employee-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isPending,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !onStartEdit ? undefined : (event) => onStartEdit(resolveGridFocusKeyFromEvent(event, EMPLOYEE_DEFAULT_FOCUS_KEY))}
      onKeyDownCapture={isEditing ? (event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onSave }) : undefined}
      title={isEditing ? undefined : "Double-cliquez pour éditer ce chauffeur"}
    >
      <td className={`${GRID_CELL_CLASS_NAME} text-center`} style={selectionColumnStyle}>
        <input
          type="checkbox"
          aria-label={`Sélectionner le chauffeur ${employee?.employee_id || ""}`}
          checked={Boolean(isSelected)}
          disabled={!isSelectable || isPending}
          onChange={(event) => onSelectionChange?.(event.target.checked)}
          onClick={(event) => {
            event.stopPropagation();
            handleDataManagerGridRowClick(onFocusRow);
          }}
          onDoubleClick={stopGridSelectionPropagation}
          title={!isSelectable ? selectionTitle : undefined}
          className={GRID_SELECTION_INPUT_CLASS_NAME}
        />
      </td>
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          data-grid-focus-key={column.key}
          style={buildResolvedGridColumnStyle(column)}
        >
          <EmployeeGridField
            column={column}
            draft={draft}
            employee={employee}
            hasDraft={hasDraft}
            isEditing={isEditing}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onSave}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>
        {isEditing ? (
          <div className={GRID_ACTIONS_ROW_CLASS_NAME}>
            <button type="button" onClick={onSave} disabled={isPending} className={GRID_PRIMARY_ACTION_CLASS_NAME}>
              <Save size={13} />
              Enregistrer
            </button>
            <button type="button" onClick={onCancel} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              Fermer
            </button>
            {onDelete ? (
              <button type="button" onClick={onDelete} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
                <Trash2 size={13} />
                Supprimer
              </button>
            ) : null}
          </div>
        ) : onStartEdit ? (
          <button type="button" onClick={() => onStartEdit(EMPLOYEE_DEFAULT_FOCUS_KEY)} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
            Modifier
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function NewEmployeeGridRow({ draft, onCancel, onChange, onCreate, resolvedColumns = EMPLOYEE_GRID_COLUMNS, selectionColumnStyle = buildDataManagerGridSelectionColumnStyle() }) {
  const rowRef = useEditableRowFocus(true, EMPLOYEE_DEFAULT_FOCUS_KEY);

  return (
    <tr
      data-frozen-row-state="create"
      ref={rowRef}
      className="bg-slate-100"
      onKeyDownCapture={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onCreate })}
    >
      <td className={GRID_CELL_CLASS_NAME} style={selectionColumnStyle} />
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          style={buildResolvedGridColumnStyle(column)}
        >
          <EmployeeGridField
            column={column}
            draft={draft}
            isEditing
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onCreate}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE} />
    </tr>
  );
}

function ProductGridField({
  productCatalog = [],
  column,
  draft,
  hasDraft = false,
  isEditing = false,
  onCancel,
  onChange,
  onSubmit,
  product = null,
}) {
  if (!isEditing) {
    return <span className={GRID_VALUE_CLASS_NAME}>{formatGridDisplayValue(draft?.[column.key] ?? product?.[column.key])}</span>;
  }

  if (column.key === "product_value") {
    return <DataManagerProductSelect products={productCatalog} optional value={draft.product_value ?? ""}
      originalValue={product ? product.product_value ?? "" : undefined} className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key} onChange={(value) => onChange(column.key, value)}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })} />;
  }

  return (
    <input
      className={GRID_INPUT_CLASS_NAME}
      data-grid-focus-key={column.key}
      inputMode={column.type === "number" ? (String(column.step || "").includes(".") ? "decimal" : "numeric") : undefined}
      onChange={(event) => onChange(column.key, event.target.value)}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
      placeholder={column.placeholder}
      min={column.min}
      step={column.step}
      type={column.type === "number" ? "number" : "text"}
      value={draft[column.key] ?? ""}
    />
  );
}

function ProductGridRow({
  productCatalog = [],
  draft,
  editingFocusKey,
  hasDraft,
  isEditing,
  isFocused = false,
  isPending,
  isSelectable = true,
  isSelected = false,
  onCancel,
  onChange,
  onDelete,
  onFocusRow = null,
  onSave,
  onSelectionChange = null,
  onStartEdit,
  product,
  resolvedColumns = PRODUCT_GRID_COLUMNS,
  selectionColumnStyle = buildDataManagerGridSelectionColumnStyle(),
  selectionTitle = "Sélectionner ce code produit",
}) {
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);

  return (
    <tr
      ref={rowRef}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-product-row-editing={isEditing ? "true" : undefined}
      data-product-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isPending,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !onStartEdit ? undefined : (event) => onStartEdit(resolveGridFocusKeyFromEvent(event, PRODUCT_DEFAULT_FOCUS_KEY))}
      onKeyDownCapture={isEditing ? (event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onSave }) : undefined}
      title={isEditing ? undefined : "Double-cliquez pour éditer ce code produit"}
    >
      <td className={`${GRID_CELL_CLASS_NAME} text-center`} style={selectionColumnStyle}>
        <input
          type="checkbox"
          aria-label={`Sélectionner le code produit ${product?.product_id || ""}`}
          checked={Boolean(isSelected)}
          disabled={!isSelectable || isPending}
          onChange={(event) => onSelectionChange?.(event.target.checked)}
          onClick={(event) => {
            event.stopPropagation();
            handleDataManagerGridRowClick(onFocusRow);
          }}
          onDoubleClick={stopGridSelectionPropagation}
          title={!isSelectable ? selectionTitle : undefined}
          className={GRID_SELECTION_INPUT_CLASS_NAME}
        />
      </td>
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          data-grid-focus-key={column.key}
          style={buildResolvedGridColumnStyle(column)}
        >
          <ProductGridField
            productCatalog={productCatalog}
            column={column}
            draft={draft}
            hasDraft={hasDraft}
            isEditing={isEditing}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onSave}
            product={product}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>
        {isEditing ? (
          <div className={GRID_ACTIONS_ROW_CLASS_NAME}>
            <button type="button" onClick={onSave} disabled={isPending} className={GRID_PRIMARY_ACTION_CLASS_NAME}>
              <Save size={13} />
              Enregistrer
            </button>
            <button type="button" onClick={onCancel} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              Fermer
            </button>
            <button type="button" onClick={onDelete} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              <Trash2 size={13} />
              Supprimer
            </button>
          </div>
        ) : onStartEdit ? (
          <button type="button" onClick={() => onStartEdit(PRODUCT_DEFAULT_FOCUS_KEY)} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
            Modifier
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function NewProductGridRow({ productCatalog = [], draft, onCancel, onChange, onCreate, resolvedColumns = PRODUCT_GRID_COLUMNS, selectionColumnStyle = buildDataManagerGridSelectionColumnStyle() }) {
  const rowRef = useEditableRowFocus(true, PRODUCT_DEFAULT_FOCUS_KEY);

  return (
    <tr
      data-frozen-row-state="create"
      ref={rowRef}
      className="bg-slate-100"
      onKeyDownCapture={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onCreate })}
    >
      <td className={GRID_CELL_CLASS_NAME} style={selectionColumnStyle} />
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          style={buildResolvedGridColumnStyle(column)}
        >
          <ProductGridField
            column={column}
            draft={draft}
            isEditing
            productCatalog={productCatalog}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onCreate}
          />
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE} />
    </tr>
  );
}

function CompartmentMatrixCell({ draft = null, hasDraft = false, isEditing = false, onChange, onSubmit = null, position, truck }) {
  const matchingCompartment = Array.isArray(truck?.compartments)
    ? truck.compartments.find((compartment) => Number(compartment?.position) === Number(position))
    : null;

  if (!isEditing) {
    const displayValue = hasDraft ? draft?.valuesByPosition?.[position] : matchingCompartment?.capacity_tons;

    return <span className={`${GRID_VALUE_CLASS_NAME} text-center`}>{formatGridDisplayValue(displayValue)}</span>;
  }

  return (
    <input
      className={`${GRID_COMPARTMENT_POSITION_INPUT_CLASS_NAME} px-1`}
      data-grid-focus-key={`position:${position}`}
      inputMode="decimal"
      onChange={(event) => onChange(position, event.target.value)}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => handleEditableGridKeyDown(event, { onSubmit })}
      placeholder="—"
      step="0.1"
      type="text"
      value={draft?.valuesByPosition?.[position] ?? ""}
    />
  );
}

function resolveCompartmentMatrixBaseFocusKey(column) {
  return column.type && column.type !== "readonly" ? column.key : COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY;
}

function CompartmentMatrixBaseField({ column, draft, hasDraft = false, isEditing = false, onCancel = null, onChangeTruckField, onSave, productCatalog = [], truck }) {
  // Mêmes affichages et éditeurs que l'onglet Camions ; la date de retrait sous le nom de la remorque.
  const field = <VehicleGridField column={column} draft={draft} hasDraft={hasDraft} isEditing={isEditing} onCancel={onCancel}
    onChange={onChangeTruckField} onSubmit={onSave} productCatalog={productCatalog} truck={truck} />;
  return column.key === "compartment_name" && !isEditing ? <DataManagerRetirementNotes truck={truck}>{field}</DataManagerRetirementNotes> : field;
}

function CompartmentMatrixRow({
  draft,
  editingFocusKey,
  hasDraft,
  isEditing,
  isFocused = false,
  isPending,
  isSelectable = true,
  isSelected = false,
  onCancel,
  onChangePosition,
  onChangeTruckField,
  onFocusRow = null,
  onSave,
  onSelectionChange = null,
  onStartEdit,
  productCatalog = [],
  resolvedColumns = COMPARTMENT_MATRIX_BASE_COLUMNS,
  selectionColumnStyle = buildDataManagerGridSelectionColumnStyle(),
  selectionTitle = "Sélectionner cette ligne de compartiments",
  truck,
}) {
  const rowLabel = truck?.compartment_name || truck?.truck_id;
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);

  return (
    <tr
      ref={rowRef}
      data-truck-id={truck.truck_id}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-compartment-row-editing={isEditing ? "true" : undefined}
      data-compartment-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isPending,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !onStartEdit ? undefined : (event) => onStartEdit(resolveGridFocusKeyFromEvent(event, COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY))}
      onKeyDownCapture={isEditing ? (event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onSave }) : undefined}
      title={isEditing ? undefined : `Double-cliquez pour éditer la remorque ${rowLabel}`}
    >
      <td className={`${GRID_CELL_CLASS_NAME} text-center`} style={selectionColumnStyle}>
        <input
          type="checkbox"
          aria-label={`Sélectionner la ligne de compartiments ${truck?.truck_id || ""}`}
          checked={Boolean(isSelected)}
          disabled={!isSelectable || isPending}
          onChange={(event) => onSelectionChange?.(event.target.checked)}
          onClick={(event) => {
            event.stopPropagation();
            handleDataManagerGridRowClick(onFocusRow);
          }}
          onDoubleClick={stopGridSelectionPropagation}
          title={!isSelectable ? selectionTitle : undefined}
          className={GRID_SELECTION_INPUT_CLASS_NAME}
        />
      </td>
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.position ? GRID_COMPARTMENT_POSITION_CELL_CLASS_NAME : column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          data-grid-focus-key={column.position ? column.key : resolveCompartmentMatrixBaseFocusKey(column)}
          style={buildResolvedGridColumnStyle(column)}
        >
          {column.position ? (
            <CompartmentMatrixCell
              draft={draft}
              hasDraft={hasDraft}
              isEditing={isEditing}
              onChange={onChangePosition}
              onSubmit={onSave}
              position={column.position}
              truck={truck}
            />
          ) : <CompartmentMatrixBaseField
            column={column}
            draft={draft}
            hasDraft={hasDraft}
            isEditing={isEditing}
            onCancel={onCancel}
            onChangeTruckField={onChangeTruckField}
            onSave={onSave}
            productCatalog={productCatalog}
            truck={truck}
          />}
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>
        {isEditing ? (
          <div className={GRID_ACTIONS_ROW_CLASS_NAME}>
            <button type="button" onClick={onSave} disabled={isPending} className={GRID_PRIMARY_ACTION_CLASS_NAME}>
              <Save size={13} />
              Enregistrer
            </button>
            <button type="button" onClick={onCancel} disabled={isPending} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
              Fermer
            </button>
          </div>
        ) : onStartEdit ? (
          <button type="button" onClick={() => onStartEdit(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY)} className={GRID_SECONDARY_ACTION_CLASS_NAME}>
            Modifier
          </button>
        ) : null}
      </td>
    </tr>
  );
}

// « Ajouter une remorque » : remorque seule, sans camion ni chauffeur ; ses compartiments s'enregistrent ensuite.
function NewTrailerMatrixRow({ draft, onCancel, onChange, onChangePosition, onCreate, productCatalog = [], resolvedColumns = COMPARTMENT_MATRIX_BASE_COLUMNS, selectionColumnStyle = buildDataManagerGridSelectionColumnStyle() }) {
  const rowRef = useEditableRowFocus(true, "compartment_name");
  const truck = { unit_kind: "remorque", truck_type: "citerne", compartments: [] };

  return (
    <tr data-frozen-row-state="create" data-new-trailer-row ref={rowRef} className="bg-slate-100" onKeyDownCapture={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit: onCreate })}>
      <td className={GRID_CELL_CLASS_NAME} style={selectionColumnStyle} />
      {resolvedColumns.map((column) => (
        <td
          key={column.key}
          className={`${GRID_CELL_CLASS_NAME} ${column.position ? GRID_COMPARTMENT_POSITION_CELL_CLASS_NAME : column.widthClassName || ""} ${column.frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : ""}`}
          data-frozen-cell={column.frozenLayoutEntry ? "true" : undefined}
          style={buildResolvedGridColumnStyle(column)}
        >
          {column.position ? <CompartmentMatrixCell draft={draft} hasDraft isEditing onChange={onChangePosition} onSubmit={onCreate} position={column.position} truck={truck} />
            : <CompartmentMatrixBaseField column={column} draft={draft} hasDraft isEditing onCancel={onCancel} onChangeTruckField={onChange}
              onSave={onCreate} productCatalog={productCatalog} truck={truck} />}
        </td>
      ))}
      <td className={GRID_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE} />
    </tr>
  );
}

function AdminTransferCard({
  accept = ".json,application/json",
  disabledExport = false,
  disabledImport = false,
  onExport = null,
  onImport = null,
  title,
}) {
  const inputRef = React.useRef(null);
  const supportsImport = typeof onImport === "function";

  return (
    <article className="flex min-w-0 flex-col rounded-lg border border-black/6 bg-white px-2.5 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-1.5">
        <h3 className="text-[0.92rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950">{title}</h3>
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-accent text-white"><ShieldCheck size={14} /></span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {typeof onExport === "function" ? (
          <button type="button" onClick={onExport} disabled={disabledExport} className={SECONDARY_ACTION_CLASS_NAME}>
            <Download size={14} />
            Exporter
          </button>
        ) : null}
        {supportsImport ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  onImport(file);
                }
                event.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabledImport}
              className={SECONDARY_ACTION_CLASS_NAME}
            >
              <Upload size={14} />
              Importer
            </button>
          </>
        ) : null}
      </div>
    </article>
  );
}

function HistoryPanel({
  activeTab,
  history,
  isCollapsed = false,
  isFullPage = false,
  isLoaded = true,
  isLoading = false,
  onCloseFullPage = null,
  onLoadAuditDetails = null,
  onOpenFullPage = null,
  onToggleCollapsed = null,
  searchQuery = "",
}) {
  const [historySortState, setHistorySortState] = useDataManagerTableSortState("history", () => ({ direction: "desc", key: "date" }));
  const effectiveHistorySortState = resolveDataManagerHistorySortState(historySortState, activeTab);
  const sortKey = effectiveHistorySortState.key || "date";
  const sortDirection = effectiveHistorySortState.direction;
  const isClientHistory = activeTab === "correspondences";
  const canShowAuditDetails = isClientHistory || (isFullPage && typeof onLoadAuditDetails === "function");
  const [selectedAuditId, setSelectedAuditId] = React.useState("");
  const [selectedClientDetailRowKey, setSelectedClientDetailRowKey] = React.useState("");
  const [auditDetailsById, setAuditDetailsById] = React.useState({});
  const [auditDetailErrorMessage, setAuditDetailErrorMessage] = React.useState("");
  const [loadingAuditId, setLoadingAuditId] = React.useState("");
  const sectionLabel = resolveHistorySectionLabel(activeTab);
  const normalizedSearchQuery = typeof searchQuery === "string" ? searchQuery.trim() : "";

  const sectionHistoryRows = React.useMemo(
    () => buildHistoryRows(history).filter((row) => row.section === activeTab),
    [activeTab, history]
  );

  const filteredHistoryRows = React.useMemo(
    () => sectionHistoryRows.filter((row) => matchesHistorySearch(row, normalizedSearchQuery)),
    [normalizedSearchQuery, sectionHistoryRows]
  );

  const previewRows = React.useMemo(
    () => sortHistoryRows(filteredHistoryRows, "date", "desc").slice(0, HISTORY_PREVIEW_LIMIT),
    [filteredHistoryRows]
  );

  const fullRows = React.useMemo(
    () => sortHistoryRows(filteredHistoryRows, sortKey, sortDirection),
    [filteredHistoryRows, sortDirection, sortKey]
  );
  const historyScrollRef = useDataManagerTableScrollRestore("history", fullRows.length);
  const selectedAuditEntry = selectedAuditId
    ? isClientHistory
      ? history.find((entry) => entry.audit_id === selectedAuditId) || null
      : auditDetailsById[selectedAuditId] || null
    : null;
  const selectedAuditFieldChanges = React.useMemo(
    () => resolveDataManagerAuditFieldChanges(selectedAuditEntry),
    [selectedAuditEntry]
  );
  const selectedAuditRow = React.useMemo(
    () => fullRows.find((row) => row.auditId === selectedAuditId) || null,
    [fullRows, selectedAuditId]
  );

  React.useEffect(() => {
    if (!isClientHistory || !selectedClientDetailRowKey) return;
    document.getElementById(`client-audit-detail-${selectedClientDetailRowKey}`)
      ?.scrollIntoView({ block: "nearest", inline: "start" });
  }, [isClientHistory, selectedClientDetailRowKey]);

  const visibleRows = isFullPage ? fullRows : previewRows;
  const historySummaryLabel = normalizedSearchQuery
    ? isLoaded
      ? `${filteredHistoryRows.length} résultat(s) de traçabilité pour « ${normalizedSearchQuery} »`
      : `Traçabilité chargée à l’ouverture pour « ${normalizedSearchQuery} »`
    : isLoading
      ? "Chargement de la traçabilité..."
      : isLoaded
        ? isClientHistory
          ? `${sectionHistoryRows.length} ligne(s) de traçabilité liées aux clients`
          : `${sectionHistoryRows.length} modification(s) liées à ${sectionLabel.toLowerCase()}`
        : "Traçabilité chargée à l’ouverture";

  function handleSort(nextSortKey) {
    setHistorySortState((currentSortState) => {
      const effectiveCurrentSortState = resolveDataManagerHistorySortState(currentSortState, activeTab);
      const currentSortKey = effectiveCurrentSortState.key || "date";

      if (currentSortKey === nextSortKey) {
        return {
          direction: effectiveCurrentSortState.direction === "asc" ? "desc" : "asc",
          key: nextSortKey,
        };
      }

      return {
        direction: nextSortKey === "date" ? "desc" : "asc",
        key: nextSortKey,
      };
    });
  }

  async function handleToggleAuditDetails(auditId, rowKey) {
    if (!canShowAuditDetails || !auditId) {
      return;
    }

    if (isClientHistory) {
      const isSameRow = selectedClientDetailRowKey === rowKey;
      setSelectedAuditId(isSameRow ? "" : auditId);
      setSelectedClientDetailRowKey(isSameRow ? "" : rowKey);
      setAuditDetailErrorMessage("");
      return;
    }

    if (selectedAuditId === auditId && auditDetailsById[auditId]) {
      setSelectedAuditId("");
      setAuditDetailErrorMessage("");
      return;
    }

    setSelectedAuditId(auditId);
    setAuditDetailErrorMessage("");
    if (auditDetailsById[auditId]) {
      return;
    }

    try {
      setLoadingAuditId(auditId);
      const detailEntry = await onLoadAuditDetails(auditId);
      setAuditDetailsById((currentState) => ({
        ...currentState,
        [auditId]: detailEntry,
      }));
    } catch (error) {
      setAuditDetailErrorMessage(resolveMutationErrorMessage(error, "Impossible de charger le détail de traçabilité."));
    } finally {
      setLoadingAuditId((currentAuditId) => (currentAuditId === auditId ? "" : currentAuditId));
    }
  }

  function renderSortableHeaderCell(columnKey, label, widthClassName) {
    const { direction: activeSortDirection, isActiveSort } = resolveDataManagerActiveSortState(
      { direction: sortDirection, key: sortKey },
      columnKey,
    );
    const SortIndicatorIcon = activeSortDirection === "desc" ? ChevronDown : ChevronUp;

    if (!isFullPage) {
      return <th className={`${GRID_HEADER_CELL_CLASS_NAME} ${widthClassName}`}>{label}</th>;
    }

    return (
      <th
        className={[
        GRID_HEADER_CELL_CLASS_NAME,
        widthClassName,
        isActiveSort ? DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME : "",
      ].filter(Boolean).join(" ")}>
        <button
          type="button"
          onClick={() => handleSort(columnKey)}
          className={[
            "inline-flex items-center gap-1 text-left transition-colors duration-150",
            isActiveSort ? DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME : "text-slate-800 hover:text-slate-950",
          ].join(" ")}
        >
          <span>{label}</span>
          {isActiveSort ? (
            <span className={DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME}>
              <SortIndicatorIcon
                aria-hidden="true"
                size={14}
                strokeWidth={2.5}
              />
            </span>
          ) : null}
        </button>
      </th>
    );
  }

  return (
    <article className={[PANEL_CLASS_NAME, "traceability-panel", isFullPage ? "min-h-[70vh]" : ""].join(" ")}>
      <div className="traceability-banner">
        <div className="traceability-heading">
          {!isFullPage && onToggleCollapsed ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={isCollapsed ? `Déplier la traçabilité ${sectionLabel.toLowerCase()}` : `Replier la traçabilité ${sectionLabel.toLowerCase()}`}
              className="traceability-toggle inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950"
            >
              <ChevronRight size={13} className={isCollapsed ? "" : "rotate-90"} />
            </button>
          ) : null}
          <span className="traceability-icon"><History size={15} /></span>
          <div className="traceability-copy">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Traçabilité</p>
            <h3 className="text-[0.98rem] font-semibold tracking-[-0.02em] text-slate-950">
              {isFullPage ? `Traçabilité complète · ${sectionLabel}` : `${HISTORY_PREVIEW_LIMIT} derniers changements`}
            </h3>
          </div>
        </div>

        <div className="traceability-actions">
          <span>{historySummaryLabel}</span>
          {isFullPage ? (
            <button type="button" onClick={onCloseFullPage} className={SECONDARY_ACTION_CLASS_NAME}>
              Retour à la section
            </button>
          ) : filteredHistoryRows.length > 0 ? (
            <button type="button" onClick={onOpenFullPage} className={SECONDARY_ACTION_CLASS_NAME}>
              Voir la traçabilité complète
            </button>
          ) : null}
        </div>
      </div>

      {!isClientHistory && canShowAuditDetails && selectedAuditRow ? (
        <div className="mt-3 rounded-[16px] border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Détail à la demande</p>
              <h4 className="mt-1 text-[13px] font-semibold text-slate-950">
                {`${selectedAuditRow.entityLabel} · ${selectedAuditRow.dateLabel}`}
              </h4>
              <p className="mt-1 text-[11px] text-slate-500">
                {selectedAuditEntry
                  ? `${formatAuditActionLabel(selectedAuditEntry.action)} · ${formatAuditImpactLabel(selectedAuditEntry.impact_level)}`
                  : "Chargement du before/after complet pour cet audit."}
              </p>
            </div>
            <button type="button" onClick={() => {
              setSelectedAuditId("");
              setAuditDetailErrorMessage("");
            }} className={SECONDARY_ACTION_CLASS_NAME}>
              Fermer le détail
            </button>
          </div>

          {loadingAuditId === selectedAuditId ? (
            <div className="mt-3 rounded-[14px] border border-dashed border-slate-300 bg-white px-3 py-3 text-[12px] text-slate-600">
              Chargement du détail complet de cet audit...
            </div>
          ) : auditDetailErrorMessage ? (
            <div className="mt-3 rounded-[14px] border border-rose-200 bg-rose-50 px-3 py-3 text-[12px] text-rose-700">
              {auditDetailErrorMessage}
            </div>
          ) : selectedAuditEntry ? (
            <div className="mt-3 space-y-2">
              {selectedAuditEntry.summary ? (
                <p className="rounded-[14px] border border-slate-200 bg-white px-3 py-2 text-[11px] leading-5 text-slate-600">
                  {selectedAuditEntry.summary}
                </p>
              ) : null}
              {selectedAuditFieldChanges.length > 0 ? selectedAuditFieldChanges.map((fieldChange, index) => (
                <div key={`${selectedAuditEntry.audit_id}:${fieldChange?.field || "field"}:${index}`} className="rounded-[14px] border border-slate-200 bg-white px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-slate-950">{formatAuditFieldLabel(fieldChange?.field)}</span>
                    <span className="text-[10px] uppercase tracking-[0.12em] text-slate-400">{selectedAuditEntry.audit_id}</span>
                  </div>
                  <div className="mt-2 grid gap-2 lg:grid-cols-2">
                    <div className="rounded-[12px] bg-slate-50 px-2.5 py-2">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Avant</p>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap wrap-break-word text-[10.5px] leading-5 text-slate-600">{formatAuditDetailValue(fieldChange?.old_value)}</pre>
                    </div>
                    <div className="rounded-[12px] bg-slate-50 px-2.5 py-2">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Après</p>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap wrap-break-word text-[10.5px] leading-5 text-slate-900">{formatAuditDetailValue(fieldChange?.new_value)}</pre>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="rounded-[14px] border border-dashed border-slate-300 bg-white px-3 py-3 text-[12px] text-slate-600">
                  Aucun champ détaillé n'est disponible pour cet audit.
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-2">
        {!isFullPage && isCollapsed ? null : isLoading ? (
          <div className="rounded-[16px] border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] text-slate-600">
            Chargement de la traçabilité de la section {sectionLabel.toLowerCase()}...
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="rounded-[16px] border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] text-slate-600">
            {normalizedSearchQuery
              ? `Aucune ligne de traçabilité ne correspond à « ${normalizedSearchQuery} ».`
              : isClientHistory
                ? "Aucun changement de client n’a encore été enregistré."
              : activeTab === "orders"
                ? "Aucune modification de commande n’a encore été enregistrée."
                : activeTab === "compartments"
                  ? "Aucune modification de compartiment n’a encore été enregistrée."
                  : "Aucune modification de camion n’a encore été enregistrée."}
          </div>
        ) : (
          <div
            ref={historyScrollRef}
            className={["overflow-auto border border-slate-200/90", isClientHistory ? "rounded-[4px]" : "rounded-lg", isFullPage ? "max-h-[calc(100vh-18rem)]" : "max-h-120"].join(" ")}
          >
            <table className="min-w-full border-collapse text-[11px] text-slate-700">
              <thead className="sticky top-0 z-10 bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.95))]">
                <tr>
                  {renderSortableHeaderCell("date", "Date", "min-w-24")}
                  {isClientHistory ? renderSortableHeaderCell("action", "Action", "min-w-28") : null}
                  {renderSortableHeaderCell("actor", "Utilisateur", "min-w-36")}
                  {renderSortableHeaderCell("source", "Source", "min-w-28")}
                  {renderSortableHeaderCell("orderNumber", "N° commande", "min-w-28")}
                  {renderSortableHeaderCell("entity", "Élément", "min-w-40")}
                  {renderSortableHeaderCell("field", "Champ", "min-w-28")}
                  {renderSortableHeaderCell("before", "Avant", "min-w-28")}
                  {renderSortableHeaderCell("after", "Après", "min-w-28")}
                  {canShowAuditDetails ? <th className={`${GRID_HEADER_CELL_CLASS_NAME} min-w-20`}>Détail</th> : null}
                </tr>
              </thead>
              <tbody className="bg-white">
                {visibleRows.map((row, rowIndex) => {
                  const isExpanded = isClientHistory
                    ? selectedClientDetailRowKey === row.rowKey && Boolean(selectedAuditEntry)
                    : selectedAuditId === row.auditId && Boolean(selectedAuditEntry);
                  const detailId = `client-audit-detail-${row.rowKey}`;
                  return (
                    <React.Fragment key={row.rowKey}>
                      <tr className={rowIndex % 2 === 0 ? "bg-slate-100" : undefined}>
                        <td className={`${GRID_CELL_CLASS_NAME} whitespace-nowrap text-[10.5px] text-slate-500`}>{row.dateLabel}</td>
                        {isClientHistory ? (
                          <td className={GRID_CELL_CLASS_NAME}>
                            <span className={`inline-flex h-4 items-center whitespace-nowrap rounded-[4px] border px-1.5 text-[10px] font-semibold leading-none ${row.actionClassName}`}>
                              {row.actionLabel}
                            </span>
                          </td>
                        ) : null}
                        <td className={GRID_CELL_CLASS_NAME} title={row.actorLabel}>
                          <span className="block max-w-44 truncate text-[11px] text-slate-700">{row.actorLabel}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME} title={row.sourceLabel}>
                          <span className="block max-w-36 truncate text-[11px] text-slate-700">{row.sourceLabel}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME} title={row.orderNumberLabel}>
                          <span className="block max-w-36 truncate text-[11px] font-medium text-slate-900">{row.orderNumberLabel}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME} title={row.entityLabel}>
                          <span className="block max-w-60 truncate text-[11px] font-semibold text-slate-900">{row.entityLabel}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME}>
                          <span className="block max-w-44 truncate" title={row.fieldLabel}>{row.fieldLabel}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME}>
                          <span className="block max-w-52 truncate text-slate-500" title={row.beforeValue}>{row.beforeValue}</span>
                        </td>
                        <td className={GRID_CELL_CLASS_NAME}>
                          <span className="block max-w-52 truncate font-medium text-slate-900" title={row.afterValue}>{row.afterValue}</span>
                        </td>
                        {canShowAuditDetails ? (
                          <td className={`${GRID_CELL_CLASS_NAME} whitespace-nowrap`}>
                            <button
                              type="button"
                              onClick={() => handleToggleAuditDetails(row.auditId, row.rowKey)}
                              aria-expanded={isClientHistory ? isExpanded : undefined}
                              aria-controls={isClientHistory ? detailId : undefined}
                              className={`inline-flex h-7 items-center ${isClientHistory ? "rounded-[4px]" : "rounded-full"} border border-slate-200 bg-white px-2.5 text-[10px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950`}
                            >
                              {loadingAuditId === row.auditId
                                ? "Chargement..."
                                : isExpanded
                                  ? "Masquer"
                                  : "Détail"}
                            </button>
                          </td>
                        ) : null}
                      </tr>
                      {isClientHistory && isExpanded ? (
                        <tr id={detailId} data-client-audit-detail="true">
                          <td colSpan={10} className="border-b border-slate-200 bg-slate-50 px-2 py-2">
                            <div className="max-h-72 w-fit max-w-full overflow-auto rounded-[4px] border border-slate-200 bg-white">
                              <table aria-label="Détail du changement client" className="min-w-full border-collapse text-[11px] text-slate-700">
                                <thead className="sticky top-0 bg-slate-50">
                                  <tr>
                                    <th className={GRID_HEADER_CELL_CLASS_NAME}>Champ</th>
                                    <th className={GRID_HEADER_CELL_CLASS_NAME}>Avant</th>
                                    <th className={GRID_HEADER_CELL_CLASS_NAME}>Après</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedAuditFieldChanges.length > 0 ? selectedAuditFieldChanges.map((fieldChange, index) => (
                                    <tr key={`${selectedAuditId}:${fieldChange.field}:${index}`} className="odd:bg-slate-100">
                                      <td className={`${GRID_CELL_CLASS_NAME} min-w-28`}>{formatAuditFieldLabel(fieldChange.field)}</td>
                                      <td className={`${GRID_CELL_CLASS_NAME} min-w-40`}><span className="block max-w-lg whitespace-pre-wrap wrap-break-word">{formatAuditValue(fieldChange.old_value)}</span></td>
                                      <td className={`${GRID_CELL_CLASS_NAME} min-w-40`}><span className="block max-w-lg whitespace-pre-wrap wrap-break-word">{formatAuditValue(fieldChange.new_value)}</span></td>
                                    </tr>
                                  )) : <tr><td colSpan={3} className={GRID_CELL_CLASS_NAME}>Aucun champ détaillé n'est disponible pour cet audit.</td></tr>}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isFullPage && !isCollapsed && filteredHistoryRows.length > HISTORY_PREVIEW_LIMIT ? (
          <p className="mt-2 text-[10.5px] leading-4 text-slate-500">
            {isClientHistory
              ? `Aperçu limité aux ${HISTORY_PREVIEW_LIMIT} dernières lignes de traçabilité des clients.`
              : `Aperçu limité aux ${HISTORY_PREVIEW_LIMIT} dernières modifications de la section ${sectionLabel.toLowerCase()}.`}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function DataManagerSortableHeaderCell({
  align = "left",
  className = "",
  columnKey,
  columnWidth = null,
  defaultColumnWidth = null,
  draggable = false,
  frozenLayoutEntry = null,
  onColumnContextMenu = null,
  label,
  onDrop = null,
  onResizePointerDown = null,
  onSort,
  sortState,
  title,
}) {
  const { direction: activeSortDirection, isActiveSort } = resolveDataManagerActiveSortState(sortState, columnKey);
  const SortIndicatorIcon = activeSortDirection === "desc" ? ChevronDown : ChevronUp;
  const thRef = React.useRef(null);
  const { dragOverlay, isDragging } = useSortableColumnHeaderDrag({
    columnKey,
    columnKeyAttribute: "data-column-key",
    draggable,
    label,
    onMove: onDrop,
    thRef,
  });

  const handleClick = React.useCallback(() => {
    onSort(columnKey);
  }, [columnKey, onSort]);

  return (
    <th
      ref={thRef}
      data-column-key={draggable ? columnKey : undefined}
      title={title}
      aria-sort={resolveDataManagerHeaderAriaSort(sortState, columnKey)}
      className={[
        GRID_HEADER_CELL_CLASS_NAME,
        className,
        frozenLayoutEntry ? DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME : "",
        frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : "",
        isActiveSort ? DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME : "",
        draggable ? "cursor-grab select-none" : "",
        isDragging ? "border border-dashed border-accent/40 bg-slate-50/30 opacity-30" : "",
      ].filter(Boolean).join(" ")}
      style={(() => {
        const resolvedWidth = resolveDataManagerGridColumnWidth({
          width: columnWidth,
          defaultWidth: defaultColumnWidth,
        });
        return buildFrozenColumnCellStyle(
          frozenLayoutEntry,
          resolvedWidth > 0 ? buildOrderGridColumnStyle(resolvedWidth) : undefined,
          { isHeader: true },
        );
      })()}
      onClick={handleClick}
      onContextMenu={onColumnContextMenu ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        onColumnContextMenu({
          columnKey, columnLabel: label,
          x: Math.max(0, Math.min(event.clientX, window.innerWidth - 256)),
          y: Math.max(0, Math.min(event.clientY, window.innerHeight - 112)),
        });
      } : undefined}
    >
      <div className="relative flex min-w-0 items-center pr-2.5">
        <span className={[
          SORTABLE_HEADER_BUTTON_CLASS_NAME,
          "min-w-0",
          align === "center" ? "justify-center" : "justify-start",
          isActiveSort ? DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME : "text-slate-800",
        ].join(" ")}>
          {frozenLayoutEntry ? <Pin aria-label="Colonne figée" size={13} className={DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME} strokeWidth={DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH} /> : null}
          <span className="truncate">{label}</span>
          {isActiveSort ? (
            <span className={DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME}>
              <SortIndicatorIcon
                aria-hidden="true"
                size={14}
                strokeWidth={2.5}
              />
            </span>
          ) : null}
        </span>
        {onResizePointerDown ? (
          <button
            type="button"
            aria-label={`Redimensionner la colonne ${label}`}
            className="absolute inset-y-0 right-0 z-10 w-3 translate-x-1/2 cursor-col-resize touch-none select-none bg-transparent p-0 after:absolute after:bottom-1 after:left-1/2 after:top-1 after:w-px after:-translate-x-1/2 after:rounded-full after:bg-[rgba(148,163,184,0.38)] hover:after:bg-[rgba(15,118,110,0.72)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/25"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onResizePointerDown(event, columnKey);
            }}
            title={`Redimensionner la colonne ${label}`}
          />
        ) : null}
      </div>
      <ColumnHeaderDragOverlay dragOverlay={dragOverlay} />
    </th>
  );
}

// PHASE 1.5 TEMP: diagnostic local des transitions d'etat de grille Data Manager.
function logPhase15DataManagerGridSnapshot(payload) {
  if (!isDiagnosticsActive()) {
    return;
  }

  recordDiagnosticsEvent("DataManagerPage", "grid_snapshot", payload);
}

export const __DATA_MANAGER_PAGE_TEST_CONTRACT = {
  VEHICLE_GRID_COLUMNS,
  HISTORY_FIELD_LABELS,
  buildEmployeeDriverSelectOptions,
  createTruckDraft,
  buildTruckPayload,
  hasDriverPortalAccount,
  findClientCorrespondenceClientCodeConflict,
  normalizeClientCorrespondenceImportKey,
};

export default function DataManagerPage() {
  const mobile = useMediaQuery(MOBILE_QUERY);
  const VehicleTable = mobile ? "div" : "table";
  const VehicleBody = mobile ? "div" : "tbody";
  const { access, hasPermission: hasAccessPermission } = useAccess();
  const { showToast, dismissToast } = useToast();
  const navigate = useNavigate();
  const handleColumnPreferencesSaveError = React.useCallback((error) => {
    const errorDetail = error instanceof Error && error.message
      ? error.message
      : "Impossible de sauvegarder les préférences de colonnes.";
    showToast({
      duration: 7000,
      title: "Préférences de colonnes non enregistrées",
      message: `${errorDetail} La dernière configuration synchronisée a été restaurée.`,
      type: "error",
    });
  }, [showToast]);
  const { missingCivicNumberDialog, requestMissingCivicNumberConfirmation } = useMissingCivicNumberConfirmation();
  const [employeeIdentityConfirmation, setEmployeeIdentityConfirmation] = React.useState(null);
  React.useEffect(() => {
    const confirmIdentity = (event) => {
      event.detail.handled = true;
      const finish = (accepted) => { setEmployeeIdentityConfirmation(null); event.detail.resolve(accepted); };
      setEmployeeIdentityConfirmation({ isOpen: true, title: "Synchroniser l'identité chauffeur", description: "Cette modification du nom ou du courriel doit aussi être enregistrée sur le compte portail de l'employé.",
        confirmCheckbox: { key: "shared-identity", label: "Je comprends que le local et le SaaS en ligne utilisent le même compte Supabase.", required: true },
        onConfirm: () => finish(true), onClose: () => finish(false), onCancel: () => finish(false) });
    };
    window.addEventListener("grain-confirm-employee-identity", confirmIdentity);
    return () => window.removeEventListener("grain-confirm-employee-identity", confirmIdentity);
  }, []);
  const [dompeurConversionConfirmation, setDompeurConversionConfirmation] = React.useState(null);
  React.useEffect(() => {
    const confirmConversion = (event) => {
      event.detail.handled = true;
      const { challenge } = event.detail;
      const finish = (accepted) => { setDompeurConversionConfirmation(null); event.detail.resolve(accepted); };
      setDompeurConversionConfirmation({ isOpen: true, tone: "warning", title: "Détacher les attelages ?",
        description: `Ce camion devient dompeur : il ne tire plus d'autre remorque et aucun autre camion ne le tire. Ces attelages des journées ouvertes seront détachés ; les remorques concernées passeront à « ${MISSING_TRACTOR_LABEL} ». Les chauffeurs restent sur leur remorque : vérifiez-les ensuite. Les journées clôturées gardent leur historique.`,
        summaryItems: (challenge.detachments || []).map((item) => ({
          label: `${item.dispatch_date} — ${item.dispatch_period === "evening" ? "Soir" : "Jour"}`,
          value: `${item.trailer_label} · camion ${item.tractor_name || item.tractor_truck_id}`,
        })),
        confirmLabel: "Détacher et enregistrer",
        onConfirm: () => finish(true), onClose: () => finish(false), onCancel: () => finish(false) });
    };
    window.addEventListener("grain-confirm-dompeur-conversion", confirmConversion);
    return () => window.removeEventListener("grain-confirm-dompeur-conversion", confirmConversion);
  }, []);
  // « Changer le type ? » avant l'envoi, quand le changement efface ; « Annuler » garde la ligne en modification.
  const [truckTypeConfirmation, setTruckTypeConfirmation] = React.useState(null);
  const confirmTruckTypeChange = (description) => new Promise((resolve) => {
    const finish = (accepted) => { setTruckTypeConfirmation(null); resolve(accepted); };
    setTruckTypeConfirmation({ isOpen: true, tone: "warning", title: "Changer le type ?", description, confirmLabel: "Changer le type",
      cancelLabel: "Annuler", onConfirm: () => finish(true), onClose: () => finish(false), onCancel: () => finish(false) });
  });
  const [pmTargetSuspensionConfirmation, setPmTargetSuspensionConfirmation] = React.useState(null);
  React.useEffect(() => {
    const confirmPmTargetSuspension = (event) => {
      event.detail.handled = true;
      setPmTargetSuspensionConfirmation(buildEndOfDayLoadTargetSuspensionDialog(event.detail.challenge, (decision) => {
        setPmTargetSuspensionConfirmation(null);
        event.detail.resolve(decision);
      }));
    };
    window.addEventListener("grain-confirm-pm-target-suspension", confirmPmTargetSuspension);
    return () => window.removeEventListener("grain-confirm-pm-target-suspension", confirmPmTargetSuspension);
  }, []);
  const [compartmentSharingConfirmation, setCompartmentSharingConfirmation] = React.useState(null);
  React.useEffect(() => {
    // Chargé PM : compléter un compartiment entamé du même produit (sinon le Chargé PM est refusé).
    const confirmCompartmentSharing = (event) => {
      event.detail.handled = true;
      const content = buildCompartmentSharingConfirmationContent(event.detail.challenge.error_details);
      const finish = (accepted) => { setCompartmentSharingConfirmation(null); event.detail.resolve(accepted); };
      setCompartmentSharingConfirmation({ isOpen: true, eyebrow: "Compartiments", tone: "accent", title: content.title, description: content.description,
        confirmLabel: content.confirmLabel, cancelLabel: content.cancelLabel, summaryLayout: "stack", summaryItems: content.summaryItems,
        onConfirm: () => finish(true), onClose: () => finish(false), onCancel: () => finish(false) });
    };
    window.addEventListener("grain-confirm-compartment-sharing", confirmCompartmentSharing);
    return () => window.removeEventListener("grain-confirm-compartment-sharing", confirmCompartmentSharing);
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const savedTab = readSavedActiveTab(access.userId);
  const activeTab = resolveActiveTab(searchParams, savedTab);
  const isDriversView = activeTab === "employees";
  const selectedTabKey = isDriversView ? DRIVERS_TAB_KEY : activeTab;
  React.useEffect(() => {
    saveActiveTab(access.userId, selectedTabKey);
  }, [access.userId, selectedTabKey]);
  const historyView = resolveHistoryView(searchParams);
  const vehiclePeriod = searchParams.get("truck_period") === "evening" ? "evening" : "day";
  const clientTypeFilter = resolveClientTypeFilter(searchParams.get(CLIENT_TYPE_URL_PARAM));
  const requestedOrdersSearchQuery = resolveOrdersSearchQuery(searchParams);
  const requestedOrderBrowseDateFilter = resolveOrderBrowseDateQuery(searchParams);
  const requestedOrderScope = resolveOrderScopeQuery(searchParams);
  const contextualDispatchDate = resolveContextualDispatchDate(searchParams);
  const normalizedContextualDispatchDate = resolveOrderDateFilterValue(contextualDispatchDate);
  const contextualDispatchPeriod = resolveContextualDispatchPeriod(searchParams);
  const dispatchDayCalendar = useDispatchDayCalendarContext({
    activeDate: normalizedContextualDispatchDate,
    isOpen: !normalizedContextualDispatchDate,
  });
  const defaultDataManagerDispatchDate = resolveOrderDateFilterValue(
    dispatchDayCalendar.defaultDispatchDate,
  );
  const effectiveContextualDispatchDate = normalizedContextualDispatchDate || defaultDataManagerDispatchDate;
  const canEditOrders = canEditOrdersForRole(access);
  const [searchQuery, setSearchQuery] = React.useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [ordersSearchQuery, setOrdersSearchQuery] = React.useState(requestedOrdersSearchQuery);
  const deferredOrdersSearchQuery = useDeferredValue(ordersSearchQuery);
  const [orderScope, setOrderScope] = React.useState(
    requestedOrderScope || (requestedOrdersSearchQuery || requestedOrderBrowseDateFilter ? "browse" : "dispatch")
  );
  const [orderBrowseDateFilter, setOrderBrowseDateFilter] = React.useState(requestedOrderBrowseDateFilter);
  const [isPendingBillingApprovalFilter, setIsPendingBillingApprovalFilter] = React.useState(searchParams.get("orderPending") === "1");
  const orderApprovalQueueRestoreRef = React.useRef(null);
  const [isOrderBrowserOpen, setIsOrderBrowserOpen] = React.useState(false);
  const [requireFullOrdersCatalog, setRequireFullOrdersCatalog] = React.useState(searchParams.get("orderCatalog") === "full");
  const [collapsedHistorySections, setCollapsedHistorySections] = React.useState({
    correspondences: true,
    compartments: true,
    employees: true,
    products: true,
    product_catalog: true,
    orders: true,
    vehicles: true,
  });
  const orderDataRequestOptions = React.useMemo(() => ({
    contextualDispatchDate: effectiveContextualDispatchDate,
    contextualDispatchPeriod,
    orderBillingApprovalStatus: isPendingBillingApprovalFilter ? DATA_MANAGER_ORDER_APPROVAL_VIEW_PENDING : "",
    orderBrowseDateFilter,
    orderDeliveryDateFrom: "",
    orderDeliveryDateTo: "",
    scope: orderScope,
    searchQuery: ordersSearchQuery,
  }), [
    effectiveContextualDispatchDate,
    contextualDispatchPeriod,
    isPendingBillingApprovalFilter,
    orderBrowseDateFilter,
    orderScope,
    ordersSearchQuery,
  ]);
  const dataManagerSectionRequestOptions = React.useMemo(() => ({
    includeHistory: historyView === "full",
    includePlanning: activeTab === "orders",
    lazySections: historyView !== "full" && collapsedHistorySections[activeTab] === false ? ["history"] : [],
    loadEnabled: activeTab !== "orders" || orderScope !== "dispatch" || Boolean(effectiveContextualDispatchDate),
  }), [activeTab, collapsedHistorySections, effectiveContextualDispatchDate, historyView, orderScope]);
  const {
    clearOrderDeliveredStatus,
    createClientCorrespondence,
    createCompartment,
    createEmployee,
    createOrder,
    createOrders,
    createProduct,
    createCatalogProduct,
    updateCatalogProduct,
    deleteCatalogProduct,
    createTruck,
    assignOrdersEndOfDayLoad,
    removeOrdersEndOfDayLoad,
    deleteClientCorrespondence,
    deleteCompartment,
    deleteEmployee,
    deleteOrder,
    deleteProduct,
    deleteTruck,
    retireTruck,
    downloadExport,
    ensureSectionsLoaded,
    errorMessage,
    importDispatchSaves,
    isPending,
    loadHistoryAuditDetails,
    previewDispatchSavesImport,
    refreshState,
    saveColumnPreferences: saveColumnPreferencesFn,
    state,
    status,
    updateClientCorrespondence,
    updateCompartment,
    saveCompartmentTruck,
    updateEmployee,
    updateOrder,
    updateOrderExecution,
    updateProduct,
    updateTruck,
    validateOrderAddress,
  } = useDataManagerPage(activeTab, orderDataRequestOptions, dataManagerSectionRequestOptions);
  const consolidationScope = orderNavigationScope(access?.userId, state.company_id);
  const consolidationReturn = React.useMemo(() => readOrdersReturn(consolidationScope, searchParams.get("consolidation_return")),
    [consolidationScope, searchParams.get("consolidation_return")]);
  const restoredReturn = React.useRef(null);
  const consolidationNavigation = useConsolidationOrderNavigation({ scope: consolidationScope,
    allowed: hasAccessPermission("billing.consolidation.read"), pageContext: { ordersSearchQuery, orderScope,
      orderBrowseDateFilter, isPendingBillingApprovalFilter, requireFullOrdersCatalog,
      queueRestore: orderApprovalQueueRestoreRef.current } });
  React.useEffect(() => {
    if (!consolidationReturn || restoredReturn.current === consolidationReturn.token) return;
    restoredReturn.current = consolidationReturn.token;
    const saved = consolidationReturn.page;
    setOrdersSearchQuery(saved.ordersSearchQuery);
    setOrderScope(saved.orderScope);
    setOrderBrowseDateFilter(saved.orderBrowseDateFilter);
    setIsPendingBillingApprovalFilter(saved.isPendingBillingApprovalFilter);
    setRequireFullOrdersCatalog(saved.requireFullOrdersCatalog);
    orderApprovalQueueRestoreRef.current = saved.queueRestore;
  }, [consolidationReturn]);
  React.useEffect(() => {
    const refreshDrivers = () => { void refreshState?.({ silent: true, force: true }); };
    window.addEventListener("grain-driver-assignments-changed", refreshDrivers);
    return () => window.removeEventListener("grain-driver-assignments-changed", refreshDrivers);
  }, [refreshState]);
  const canBulkDeleteOrders = canBulkDeleteOrdersForRole(access);
  const canClearDeliveredOrders = canClearDeliveredOrdersForRole(access);
  const canDeleteDeliveredOrders = canDeleteDeliveredOrdersForRole(access);
  const canMarkOrdersDelivered = canMarkOrdersDeliveredForRole(access);
  const canWriteOrderActualTonnage = canWriteOrderActualTonnageForRole(access);
  const canWriteOrderDeliveryDriver = canWriteOrderDeliveryDriverForRole(access);
  const canManageImports = canManageDataManagerImportsForRole(access);
  const canUseAdminTransferPanel = canAccessDataManagerAdminTransferForRole(access);
  const canExportTable = (kind) => canExportDataManagerTableForRole(access, kind);
  // Sans le droit de l'onglet : ni ajout, ni suppression, ni édition (le serveur refuserait).
  const activeTabWritePermission = DATA_MANAGER_TAB_WRITE_PERMISSIONS[activeTab];
  const canWriteActiveTab = !activeTabWritePermission || hasPermission(access, activeTabWritePermission);
  const loadedSections = Array.isArray(state.loaded_sections) ? state.loaded_sections : [];
  const isHistoryLoaded = loadedSections.includes("history");
  const isHistoryLoading = status === "ready"
    && (historyView === "full" || collapsedHistorySections[activeTab] === false)
    && !isHistoryLoaded;
  const clientCorrespondenceAddressFieldRefsRef = React.useRef({});
  const clientCorrespondenceAddressToastIdRef = React.useRef(null);
  const previousPhase15GridSnapshotRef = React.useRef(null);
  const [isCreatingTruck, setIsCreatingTruck] = React.useState(false);
  const [fleetFilters, setFleetFilters] = React.useState({ vehicles: "fleet", compartments: "fleet" });
  const [fleetRetirementDialog, setFleetRetirementDialog] = React.useState(null);
  const [newTrailerDraft, setNewTrailerDraft] = React.useState(null);
  const [isPreparingTruck, setIsPreparingTruck] = React.useState(false);
  const [isCreatingClientCorrespondence, setIsCreatingClientCorrespondence] = React.useState(false);
  const [isCreatingEmployee, setIsCreatingEmployee] = React.useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = React.useState(false);
  const [editingTruckId, setEditingTruckId] = React.useState(null);
  const [editingTruckFocusKey, setEditingTruckFocusKey] = React.useState(VEHICLE_DEFAULT_FOCUS_KEY);
  const [editingClientCorrespondenceId, setEditingClientCorrespondenceId] = React.useState(null);
  const [editingClientCorrespondenceFocusKey, setEditingClientCorrespondenceFocusKey] = React.useState(CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY);
  const [editingEmployeeId, setEditingEmployeeId] = React.useState(null);
  const [editingEmployeeFocusKey, setEditingEmployeeFocusKey] = React.useState(EMPLOYEE_DEFAULT_FOCUS_KEY);
  const [editingProductId, setEditingProductId] = React.useState(null);
  const [editingProductFocusKey, setEditingProductFocusKey] = React.useState(PRODUCT_DEFAULT_FOCUS_KEY);
  const [editingCompartmentTruckId, setEditingCompartmentTruckId] = React.useState(null);
  const [editingCompartmentTruckFocusKey, setEditingCompartmentTruckFocusKey] = React.useState(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY);
  const [newTruckDraft, setNewTruckDraft] = React.useState(createTruckDraft());
  const [newClientCorrespondenceDraft, setNewClientCorrespondenceDraft] = React.useState(createClientCorrespondenceDraft());
  const [newEmployeeDraft, setNewEmployeeDraft] = React.useState(createEmployeeDraft());
  const [newProductDraft, setNewProductDraft] = React.useState(createProductDraft());
  const [dispatchSavesImportDialog, setDispatchSavesImportDialog] = React.useState({
    candidates: [],
    errorMessage: "",
    isOpen: false,
    items: [],
  });
  const [truckDrafts, setTruckDrafts] = React.useState({});
  const [clientCorrespondenceDrafts, setClientCorrespondenceDrafts] = React.useState({});
  const [employeeDrafts, setEmployeeDrafts] = React.useState({});
  const [productDrafts, setProductDrafts] = React.useState({});
  const [compartmentDrafts, setCompartmentDrafts] = React.useState({});
  const [compartmentMatrixDrafts, setCompartmentMatrixDrafts] = React.useState({});
  const [newCompartmentDrafts, setNewCompartmentDrafts] = React.useState({});
  const [dayVehicleSortState, setDayVehicleSortState] = useDataManagerTableSortState("vehicles", () => ({ direction: "asc", key: null }));
  const [eveningVehicleSortState, setEveningVehicleSortState] = useDataManagerTableSortState("vehicles_evening", () => ({ direction: "asc", key: null }));
  const [vehicleSortState, setVehicleSortState] = vehiclePeriod === "evening"
    ? [eveningVehicleSortState, setEveningVehicleSortState] : [dayVehicleSortState, setDayVehicleSortState];
  const [clientCorrespondenceSortState, setClientCorrespondenceSortState] = useDataManagerTableSortState("correspondences", () => ({ direction: "asc", key: null }));
  const [employeeSortState, setEmployeeSortState] = useDataManagerTableSortState("employees", () => ({ direction: "asc", key: null }));
  const [productSortState, setProductSortState] = useDataManagerTableSortState("products", () => ({ direction: "asc", key: null }));
  const [compartmentSortState, setCompartmentSortState] = useDataManagerTableSortState("compartments", () => ({ direction: "asc", key: null }));
  const [selectedTruckIds, setSelectedTruckIds] = React.useState({});
  const [selectedClientCorrespondenceIds, setSelectedClientCorrespondenceIds] = React.useState({});
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState({});
  const [selectedProductIds, setSelectedProductIds] = React.useState({});
  const [selectedCompartmentTruckIds, setSelectedCompartmentTruckIds] = React.useState({});
  const [focusedTruckId, setFocusedTruckId] = React.useState(null);
  const [focusedClientCorrespondenceId, setFocusedClientCorrespondenceId] = React.useState(null);
  const [focusedEmployeeId, setFocusedEmployeeId] = React.useState(null);
  const [focusedProductId, setFocusedProductId] = React.useState(null);
  const [focusedCompartmentTruckId, setFocusedCompartmentTruckId] = React.useState(null);
  const [deleteSelectionDialogSection, setDeleteSelectionDialogSection] = React.useState("");
  const [clientRenameCandidate, setClientRenameCandidate] = React.useState(null);
  const [isDeletingSelection, setIsDeletingSelection] = React.useState(false);

  const savedColumnPrefs = state?.column_preferences && typeof state.column_preferences === "object"
    ? state.column_preferences
    : {};

  const dayVehicleColumns = React.useMemo(
    () => withoutUnauthorizedColumns(truckColumnsForPeriod(VEHICLE_GRID_COLUMNS, "day"), access),
    [access],
  );
  const eveningVehicleColumns = React.useMemo(
    () => withoutUnauthorizedColumns(truckColumnsForPeriod(VEHICLE_GRID_COLUMNS, "evening"), access),
    [access],
  );
  const dayVehicleColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: dayVehicleColumns,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs["vehicles"] ?? null,
    tableKey: "vehicles",
  });
  const eveningVehicleColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: eveningVehicleColumns,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs["vehicles_evening"] ?? null,
    tableKey: "vehicles_evening",
  });
  const vehicleColumnPrefs = vehiclePeriod === "evening" ? eveningVehicleColumnPrefs : dayVehicleColumnPrefs;
  const vehicleSelectionColumnWidth = vehiclePeriod === "evening" ? 0 : resolveSelectionColumnWidth(vehicleColumnPrefs);
  const vehicleSelectionColumnStyle = React.useMemo(
    () => buildDataManagerGridSelectionColumnStyle(resolveSelectionColumnWidth(vehicleColumnPrefs)),
    [vehicleColumnPrefs.resolvedFixedColumnWidths],
  );
  const vehicleTableWidth = React.useMemo(
    () => getVehicleGridTableWidth(
      vehicleColumnPrefs.resolvedColumns,
      vehicleSelectionColumnWidth,
    ),
    [vehicleColumnPrefs.resolvedColumns, vehicleSelectionColumnWidth],
  );
  const vehicleTableStyle = React.useMemo(
    () => buildDataManagerGridTableStyle(vehicleTableWidth),
    [vehicleTableWidth],
  );

  const billToCompanyNames = React.useMemo(
    () => buildCoopClientNames(state.client_correspondences),
    [state.client_correspondences],
  );
  const clientCorrespondenceBaseColumns = React.useMemo(
    () => withoutUnauthorizedColumns(CLIENT_CORRESPONDENCE_GRID_COLUMNS, access),
    [access],
  );
  const clientCorrespondenceColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: clientCorrespondenceBaseColumns,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs["correspondences"] ?? null,
    tableKey: "correspondences",
  });
  const clientCorrespondenceSelectionColumnStyle = React.useMemo(
    () => buildDataManagerGridSelectionColumnStyle(resolveSelectionColumnWidth(clientCorrespondenceColumnPrefs)),
    [clientCorrespondenceColumnPrefs.resolvedFixedColumnWidths],
  );
  const clientCorrespondenceTableWidth = React.useMemo(
    () => getDataManagerGridTableWidth(
      clientCorrespondenceColumnPrefs.resolvedColumns,
      [
        resolveSelectionColumnWidth(clientCorrespondenceColumnPrefs),
        DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH,
      ],
    ),
    [clientCorrespondenceColumnPrefs.resolvedColumns, clientCorrespondenceColumnPrefs.resolvedFixedColumnWidths],
  );
  const clientCorrespondenceTableStyle = React.useMemo(
    () => buildDataManagerGridTableStyle(clientCorrespondenceTableWidth),
    [clientCorrespondenceTableWidth],
  );

  const employeeColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: EMPLOYEE_GRID_COLUMNS,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs[DRIVERS_TAB_KEY] ?? null,
    tableKey: DRIVERS_TAB_KEY,
  });
  const employeeSelectionColumnStyle = React.useMemo(
    () => buildDataManagerGridSelectionColumnStyle(resolveSelectionColumnWidth(employeeColumnPrefs)),
    [employeeColumnPrefs.resolvedFixedColumnWidths],
  );
  const employeeTableWidth = React.useMemo(
    () => getDataManagerGridTableWidth(
      employeeColumnPrefs.resolvedColumns,
      [
        resolveSelectionColumnWidth(employeeColumnPrefs),
        DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH,
      ],
    ),
    [employeeColumnPrefs.resolvedColumns, employeeColumnPrefs.resolvedFixedColumnWidths],
  );
  const employeeTableStyle = React.useMemo(
    () => buildDataManagerGridTableStyle(employeeTableWidth),
    [employeeTableWidth],
  );

  const productColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: PRODUCT_GRID_COLUMNS,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs["products"] ?? null,
    tableKey: "products",
  });
  const productSelectionColumnStyle = React.useMemo(
    () => buildDataManagerGridSelectionColumnStyle(resolveSelectionColumnWidth(productColumnPrefs)),
    [productColumnPrefs.resolvedFixedColumnWidths],
  );
  const productTableWidth = React.useMemo(
    () => getDataManagerGridTableWidth(
      productColumnPrefs.resolvedColumns,
      [
        resolveSelectionColumnWidth(productColumnPrefs),
        DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH,
      ],
    ),
    [productColumnPrefs.resolvedColumns, productColumnPrefs.resolvedFixedColumnWidths],
  );
  const productTableStyle = React.useMemo(
    () => buildDataManagerGridTableStyle(productTableWidth),
    [productTableWidth],
  );

  const compartmentBaseColumns = React.useMemo(
    () => withoutUnauthorizedColumns(COMPARTMENT_MATRIX_BASE_COLUMNS, access),
    [access],
  );
  const compartmentColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: compartmentBaseColumns,
    onSaveError: handleColumnPreferencesSaveError,
    resizableFixedColumns: DATA_MANAGER_RESIZABLE_FIXED_COLUMNS,
    saveColumnPreferences: saveColumnPreferencesFn,
    savedPrefs: savedColumnPrefs["compartments"] ?? null,
    tableKey: "compartments",
  });
  const compartmentSelectionColumnStyle = React.useMemo(
    () => buildDataManagerGridSelectionColumnStyle(resolveSelectionColumnWidth(compartmentColumnPrefs)),
    [compartmentColumnPrefs.resolvedFixedColumnWidths],
  );

  const dismissClientCorrespondenceAddressValidationToast = React.useCallback(() => {
    const toastId = clientCorrespondenceAddressToastIdRef.current;
    if (!toastId) {
      return;
    }

    dismissToast(toastId);
    clientCorrespondenceAddressToastIdRef.current = null;
  }, [dismissToast]);

  const registerClientCorrespondenceAddressValidationFieldRef = React.useCallback((validationKey, node) => {
    if (!validationKey) {
      return;
    }

    if (node) {
      clientCorrespondenceAddressFieldRefsRef.current[validationKey] = node;
      return;
    }

    delete clientCorrespondenceAddressFieldRefsRef.current[validationKey];
  }, []);

  const showClientCorrespondenceAddressValidationToast = React.useCallback((toastPayload, validationKey) => {
    dismissClientCorrespondenceAddressValidationToast();

    const anchorElement = validationKey ? clientCorrespondenceAddressFieldRefsRef.current[validationKey] : null;
    const positionStyle = resolveAddressValidationToastPositionStyle(anchorElement);
    clientCorrespondenceAddressToastIdRef.current = showToast({
      ...toastPayload,
      ...(positionStyle ? { positionStyle } : {}),
    });
  }, [dismissClientCorrespondenceAddressValidationToast, showToast]);

  const focusClientCorrespondenceValidationField = React.useCallback((validationKey) => {
    const field = validationKey ? clientCorrespondenceAddressFieldRefsRef.current[validationKey] : null;
    if (!field || typeof field.focus !== "function") {
      return;
    }

    field.focus({ preventScroll: true });
    if (typeof field.select === "function") {
      field.select();
    }
  }, []);

  React.useEffect(() => () => {
    dismissClientCorrespondenceAddressValidationToast();
  }, [dismissClientCorrespondenceAddressValidationToast]);

  React.useEffect(() => {
    if (normalizedContextualDispatchDate || !defaultDataManagerDispatchDate) {
      return;
    }

    const resolvedDispatchMonth = defaultDataManagerDispatchDate.slice(0, 7);
    writeLastDispatchContext({
      dispatch_date: defaultDataManagerDispatchDate,
      dispatch_month: resolvedDispatchMonth,
      dispatch_period: contextualDispatchPeriod,
    });
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("dispatch_date", defaultDataManagerDispatchDate);
    nextSearchParams.set("dispatch_month", resolvedDispatchMonth);
    setSearchParams(nextSearchParams, { replace: true });
  }, [
    contextualDispatchPeriod,
    defaultDataManagerDispatchDate,
    normalizedContextualDispatchDate,
    searchParams,
    setSearchParams,
  ]);

  React.useEffect(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const requestedTab = String(nextSearchParams.get("tab") || "").trim().toLowerCase();
    let shouldReplace = false;

    if (!requestedTab) {
      nextSearchParams.set("tab", selectedTabKey);
      shouldReplace = true;
    }

    if (shouldReplace) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [searchParams, selectedTabKey, setSearchParams]);

  const validateClientCorrespondenceAddressValue = React.useCallback(async ({
    validationKey,
    value,
    onResolvedValue,
    showSuccessToast = false,
    allowMissingCivicNumber = false,
  }) => {
    const normalizedValue = normalizeOptionalString(value);
    let hasShownValidationToast = false;

    try {
      if (typeof validateOrderAddress !== "function") {
        return normalizedValue || "";
      }

      const validationResult = await validateOrderAddress({
        address: normalizedValue,
        allow_missing_civic_number: allowMissingCivicNumber,
      });
      const failureMessage = resolveAddressValidationFailureMessage(CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL, validationResult);
      if (failureMessage) {
        showClientCorrespondenceAddressValidationToast({
          ...buildAddressValidationToast({
            fieldLabel: CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL,
            inputAddress: normalizedValue,
            resolvedAddress: validationResult?.resolved_address,
            validationResult,
          }),
          duration: 12000,
          emphasis: "high",
        }, validationKey);
        hasShownValidationToast = true;
        throw new Error(failureMessage);
      }

      const resolvedAddress = normalizeOptionalString(validationResult?.resolved_address);
      const resolvedValue = resolvedAddress || normalizedValue || "";
      if (showSuccessToast) {
        showClientCorrespondenceAddressValidationToast({
          ...buildAddressValidationToast({
            fieldLabel: CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL,
            inputAddress: normalizedValue,
            resolvedAddress,
            validationResult,
          }),
          duration: 9000,
          emphasis: "high",
        }, validationKey);
        hasShownValidationToast = true;
      }
      onResolvedValue?.(resolvedValue);
      return resolvedValue;
    } catch (error) {
      if (isMissingCivicNumberConfirmationCancelledError(error)) {
        throw error;
      }

      const failureMessage = error instanceof Error && error.message
        ? error.message
        : `${CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL} invalide ou introuvable.`;
      if (!hasShownValidationToast) {
        showClientCorrespondenceAddressValidationToast({
          type: "error",
          title: `${CLIENT_CORRESPONDENCE_ADDRESS_FIELD_LABEL} à vérifier`,
          message: `Saisie: ${normalizedValue || "Adresse non fournie."}. ${failureMessage}`,
          duration: 12000,
          emphasis: "high",
        }, validationKey);
      }
      throw error;
    }
  }, [showClientCorrespondenceAddressValidationToast, validateOrderAddress]);

  const validateClientCorrespondenceAddressWithCivicCheck = React.useCallback(async (params) => {
    const normalizedValue = normalizeOptionalString(params.value);
    const confirmed = await requestMissingCivicNumberConfirmation(normalizedValue);
    if (!confirmed) {
      throw new MissingCivicNumberConfirmationCancelledError();
    }

    return validateClientCorrespondenceAddressValue({
      ...params,
      allowMissingCivicNumber: !addressHasCivicNumber(normalizedValue) && confirmed,
    });
  }, [requestMissingCivicNumberConfirmation, validateClientCorrespondenceAddressValue]);

  const assertClientCorrespondenceClientCodeIsRequired = React.useCallback(({ clientCode, validationKey }) => {
    if (!isMissingClientCorrespondenceClientCode(clientCode)) {
      return;
    }

    showClientCorrespondenceAddressValidationToast({
      type: "error",
      title: `${CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_LABEL} requis`,
      message: "Saisissez un numéro de client avant d’enregistrer.",
      duration: 12000,
      emphasis: "high",
    }, validationKey);
    focusClientCorrespondenceValidationField(validationKey);
    throw new Error(`${CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_LABEL} requis`);
  }, [focusClientCorrespondenceValidationField, showClientCorrespondenceAddressValidationToast]);

  const assertClientCorrespondenceClientCodeIsUnique = React.useCallback(({ clientCode, excludeCorrespondenceId = null, validationKey }) => {
    const clientCorrespondences = Array.isArray(state.client_correspondences) ? state.client_correspondences : [];
    const conflict = findClientCorrespondenceClientCodeConflict(clientCorrespondences, clientCode, excludeCorrespondenceId);
    if (!conflict) {
      return;
    }

    const normalizedClientCode = normalizeOptionalString(clientCode) || "";
    const conflictLabel = resolveClientCorrespondenceClientCodeConflictLabel(conflict);
    showClientCorrespondenceAddressValidationToast({
      type: "error",
      title: `${CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_LABEL} déjà utilisé`,
      message: `Le numéro « ${normalizedClientCode} » est déjà assigné au client « ${conflictLabel} ».`,
      duration: 12000,
      emphasis: "high",
    }, validationKey);
    throw new Error(`Numéro de client déjà utilisé: ${normalizedClientCode}`);
  }, [showClientCorrespondenceAddressValidationToast, state.client_correspondences]);

  React.useEffect(() => {
    setClientCorrespondenceDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([correspondenceId]) => state.client_correspondences.some((entry) => entry.correspondence_id === correspondenceId))
    ));
    setEmployeeDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([employeeId]) => state.employees.some((entry) => entry.employee_id === employeeId))
    ));
    setProductDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([productId]) => state.products.some((entry) => entry.product_id === productId))
    ));
    setTruckDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([truckId]) => state.trucks.some((truck) => truck.truck_id === truckId))
    ));
    setCompartmentDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([draftKey]) => {
        const [truckId, compartmentId] = draftKey.split(":");
        return state.trucks.some((truck) => truck.truck_id === truckId && truck.compartments.some((compartment) => compartment.compartment_id === compartmentId));
      })
    ));
    setCompartmentMatrixDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([truckId]) => state.trucks.some((truck) => truck.truck_id === truckId))
    ));
  }, [state.client_correspondences, state.employees, state.products, state.trucks]);

  React.useEffect(() => {
    if (editingClientCorrespondenceId && !state.client_correspondences.some((entry) => entry.correspondence_id === editingClientCorrespondenceId)) {
      dismissClientCorrespondenceAddressValidationToast();
      setEditingClientCorrespondenceId(null);
      setEditingClientCorrespondenceFocusKey(CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY);
    }
  }, [dismissClientCorrespondenceAddressValidationToast, editingClientCorrespondenceId, state.client_correspondences]);

  React.useEffect(() => {
    if (editingEmployeeId && !state.employees.some((entry) => entry.employee_id === editingEmployeeId)) {
      setEditingEmployeeId(null);
      setEditingEmployeeFocusKey(EMPLOYEE_DEFAULT_FOCUS_KEY);
    }
  }, [editingEmployeeId, state.employees]);

  React.useEffect(() => {
    if (editingProductId && !state.products.some((entry) => entry.product_id === editingProductId)) {
      setEditingProductId(null);
      setEditingProductFocusKey(PRODUCT_DEFAULT_FOCUS_KEY);
    }
  }, [editingProductId, state.products]);

  React.useEffect(() => {
    if (editingTruckId && !state.trucks.some((truck) => truck.truck_id === editingTruckId)) {
      setEditingTruckId(null);
      setEditingTruckFocusKey(VEHICLE_DEFAULT_FOCUS_KEY);
    }
  }, [editingTruckId, state.trucks]);

  React.useEffect(() => {
    if (editingCompartmentTruckId && !state.trucks.some((truck) => truck.truck_id === editingCompartmentTruckId)) {
      setEditingCompartmentTruckId(null);
      setEditingCompartmentTruckFocusKey(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY);
    }
  }, [editingCompartmentTruckId, state.trucks]);

  React.useEffect(() => {
    if (!editingTruckId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingTruckRow = (event) => {
      const editingRow = document.querySelector('tr[data-truck-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target) || event.target.closest?.("[data-truck-period-controls]")) {
        return;
      }

      // « Changer le type ? » : « Annuler » garde la ligne en modification.
      if (isPointerInsideConfirmationModal(event.target)) {
        return;
      }

      handleCancelTruckEdit(editingTruckId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingTruckRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingTruckRow, true);
  }, [editingTruckId]);

  React.useEffect(() => {
    if (!editingClientCorrespondenceId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingClientCorrespondenceRow = (event) => {
      const editingRow = document.querySelector('tr[data-client-correspondence-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target)) {
        return;
      }

      if (isPointerInsideConfirmationModal(event.target)) {
        return;
      }

      handleCancelClientCorrespondenceEdit(editingClientCorrespondenceId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingClientCorrespondenceRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingClientCorrespondenceRow, true);
  }, [editingClientCorrespondenceId]);

  React.useEffect(() => {
    if (!editingEmployeeId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingEmployeeRow = (event) => {
      const editingRow = document.querySelector('tr[data-employee-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target)) {
        return;
      }

      handleCancelEmployeeEdit(editingEmployeeId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingEmployeeRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingEmployeeRow, true);
  }, [editingEmployeeId]);

  React.useEffect(() => {
    if (!editingProductId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingProductRow = (event) => {
      const editingRow = document.querySelector('tr[data-product-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target)) {
        return;
      }

      handleCancelProductEdit(editingProductId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingProductRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingProductRow, true);
  }, [editingProductId]);

  React.useEffect(() => {
    if (!editingCompartmentTruckId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingCompartmentRow = (event) => {
      const editingRow = document.querySelector('tr[data-compartment-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target)) {
        return;
      }

      handleCancelCompartmentTruckEdit(editingCompartmentTruckId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingCompartmentRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingCompartmentRow, true);
  }, [editingCompartmentTruckId]);

  React.useEffect(() => {
    const nextOrdersSearchQuery = requestedOrdersSearchQuery;
    const nextOrderBrowseDateFilter = requestedOrderBrowseDateFilter;
    const nextOrderScope = requestedOrderScope || (nextOrdersSearchQuery || nextOrderBrowseDateFilter ? "browse" : "dispatch");

    setOrdersSearchQuery((currentValue) => (currentValue === nextOrdersSearchQuery ? currentValue : nextOrdersSearchQuery));
    setOrderBrowseDateFilter((currentValue) => (currentValue === nextOrderBrowseDateFilter ? currentValue : nextOrderBrowseDateFilter));
    setOrderScope((currentValue) => (currentValue === nextOrderScope ? currentValue : nextOrderScope));
  }, [requestedOrderBrowseDateFilter, requestedOrderScope, requestedOrdersSearchQuery]);

  const orders = Array.isArray(state.orders) ? state.orders : [];
  const currentDispatchOrderIds = Array.isArray(state.planning?.current_order_ids)
    ? state.planning.current_order_ids.filter((value) => typeof value === "string" && value.trim())
    : [];
  const currentDispatchDates = Array.isArray(state.planning?.current_dispatch_dates)
    ? state.planning.current_dispatch_dates.filter((value) => typeof value === "string" && value.trim())
    : [];
  const resolvedContextualDispatchDate = effectiveContextualDispatchDate;
  const currentDispatchOrderIdSet = new Set(currentDispatchOrderIds);
  const currentDispatchDateSet = new Set(currentDispatchDates);
  const dispatchScopedOrders = orders.filter((order) => {
    if (currentDispatchOrderIdSet.size > 0) {
      return currentDispatchOrderIdSet.has(order?.order_id);
    }

    if (currentDispatchDateSet.size > 0) {
      return currentDispatchDateSet.has(resolveOrderDateFilterValue(order?.delivery_date));
    }

    return true;
  });
  const shouldScopeDispatchToContextualDate = Boolean(resolvedContextualDispatchDate);
  const effectiveDispatchDates = shouldScopeDispatchToContextualDate
    ? [resolvedContextualDispatchDate]
    : currentDispatchDates;
  const dispatchDisplayOrders = shouldScopeDispatchToContextualDate
    ? orders.filter((order) => resolveOrderDateFilterValue(order?.delivery_date) === resolvedContextualDispatchDate)
    : dispatchScopedOrders;
  const orderScopeBaseOrders = orderScope === "browse"
    ? orders.filter((order) => !orderBrowseDateFilter || resolveOrderDateFilterValue(order?.delivery_date) === orderBrowseDateFilter)
    : dispatchDisplayOrders;
  const normalizedDeferredOrdersSearchQuery = resolveActiveOrderSearchQuery(deferredOrdersSearchQuery);
  const orderSearchBaseOrders = orderScopeBaseOrders;
  const searchedOrders = orderSearchBaseOrders.filter((order) => matchOrderSearch(order, deferredOrdersSearchQuery));
  const isRecentBrowseWithoutDate = orderScope === "browse" && !orderBrowseDateFilter;
  const filteredOrders = isRecentBrowseWithoutDate && !normalizedDeferredOrdersSearchQuery && !requireFullOrdersCatalog
    ? searchedOrders.slice(0, DATA_MANAGER_RECENT_BROWSE_ORDERS_LIMIT)
    : searchedOrders;
  const activeHistorySearchQuery = activeTab === "orders" ? ordersSearchQuery : searchQuery;
  const clientCorrespondences = Array.isArray(state.client_correspondences) ? state.client_correspondences : [];
  const searchedClientCorrespondences = clientCorrespondences.filter((entry) => buildSearchHaystack(entry).includes(deferredSearchQuery.trim().toLowerCase()));
  const clientTypeCounts = countClientTypes(searchedClientCorrespondences);
  const filteredClientCorrespondences = searchedClientCorrespondences.filter((entry) => matchesClientType(entry, clientTypeFilter));
  const employees = Array.isArray(state.employees) ? state.employees : [];
  const filteredEmployees = employees.filter((entry) => buildSearchHaystack({
    ...entry,
    shift_label: formatEmployeeShiftLabel(entry?.shift),
  }).includes(deferredSearchQuery.trim().toLowerCase()));
  const products = Array.isArray(state.products) ? state.products : [];
  const filteredProducts = products.filter((entry) => buildSearchHaystack(entry).includes(deferredSearchQuery.trim().toLowerCase()));

  // Chaque onglet a ses genres et son filtre « Dans la flotte / Retirés » : filteredTrucks (Remorques), visibleTrucks (Camions).
  // Les tags et le code TRK ne sont plus affichés : la recherche et le tri les ignorent (un tri enregistré sur le code aussi).
  const searchedTrucks = state.trucks.filter((truck) => buildSearchHaystack({ ...truck, tags: undefined, truck_id: undefined }).includes(deferredSearchQuery.trim().toLowerCase()));
  const fleetToday = localDateKey();
  const filteredTrucks = trucksForTab(searchedTrucks, "compartments", fleetFilters.compartments, fleetToday);
  const visibleTrucks = trucksForTab(searchedTrucks, "vehicles", fleetFilters.vehicles, fleetToday);
  const withoutTruckIdSort = (sortState) => (sortState?.key === "truck_id" ? null : sortState);
  const sortedTrucks = React.useMemo(
    () => sortDataManagerRows(visibleTrucks, withoutTruckIdSort(vehicleSortState), resolveTruckSortValue),
    [visibleTrucks, vehicleSortState, vehiclePeriod],
  );
  const sortedClientCorrespondences = React.useMemo(
    () => sortDataManagerRows(filteredClientCorrespondences, clientCorrespondenceSortState, resolveClientCorrespondenceSortValue),
    [clientCorrespondenceSortState, filteredClientCorrespondences],
  );
  const sortedEmployees = React.useMemo(
    () => sortDataManagerRows(filteredEmployees, employeeSortState, resolveEmployeeSortValue),
    [employeeSortState, filteredEmployees],
  );
  const sortedProducts = React.useMemo(
    () => sortDataManagerRows(filteredProducts, productSortState, resolveProductSortValue),
    [filteredProducts, productSortState],
  );
  const sortedCompartmentTrucks = React.useMemo(
    () => sortDataManagerRows(filteredTrucks, withoutTruckIdSort(compartmentSortState), resolveCompartmentTruckSortValue),
    [compartmentSortState, filteredTrucks],
  );
  const vehicleScrollRef = useDataManagerTableScrollRestore(vehiclePeriod === "evening" ? "vehicles_evening" : "vehicles", sortedTrucks.length);
  const clientCorrespondenceScrollRef = useDataManagerTableScrollRestore("correspondences", sortedClientCorrespondences.length);
  const employeeScrollRef = useDataManagerTableScrollRestore("employees", sortedEmployees.length);
  const productScrollRef = useDataManagerTableScrollRestore("products", sortedProducts.length);
  const compartmentScrollRef = useDataManagerTableScrollRestore("compartments", sortedCompartmentTrucks.length);
  const deletableVisibleTrucks = React.useMemo(
    () => sortedTrucks.filter((truck) => !truck.has_historical_assignments),
    [sortedTrucks],
  );
  const selectedVisibleTrucks = React.useMemo(
    () => deletableVisibleTrucks.filter((truck) => selectedTruckIds[truck.truck_id]),
    [deletableVisibleTrucks, selectedTruckIds],
  );
  const allVisibleTrucksSelected = deletableVisibleTrucks.length > 0
    && selectedVisibleTrucks.length === deletableVisibleTrucks.length;
  const deletableVisibleClientCorrespondences = sortedClientCorrespondences;
  const selectedVisibleClientCorrespondences = React.useMemo(
    () => deletableVisibleClientCorrespondences.filter((entry) => selectedClientCorrespondenceIds[entry.correspondence_id]),
    [deletableVisibleClientCorrespondences, selectedClientCorrespondenceIds],
  );
  const allVisibleClientCorrespondencesSelected = deletableVisibleClientCorrespondences.length > 0
    && selectedVisibleClientCorrespondences.length === deletableVisibleClientCorrespondences.length;
  const deletableVisibleEmployees = sortedEmployees;
  const selectedVisibleEmployees = React.useMemo(
    () => deletableVisibleEmployees.filter((entry) => selectedEmployeeIds[entry.employee_id]),
    [deletableVisibleEmployees, selectedEmployeeIds],
  );
  const allVisibleEmployeesSelected = deletableVisibleEmployees.length > 0
    && selectedVisibleEmployees.length === deletableVisibleEmployees.length;
  const deletableVisibleProducts = sortedProducts;
  const selectedVisibleProducts = React.useMemo(
    () => deletableVisibleProducts.filter((entry) => selectedProductIds[entry.product_id]),
    [deletableVisibleProducts, selectedProductIds],
  );
  const allVisibleProductsSelected = deletableVisibleProducts.length > 0
    && selectedVisibleProducts.length === deletableVisibleProducts.length;
  // Une remorque seule se supprime même sans compartiment ; une autre remorque, seulement si elle en a.
  const deletableVisibleCompartmentTrucks = React.useMemo(
    () => sortedCompartmentTrucks.filter((truck) => truckUnitKind(truck) === "remorque" || (Array.isArray(truck?.compartments) && truck.compartments.length > 0)),
    [sortedCompartmentTrucks],
  );
  const selectedVisibleCompartmentTrucks = React.useMemo(
    () => deletableVisibleCompartmentTrucks.filter((truck) => selectedCompartmentTruckIds[truck.truck_id]),
    [deletableVisibleCompartmentTrucks, selectedCompartmentTruckIds],
  );
  const selectedTrailerDeletion = React.useMemo(
    () => trailerSelectionDeletion(selectedVisibleCompartmentTrucks),
    [selectedVisibleCompartmentTrucks],
  );
  const selectedTruckDeletion = React.useMemo(() => truckSelectionDeletion(selectedVisibleTrucks), [selectedVisibleTrucks]);
  const selectedVisibleCompartmentCount = React.useMemo(
    () => selectedVisibleCompartmentTrucks.reduce(
      (totalCount, truck) => totalCount + (Array.isArray(truck?.compartments) ? truck.compartments.length : 0),
      0,
    ),
    [selectedVisibleCompartmentTrucks],
  );
  const allVisibleCompartmentTrucksSelected = deletableVisibleCompartmentTrucks.length > 0
    && selectedVisibleCompartmentTrucks.length === deletableVisibleCompartmentTrucks.length;

  const vehicleSummary = {
    active: state.trucks.filter((truck) => truckIsActiveForView(truck, vehiclePeriod)).length,
    historical: state.trucks.filter((truck) => truck.has_historical_assignments).length,
    total: state.trucks.length,
  };
  const compartmentSummary = {
    total: state.trucks.reduce(
      (totalCount, truck) => totalCount + (Array.isArray(truck?.compartments) ? truck.compartments.length : 0),
      0,
    ),
    trucks: state.trucks.filter((truck) => truck.compartments.length > 0).length,
  };
  const correspondenceSummary = {
    total: clientCorrespondences.length,
  };
  const employeeSummary = {
    total: employees.length,
  };
  const productSummary = {
    total: products.length,
  };
  const draftedCorrespondenceCount = Object.keys(clientCorrespondenceDrafts).length;
  const draftedEmployeeCount = Object.keys(employeeDrafts).length;
  const draftedProductCount = Object.keys(productDrafts).length;
  const draftedVehicleCount = Object.keys(truckDrafts).length;
  const draftedCompartmentTruckCount = Object.keys(compartmentMatrixDrafts).length;
  const maxCompartmentPosition = Math.max(
    12,
    ...state.trucks.flatMap((truck) => truck.compartments.map((compartment) => Number(compartment.position) || 0))
  );
  const compartmentMatrixPositions = Array.from({ length: maxCompartmentPosition }, (_, index) => index + 1);
  const compartmentPositionColumns = React.useMemo(() => Array.from(
    { length: maxCompartmentPosition }, (_, index) => ({
      key: `position:${index + 1}`, label: String(index + 1), position: index + 1,
      width: DATA_MANAGER_GRID_COMPARTMENT_POSITION_COLUMN_WIDTH, fixed: true,
    }),
  ), [maxCompartmentPosition]);
  const vehicleFrozenPanes = useDataManagerGridFrozenPanes(vehicleColumnPrefs, {
    leadingColumns: VEHICLE_GRID_LEADING_COLUMNS,
    selectionColumnWidth: vehicleSelectionColumnWidth,
    active: activeTab === "vehicles" && historyView !== "full",
  });
  const clientCorrespondenceFrozenPanes = useDataManagerGridFrozenPanes(clientCorrespondenceColumnPrefs, {
    selectionColumnWidth: resolveSelectionColumnWidth(clientCorrespondenceColumnPrefs),
    active: activeTab === "correspondences" && historyView !== "full",
  });
  const employeeFrozenPanes = useDataManagerGridFrozenPanes(employeeColumnPrefs, {
    selectionColumnWidth: resolveSelectionColumnWidth(employeeColumnPrefs),
    active: activeTab === "employees" && historyView !== "full",
  });
  const productFrozenPanes = useDataManagerGridFrozenPanes(productColumnPrefs, {
    selectionColumnWidth: resolveSelectionColumnWidth(productColumnPrefs),
    active: activeTab === "products" && historyView !== "full",
  });
  const compartmentFrozenPanes = useDataManagerGridFrozenPanes(compartmentColumnPrefs, {
    trailingColumns: compartmentPositionColumns,
    selectionColumnWidth: resolveSelectionColumnWidth(compartmentColumnPrefs),
    active: activeTab === "compartments" && historyView !== "full",
  });

  const compartmentTableWidth = React.useMemo(
    () => getDataManagerGridTableWidth(
      compartmentColumnPrefs.resolvedColumns,
      [
        resolveSelectionColumnWidth(compartmentColumnPrefs),
        compartmentMatrixPositions.length * DATA_MANAGER_GRID_COMPARTMENT_POSITION_COLUMN_WIDTH,
        DATA_MANAGER_GRID_ACTIONS_COLUMN_WIDTH,
      ],
    ),
    [compartmentColumnPrefs.resolvedColumns, compartmentColumnPrefs.resolvedFixedColumnWidths, compartmentMatrixPositions.length],
  );
  const compartmentTableStyle = React.useMemo(
    () => buildDataManagerGridTableStyle(compartmentTableWidth),
    [compartmentTableWidth],
  );
  const firstAvailableCompartmentAddTarget = React.useMemo(() => {
    for (const truck of sortedCompartmentTrucks) {
      const firstAvailablePosition = resolveFirstAvailableCompartmentPosition(truck, compartmentMatrixPositions);
      if (firstAvailablePosition !== null) {
        return {
          focusKey: `position:${firstAvailablePosition}`,
          truck,
        };
      }
    }

    return null;
  }, [compartmentMatrixPositions, sortedCompartmentTrucks]);
  const searchPlaceholder = activeTab === "product_catalog" ? "Rechercher un produit..." : activeTab === "vehicles"
    ? "Rechercher camion..."
    : activeTab === "employees"
      ? "Rechercher chauffeur, courriel ou quart..."
      : activeTab === "products"
        ? "Rechercher nom, correspondance, no article, produit ou densité..."
    : activeTab === "compartments"
      ? "Rechercher remorque ou compartiment..."
      : "Rechercher client, source Sollio ou adresse...";
  // « Retirer de la flotte » / « Remettre » : une seule ligne sélectionnée de Camions ou de Remorques, même si elle a roulé.
  const retirementSelection = activeTab === "vehicles"
    ? sortedTrucks.filter((truck) => selectedTruckIds[truck.truck_id])
    : activeTab === "compartments" ? sortedCompartmentTrucks.filter((truck) => selectedCompartmentTruckIds[truck.truck_id]) : [];
  const retirementCandidate = retirementSelection.length === 1 ? retirementSelection[0] : null;
  const activeSelectionCount = activeTab === "vehicles"
    ? selectedVisibleTrucks.length
    : activeTab === "compartments"
      ? selectedVisibleCompartmentTrucks.length
      : activeTab === "correspondences"
        ? selectedVisibleClientCorrespondences.length
        : activeTab === "employees"
          ? selectedVisibleEmployees.length
          : activeTab === "products"
            ? selectedVisibleProducts.length
        : 0;
  const activeSelectionSummaryLabel = activeTab === "vehicles"
    ? `${selectedVisibleTrucks.length} camion(s) sélectionné(s)`
    : activeTab === "compartments"
      ? `${selectedVisibleCompartmentTrucks.length} remorque(s) sélectionnée(s) · ${selectedVisibleCompartmentCount} compartiment(s)`
      : activeTab === "employees"
        ? `${selectedVisibleEmployees.length} chauffeur(s) sélectionné(s)`
        : activeTab === "products"
          ? `${selectedVisibleProducts.length} code(s) produit sélectionné(s)`
        : `${selectedVisibleClientCorrespondences.length} client(s) sélectionné(s)`;
  const phase15GridSnapshot = React.useMemo(() => ({
    activeSelectionCount,
    activeSelectionSummaryLabel,
    activeTab,
    compartmentTruckDraftCount: draftedCompartmentTruckCount,
    compartmentTrucksVisible: sortedCompartmentTrucks.length,
    correspondenceDraftCount: draftedCorrespondenceCount,
    correspondencesVisible: sortedClientCorrespondences.length,
    employeeDraftCount: draftedEmployeeCount,
    employeesVisible: sortedEmployees.length,
    editingCompartmentTruckId,
    editingClientCorrespondenceId,
    editingEmployeeId,
    editingTruckId,
    isCreatingClientCorrespondence,
    isCreatingEmployee,
    isCreatingTruck,
    isDeletingSelection,
    isOrderBrowserOpen,
    orderScope,
    ordersVisible: filteredOrders.length,
    status,
    truckDraftCount: draftedVehicleCount,
    trucksVisible: sortedTrucks.length,
  }), [
    activeSelectionCount,
    activeSelectionSummaryLabel,
    activeTab,
    draftedCompartmentTruckCount,
    draftedCorrespondenceCount,
    draftedEmployeeCount,
    draftedVehicleCount,
    editingCompartmentTruckId,
    editingClientCorrespondenceId,
    editingEmployeeId,
    editingTruckId,
    filteredOrders.length,
    isCreatingClientCorrespondence,
    isCreatingEmployee,
    isCreatingTruck,
    isDeletingSelection,
    isOrderBrowserOpen,
    orderScope,
    sortedClientCorrespondences.length,
    sortedCompartmentTrucks.length,
    sortedEmployees.length,
    sortedTrucks.length,
    status,
  ]);

  React.useEffect(() => {
    const previousSnapshot = previousPhase15GridSnapshotRef.current;
    previousPhase15GridSnapshotRef.current = phase15GridSnapshot;
    logPhase15DataManagerGridSnapshot({
      next: phase15GridSnapshot,
      previous: previousSnapshot,
    });
  }, [phase15GridSnapshot]);
  const deleteSelectionDialogModel = deleteSelectionDialogSection === "vehicles"
    ? {
      description: selectedTruckDeletion.description,
      impactItems: [
        ...selectedTruckDeletion.items,
        "Les lignes sélectionnées seront retirées de la grille active.",
        "Le dispatch devra être revalidé si un planning était déjà chargé.",
      ],
      summaryItems: [
        { label: "Camions ciblés", value: String(selectedVisibleTrucks.length) },
      ],
      title: selectedTruckDeletion.title,
      warningMessage: "Les camions supprimés devront être recréés ou réimportés pour revenir dans la source métier.",
    }
    : deleteSelectionDialogSection === "correspondences"
      ? {
        description: "Cette action supprime définitivement les clients sélectionnés du Data Manager actif.",
        impactItems: [
          "Les lignes sélectionnées seront retirées de la table active.",
          "Les prochains imports et conversions n’utiliseront plus ces clients.",
        ],
        summaryItems: [
          { label: "Clients ciblés", value: String(selectedVisibleClientCorrespondences.length) },
        ],
        title: selectedVisibleClientCorrespondences.length > 1 ? "Supprimer la sélection de clients ?" : "Supprimer ce client ?",
        warningMessage: "Les clients supprimés devront être recréés ou réimportés pour revenir dans la source métier.",
      }
      : deleteSelectionDialogSection === "employees"
        ? {
          description: "Cette action supprime définitivement les chauffeurs sélectionnés du Data Manager actif.",
          impactItems: [
            "Les lignes sélectionnées seront retirées de la table active.",
            "Le Dispatch et les rapports de préchargement ne pourront plus proposer ces chauffeurs.",
          ],
          summaryItems: [
            { label: "Chauffeurs ciblés", value: String(selectedVisibleEmployees.length) },
          ],
          title: selectedVisibleEmployees.length > 1 ? "Supprimer la sélection de chauffeurs ?" : "Supprimer ce chauffeur ?",
          warningMessage: "Les chauffeurs supprimés devront être recréés pour redevenir disponibles.",
        }
      : deleteSelectionDialogSection === "products"
        ? {
          description: "Cette action supprime définitivement les codes produit sélectionnés du Data Manager actif.",
          impactItems: [
            "Les lignes sélectionnées seront retirées de la table active.",
            "Les prochaines conversions Sollio ne pourront plus réutiliser ces correspondances.",
          ],
          summaryItems: [
            { label: "Codes produit ciblés", value: String(selectedVisibleProducts.length) },
          ],
          title: selectedVisibleProducts.length > 1 ? "Supprimer la sélection de codes produit ?" : "Supprimer ce code produit ?",
          warningMessage: "Les codes produit supprimés devront être recréés ou réimportés depuis Sollio.",
        }
      : deleteSelectionDialogSection === "compartments" && selectedTrailerDeletion.trailers.length
        ? {
          description: "Une remorque seule est supprimée avec ses compartiments ; une autre remorque perd seulement ses compartiments.",
          impactItems: [
            ...selectedTrailerDeletion.items,
            "Le dispatch devra être revalidé si un planning était déjà chargé.",
          ],
          summaryItems: [
            { label: "Remorques ciblées", value: String(selectedVisibleCompartmentTrucks.length) },
            { label: "Compartiments ciblés", value: String(selectedVisibleCompartmentCount) },
          ],
          title: "Supprimer la sélection ?",
          warningMessage: "Une remorque seule qui a roulé n'est pas supprimée : retirez-la plutôt de la flotte.",
        }
      : deleteSelectionDialogSection === "compartments"
        ? {
          description: "Supprime tous les compartiments des remorques cochées. Les remorques restent.",
          impactItems: [
            "Leurs capacités sont retirées.",
            "Le dispatch devra être revalidé si un planning était déjà chargé.",
          ],
          summaryItems: [
            { label: "Remorques ciblées", value: String(selectedVisibleCompartmentTrucks.length) },
            { label: "Compartiments ciblés", value: String(selectedVisibleCompartmentCount) },
          ],
          title: selectedVisibleCompartmentTrucks.length > 1 ? "Supprimer les compartiments de ces remorques ?" : "Supprimer les compartiments de cette remorque ?",
          warningMessage: "Il faudra les recréer ou les réimporter.",
        }
        : null;

  // Toute ligne visible reste sélectionnée (« Retirer de la flotte » vise aussi une ligne qui a roulé) ;
  // « Supprimer la sélection » ne compte que les lignes supprimables.
  React.useEffect(() => {
    const allowedTruckIds = new Set(sortedTrucks.map((truck) => truck.truck_id));
    setSelectedTruckIds((currentSelection) => pruneGridSelections(currentSelection, allowedTruckIds));
  }, [sortedTrucks]);

  React.useEffect(() => {
    const allowedCorrespondenceIds = new Set(
      deletableVisibleClientCorrespondences.map((entry) => entry.correspondence_id)
    );
    setSelectedClientCorrespondenceIds((currentSelection) => pruneGridSelections(currentSelection, allowedCorrespondenceIds));
  }, [deletableVisibleClientCorrespondences]);

  React.useEffect(() => {
    const allowedEmployeeIds = new Set(
      deletableVisibleEmployees.map((entry) => entry.employee_id)
    );
    setSelectedEmployeeIds((currentSelection) => pruneGridSelections(currentSelection, allowedEmployeeIds));
  }, [deletableVisibleEmployees]);

  React.useEffect(() => {
    const allowedProductIds = new Set(
      deletableVisibleProducts.map((entry) => entry.product_id)
    );
    setSelectedProductIds((currentSelection) => pruneGridSelections(currentSelection, allowedProductIds));
  }, [deletableVisibleProducts]);

  React.useEffect(() => {
    const allowedTruckIds = new Set(sortedCompartmentTrucks.map((truck) => truck.truck_id));
    setSelectedCompartmentTruckIds((currentSelection) => pruneGridSelections(currentSelection, allowedTruckIds));
  }, [sortedCompartmentTrucks]);

  React.useEffect(() => {
    if (!deleteSelectionDialogSection) {
      return;
    }

    const selectedCount = deleteSelectionDialogSection === "vehicles"
      ? selectedVisibleTrucks.length
      : deleteSelectionDialogSection === "correspondences"
        ? selectedVisibleClientCorrespondences.length
        : deleteSelectionDialogSection === "employees"
          ? selectedVisibleEmployees.length
          : deleteSelectionDialogSection === "products"
            ? selectedVisibleProducts.length
        : selectedVisibleCompartmentTrucks.length;

    if (selectedCount === 0) {
      setDeleteSelectionDialogSection("");
    }
  }, [
    deleteSelectionDialogSection,
    selectedVisibleClientCorrespondences.length,
    selectedVisibleCompartmentTrucks.length,
    selectedVisibleEmployees.length,
    selectedVisibleProducts.length,
    selectedVisibleTrucks.length,
  ]);

  function handleSelectTab(nextTab) {
    saveActiveTab(access.userId, nextTab);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", nextTab);
    setSearchParams(nextSearchParams);
  }

  function handleVehicleSort(nextSortKey) {
    setVehicleSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  function handleClientCorrespondenceSort(nextSortKey) {
    setClientCorrespondenceSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  function handleEmployeeSort(nextSortKey) {
    setEmployeeSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  function handleProductSort(nextSortKey) {
    setProductSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  function handleCompartmentSort(nextSortKey) {
    setCompartmentSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  function handleOpenFullHistory() {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("history", "full");
    setSearchParams(nextSearchParams);
  }

  function handleCloseFullHistory() {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("history");
    setSearchParams(nextSearchParams);
  }

  function handleChangeOrdersSearchQuery(nextValue) {
    startTransition(() => {
      setOrdersSearchQuery(nextValue);
    });
  }

  function handleOpenOrderBrowser() {
    setIsOrderBrowserOpen(true);
  }

  function handleCloseOrderBrowser() {
    setIsOrderBrowserOpen(false);
  }

  function handleApplyOrderBrowser({ deliveryDate, searchQuery: nextSearchQuery }) {
    setIsPendingBillingApprovalFilter(false);
    orderApprovalQueueRestoreRef.current = null;
    setOrderScope("browse");
    setOrderBrowseDateFilter(deliveryDate || "");
    handleChangeOrdersSearchQuery(nextSearchQuery || "");
    setIsOrderBrowserOpen(false);
  }

  function handleQuickSelectOrderBrowseDate(nextDeliveryDate) {
    setIsPendingBillingApprovalFilter(false);
    orderApprovalQueueRestoreRef.current = null;
    const resolvedDate = typeof nextDeliveryDate === "string" ? nextDeliveryDate.trim() : "";
    if (!resolvedDate) {
      setOrderScope("browse");
      setOrderBrowseDateFilter("");
      setIsOrderBrowserOpen(false);
      return;
    }

    setOrderScope("browse");
    setOrderBrowseDateFilter(resolvedDate);
    setIsOrderBrowserOpen(false);
  }

  function handleResetOrderScope() {
    setIsPendingBillingApprovalFilter(false);
    orderApprovalQueueRestoreRef.current = null;
    setOrderScope("dispatch");
    setOrderBrowseDateFilter("");
    setRequireFullOrdersCatalog(false);
    handleChangeOrdersSearchQuery("");
    setIsOrderBrowserOpen(false);
  }

  function handleTogglePendingBillingApprovalFilter() {
    if (isPendingBillingApprovalFilter) {
      const previousSelection = orderApprovalQueueRestoreRef.current || {
        orderBrowseDateFilter: "",
        orderScope: "dispatch",
        ordersSearchQuery: "",
        requireFullOrdersCatalog: false,
      };
      setIsPendingBillingApprovalFilter(false);
      setOrderScope(previousSelection.orderScope);
      setOrderBrowseDateFilter(previousSelection.orderBrowseDateFilter);
      setRequireFullOrdersCatalog(previousSelection.requireFullOrdersCatalog);
      handleChangeOrdersSearchQuery(previousSelection.ordersSearchQuery);
      orderApprovalQueueRestoreRef.current = null;
      setIsOrderBrowserOpen(false);
      return;
    }

    orderApprovalQueueRestoreRef.current = {
      orderBrowseDateFilter,
      orderScope,
      ordersSearchQuery,
      requireFullOrdersCatalog,
    };
    setIsPendingBillingApprovalFilter(true);
    setOrderScope("browse");
    setOrderBrowseDateFilter("");
    setRequireFullOrdersCatalog(true);
    handleChangeOrdersSearchQuery("");
    setIsOrderBrowserOpen(false);
  }

  function handleRequireFullOrdersCatalog(required) {
    setRequireFullOrdersCatalog(Boolean(required));
  }

  function handleToggleHistoryPanel(sectionKey) {
    setCollapsedHistorySections((currentState) => ({
      ...currentState,
      [sectionKey]: !currentState[sectionKey],
    }));
  }

  function handleTruckSelectionChange(truckId, isSelected) {
    setSelectedTruckIds((currentSelection) => updateGridSelectionState(currentSelection, truckId, isSelected));
  }

  function handleToggleAllVisibleTruckSelections(shouldSelect) {
    setSelectedTruckIds((currentSelection) => toggleVisibleGridSelections(
      currentSelection,
      deletableVisibleTrucks.map((truck) => truck.truck_id),
      shouldSelect,
    ));
  }

  function handleClientCorrespondenceSelectionChange(correspondenceId, isSelected) {
    setSelectedClientCorrespondenceIds((currentSelection) => updateGridSelectionState(currentSelection, correspondenceId, isSelected));
  }

  function handleToggleAllVisibleClientCorrespondenceSelections(shouldSelect) {
    setSelectedClientCorrespondenceIds((currentSelection) => toggleVisibleGridSelections(
      currentSelection,
      deletableVisibleClientCorrespondences.map((entry) => entry.correspondence_id),
      shouldSelect,
    ));
  }

  function handleEmployeeSelectionChange(employeeId, isSelected) {
    setSelectedEmployeeIds((currentSelection) => updateGridSelectionState(currentSelection, employeeId, isSelected));
  }

  function handleToggleAllVisibleEmployeeSelections(shouldSelect) {
    setSelectedEmployeeIds((currentSelection) => toggleVisibleGridSelections(
      currentSelection,
      deletableVisibleEmployees.map((entry) => entry.employee_id),
      shouldSelect,
    ));
  }

  function handleProductSelectionChange(productId, isSelected) {
    setSelectedProductIds((currentSelection) => updateGridSelectionState(currentSelection, productId, isSelected));
  }

  function handleToggleAllVisibleProductSelections(shouldSelect) {
    setSelectedProductIds((currentSelection) => toggleVisibleGridSelections(
      currentSelection,
      deletableVisibleProducts.map((entry) => entry.product_id),
      shouldSelect,
    ));
  }

  function handleCompartmentTruckSelectionChange(truckId, isSelected) {
    setSelectedCompartmentTruckIds((currentSelection) => updateGridSelectionState(currentSelection, truckId, isSelected));
  }

  function handleToggleAllVisibleCompartmentTruckSelections(shouldSelect) {
    setSelectedCompartmentTruckIds((currentSelection) => toggleVisibleGridSelections(
      currentSelection,
      deletableVisibleCompartmentTrucks.map((truck) => truck.truck_id),
      shouldSelect,
    ));
  }

  function clearCompartmentDraftStateForTrucks(truckIds) {
    if (!Array.isArray(truckIds) || truckIds.length === 0) {
      return;
    }

    const truckIdSet = new Set(truckIds);
    setCompartmentMatrixDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([truckId]) => !truckIdSet.has(truckId))
    ));
    setCompartmentDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([draftKey]) => !truckIdSet.has(draftKey.split(":")[0]))
    ));
    setNewCompartmentDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([truckId]) => !truckIdSet.has(truckId))
    ));
    setEditingCompartmentTruckId((currentTruckId) => (
      currentTruckId && truckIdSet.has(currentTruckId) ? null : currentTruckId
    ));
    setEditingCompartmentTruckFocusKey(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY);
  }

  async function handleConfirmDeleteSelectedTrucks() {
    const trucksToDelete = [...selectedVisibleTrucks];
    if (trucksToDelete.length === 0) {
      setDeleteSelectionDialogSection("");
      return;
    }

    setDeleteSelectionDialogSection("");
    setIsDeletingSelection(true);

    let deletedCount = 0;
    const failures = [];

    for (const truck of trucksToDelete) {
      try {
        await deleteTruck(
          truck.truck_id,
          { expected_updated_at: truck.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(error, `Impossible de supprimer le camion ${truck.truck_name || truck.truck_id}.`));
      }
    }

    if (deletedCount > 0) {
      await refreshState?.();
    }

    setSelectedTruckIds({});
    setIsDeletingSelection(false);

    if (deletedCount > 0 && failures.length === 0) {
      showToast({
        duration: 6000,
        title: deletedCount > 1 ? "Camions supprimés" : "Camion supprimé",
        message: `${deletedCount} camion(s) supprimé(s).`,
        type: "success",
      });
      return;
    }

    if (deletedCount > 0 && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedCount} camion(s) supprimé(s), ${failures.length} en erreur. ${failures[0]}`,
        type: "error",
      });
      return;
    }

    if (failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0],
        type: "error",
      });
    }
  }

  async function handleConfirmDeleteSelectedClientCorrespondences() {
    const correspondencesToDelete = [...selectedVisibleClientCorrespondences];
    if (correspondencesToDelete.length === 0) {
      setDeleteSelectionDialogSection("");
      return;
    }

    setDeleteSelectionDialogSection("");
    setIsDeletingSelection(true);

    let deletedCount = 0;
    const failures = [];

    for (const correspondence of correspondencesToDelete) {
      try {
        await deleteClientCorrespondence(
          correspondence.correspondence_id,
          { expected_updated_at: correspondence.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(error, `Impossible de supprimer le client ${correspondence.correspondence_id}.`));
      }
    }

    if (deletedCount > 0) {
      await refreshState?.();
    }

    setSelectedClientCorrespondenceIds({});
    setIsDeletingSelection(false);

    if (deletedCount > 0 && failures.length === 0) {
      showToast({
        duration: 6000,
        title: deletedCount > 1 ? "Clients supprimés" : "Client supprimé",
        message: `${deletedCount} client(s) supprimé(s).`,
        type: "success",
      });
      return;
    }

    if (deletedCount > 0 && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedCount} client(s) supprimé(s), ${failures.length} en erreur. ${failures[0]}`,
        type: "error",
      });
      return;
    }

    if (failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0],
        type: "error",
      });
    }
  }

  async function handleConfirmDeleteSelectedEmployees() {
    const employeesToDelete = [...selectedVisibleEmployees];
    if (employeesToDelete.length === 0) {
      setDeleteSelectionDialogSection("");
      return;
    }

    setDeleteSelectionDialogSection("");
    setIsDeletingSelection(true);

    let deletedCount = 0;
    const failures = [];

    for (const employee of employeesToDelete) {
      try {
        await deleteEmployee(
          employee.employee_id,
          { expected_updated_at: employee.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(error, `Impossible de supprimer le chauffeur ${employee.employee_id}.`));
      }
    }

    if (deletedCount > 0) {
      await refreshState?.();
    }

    setSelectedEmployeeIds({});
    setIsDeletingSelection(false);

    if (deletedCount > 0 && failures.length === 0) {
      showToast({
        duration: 6000,
        title: deletedCount > 1 ? "Chauffeurs supprimés" : "Chauffeur supprimé",
        message: `${deletedCount} chauffeur(s) supprimé(s).`,
        type: "success",
      });
      return;
    }

    if (deletedCount > 0 && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedCount} chauffeur(s) supprimé(s), ${failures.length} en erreur. ${failures[0]}`,
        type: "error",
      });
      return;
    }

    if (failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0],
        type: "error",
      });
    }
  }

  async function handleConfirmDeleteSelectedProducts() {
    const productsToDelete = [...selectedVisibleProducts];
    if (productsToDelete.length === 0) {
      setDeleteSelectionDialogSection("");
      return;
    }

    setDeleteSelectionDialogSection("");
    setIsDeletingSelection(true);

    let deletedCount = 0;
    const failures = [];

    for (const product of productsToDelete) {
      try {
        await deleteProduct(
          product.product_id,
          { expected_updated_at: product.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(error, `Impossible de supprimer le produit ${product.product_id}.`));
      }
    }

    if (deletedCount > 0) {
      await refreshState?.();
    }

    setSelectedProductIds({});
    setIsDeletingSelection(false);

    if (deletedCount > 0 && failures.length === 0) {
      showToast({
        duration: 6000,
        title: deletedCount > 1 ? "Codes produit supprimés" : "Code produit supprimé",
        message: `${deletedCount} code(s) produit supprimé(s).`,
        type: "success",
      });
      return;
    }

    if (deletedCount > 0 && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedCount} code(s) produit supprimé(s), ${failures.length} en erreur. ${failures[0]}`,
        type: "error",
      });
      return;
    }

    if (failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0],
        type: "error",
      });
    }
  }

  async function handleConfirmDeleteSelectedCompartments() {
    const trucksToDelete = [...selectedVisibleCompartmentTrucks];
    const { trailers: trailersToDelete, emptied } = trailerSelectionDeletion(trucksToDelete);
    const compartmentsToDelete = emptied.flatMap((truck) => (
      Array.isArray(truck?.compartments)
        ? truck.compartments.map((compartment) => ({ compartment, truck }))
        : []
    ));

    if (compartmentsToDelete.length === 0 && trailersToDelete.length === 0) {
      setDeleteSelectionDialogSection("");
      return;
    }

    setDeleteSelectionDialogSection("");
    setIsDeletingSelection(true);

    let deletedCount = 0;
    let deletedTrailerCount = 0;
    const failures = [];

    // Remorque seule : supprimée comme un camion, avec ses compartiments ; refusée si elle a roulé.
    for (const truck of trailersToDelete) {
      try {
        await deleteTruck(
          truck.truck_id,
          { expected_updated_at: truck.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedTrailerCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(error, `Impossible de supprimer la remorque ${truck.compartment_name || truck.truck_id}.`));
      }
    }

    for (const { compartment, truck } of compartmentsToDelete) {
      try {
        await deleteCompartment(
          truck.truck_id,
          compartment.compartment_id,
          { expected_updated_at: compartment.updated_at },
          { shouldReload: false, skipSuccessToast: true },
        );
        deletedCount += 1;
      } catch (error) {
        failures.push(resolveMutationErrorMessage(
          error,
          `Impossible de supprimer le compartiment ${compartment.position} de la remorque ${truck.compartment_name || truck.truck_id}.`,
        ));
      }
    }

    if (deletedCount > 0 || deletedTrailerCount > 0) {
      clearCompartmentDraftStateForTrucks(trucksToDelete.map((truck) => truck.truck_id));
      await refreshState?.();
    }

    setSelectedCompartmentTruckIds({});
    setIsDeletingSelection(false);

    // L'annonce résume les deux cas : remorques seules supprimées, compartiments supprimés.
    const deletedSummary = [
      ...(deletedTrailerCount > 0 ? [`${deletedTrailerCount} remorque(s) seule(s) supprimée(s)`] : []),
      ...(deletedCount > 0 ? [`${deletedCount} compartiment(s) supprimé(s) sur ${emptied.length} remorque(s)`] : []),
    ].join(", ");

    if (deletedSummary && failures.length === 0) {
      showToast({
        duration: 6000,
        title: deletedTrailerCount > 0
          ? (deletedTrailerCount > 1 ? "Remorques supprimées" : "Remorque supprimée")
          : (deletedCount > 1 ? "Compartiments supprimés" : "Compartiment supprimé"),
        message: `${deletedSummary}.`,
        type: "success",
      });
      return;
    }

    if (deletedSummary && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedSummary}, ${failures.length} en erreur. ${failures[0]}`,
        type: "error",
      });
      return;
    }

    if (failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0],
        type: "error",
      });
    }
  }

  function handleConfirmDeleteSelection() {
    if (deleteSelectionDialogSection === "vehicles") {
      void handleConfirmDeleteSelectedTrucks();
      return;
    }

    if (deleteSelectionDialogSection === "correspondences") {
      void handleConfirmDeleteSelectedClientCorrespondences();
      return;
    }

    if (deleteSelectionDialogSection === "employees") {
      void handleConfirmDeleteSelectedEmployees();
      return;
    }

    if (deleteSelectionDialogSection === "products") {
      void handleConfirmDeleteSelectedProducts();
      return;
    }

    if (deleteSelectionDialogSection === "compartments") {
      void handleConfirmDeleteSelectedCompartments();
    }
  }

  function handleStartAddingCompartment() {
    if (!firstAvailableCompartmentAddTarget) {
      return;
    }

    handleStartEditingCompartmentTruck(firstAvailableCompartmentAddTarget.truck, firstAvailableCompartmentAddTarget.focusKey);
  }

  function updateTruckDraftValue(truck, field, value) {
    setTruckDrafts((currentDrafts) => ({
      ...currentDrafts,
      [truck.truck_id]: {
        ...(currentDrafts[truck.truck_id] || createTruckDraft(truck)),
        [field]: value,
      },
    }));
  }

  function updateCompartmentDraftValue(truckId, compartment, field, value) {
    const draftKey = `${truckId}:${compartment.compartment_id}`;
    setCompartmentDrafts((currentDrafts) => ({
      ...currentDrafts,
      [draftKey]: {
        ...(currentDrafts[draftKey] || createCompartmentDraft(compartment)),
        [field]: value,
      },
    }));
  }

  function updateNewCompartmentDraftValue(truckId, field, value) {
    setNewCompartmentDrafts((currentDrafts) => ({
      ...currentDrafts,
      [truckId]: {
        ...(currentDrafts[truckId] || createCompartmentDraft()),
        [field]: value,
      },
    }));
  }

  function buildTruckPayloadForSave(draft, options = {}) {
    try {
      return buildTruckPayload(draft, options);
    } catch (error) {
      showToast({
        duration: 8000,
        title: "Paramètres camion invalides",
        message: error instanceof Error && error.message
          ? error.message
          : "La vitesse moyenne du camion est obligatoire.",
        type: "error",
      });
      return null;
    }
  }

  async function handleStartCreatingTruck() {
    setIsPreparingTruck(true);
    try {
      const freshState = await refreshState({ sections: ["trucks"], force: true, silent: true });
      setNewTruckDraft(createTruckDraft(null, freshState.trucks || []));
      setIsCreatingTruck(true);
    } catch (error) {
      showToast({ type: "error", title: "Ajout de camion", message: error.message || "Impossible de relire les valeurs des camions." });
    } finally {
      setIsPreparingTruck(false);
    }
  }

  async function handleCreateTruck() {
    const payload = buildTruckPayloadForSave(newTruckDraft, { isCreation: true });
    if (!payload) {
      return;
    }
    await createTruck(payload);
    setNewTruckDraft(createTruckDraft());
    setIsCreatingTruck(false);
  }

  function handleNewTruckKindChange(kind) {
    setNewTruckDraft((currentDraft) => ({
      ...applyNewTruckDraftChange(currentDraft, "truck_type", kind === "dompeur" ? "dompeur" : "citerne", state.trucks),
      unit_kind: kind === "dompeur" ? "ensemble" : "camion",
    }));
  }

  function handleCancelCreatingTruck() {
    setNewTruckDraft(createTruckDraft());
    setIsCreatingTruck(false);
  }

  function updateNewClientCorrespondenceDraftValue(field, value, options = {}) {
    if ((field === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY || field === CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_KEY) && options.preserveValidationToast !== true) {
      dismissClientCorrespondenceAddressValidationToast();
    }

    setNewClientCorrespondenceDraft((currentDraft) => applyClientCorrespondenceDraftFieldChange(currentDraft, field, value));
  }

  function updateNewEmployeeDraftValue(field, value) {
    setNewEmployeeDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  }

  function updateNewProductDraftValue(field, value) {
    setNewProductDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  }

  function handleCancelCreatingClientCorrespondence() {
    dismissClientCorrespondenceAddressValidationToast();
    setNewClientCorrespondenceDraft(createClientCorrespondenceDraft());
    setIsCreatingClientCorrespondence(false);
  }

  function handleStartCreatingClientCorrespondence() {
    dismissClientCorrespondenceAddressValidationToast();
    setNewClientCorrespondenceDraft(createClientCorrespondenceDraft());
    setIsCreatingClientCorrespondence(true);
  }

  function handleCancelCreatingEmployee() {
    setNewEmployeeDraft(createEmployeeDraft());
    setIsCreatingEmployee(false);
  }

  function handleStartCreatingEmployee() {
    setNewEmployeeDraft(createEmployeeDraft());
    setIsCreatingEmployee(true);
  }

  function handleCancelCreatingProduct() {
    setNewProductDraft(createProductDraft());
    setIsCreatingProduct(false);
  }

  function handleStartCreatingProduct() {
    setNewProductDraft(createProductDraft());
    setIsCreatingProduct(true);
  }

  async function handleCreateClientCorrespondence() {
    let draftToCreate = newClientCorrespondenceDraft;

    try {
      assertClientCorrespondenceClientCodeIsRequired({
        clientCode: draftToCreate.client_code,
        validationKey: getCreateClientCorrespondenceClientCodeValidationKey(),
      });
      assertClientCorrespondenceClientCodeIsUnique({
        clientCode: draftToCreate.client_code,
        validationKey: getCreateClientCorrespondenceClientCodeValidationKey(),
      });
    } catch {
      return;
    }

    try {
      draftToCreate = {
        ...newClientCorrespondenceDraft,
        address: isCoopOnlyClientCorrespondenceDraft(newClientCorrespondenceDraft)
          ? newClientCorrespondenceDraft.address
          : await validateClientCorrespondenceAddressWithCivicCheck({
            validationKey: getCreateClientCorrespondenceAddressValidationKey(),
            value: newClientCorrespondenceDraft.address,
            onResolvedValue: (resolvedValue) => updateNewClientCorrespondenceDraftValue(CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY, resolvedValue, { preserveValidationToast: true }),
          }),
      };
    } catch (error) {
      if (isMissingCivicNumberConfirmationCancelledError(error)) {
        return;
      }
      return;
    }

    await createClientCorrespondence(buildClientCorrespondencePayload(draftToCreate));
    dismissClientCorrespondenceAddressValidationToast();
    setNewClientCorrespondenceDraft(createClientCorrespondenceDraft());
    setIsCreatingClientCorrespondence(false);
  }

  async function handleCreateEmployee() {
    await createEmployee(buildEmployeePayload(newEmployeeDraft));
    setNewEmployeeDraft(createEmployeeDraft());
    setIsCreatingEmployee(false);
  }

  async function handleCreateProduct() {
    await createProduct(buildProductPayload(newProductDraft));
    setNewProductDraft(createProductDraft());
    setIsCreatingProduct(false);
  }

  function updateClientCorrespondenceDraftValue(correspondence, field, value, options = {}) {
    if ((field === CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY || field === CLIENT_CORRESPONDENCE_CLIENT_CODE_FIELD_KEY) && options.preserveValidationToast !== true) {
      dismissClientCorrespondenceAddressValidationToast();
    }

    setClientCorrespondenceDrafts((currentDrafts) => ({
      ...currentDrafts,
      [correspondence.correspondence_id]: applyClientCorrespondenceDraftFieldChange(
        currentDrafts[correspondence.correspondence_id] || createClientCorrespondenceDraft(correspondence),
        field,
        value,
      ),
    }));
  }

  function updateEmployeeDraftValue(employee, field, value) {
    setEmployeeDrafts((currentDrafts) => ({
      ...currentDrafts,
      [employee.employee_id]: {
        ...(currentDrafts[employee.employee_id] || createEmployeeDraft(employee)),
        [field]: value,
      },
    }));
  }

  function updateProductDraftValue(product, field, value) {
    setProductDrafts((currentDrafts) => ({
      ...currentDrafts,
      [product.product_id]: {
        ...(currentDrafts[product.product_id] || createProductDraft(product)),
        [field]: value,
      },
    }));
  }

  const handleCreateClientCorrespondenceAddressEnterValidation = React.useCallback(() => {
    void handleCreateClientCorrespondence();
  }, [handleCreateClientCorrespondence]);

  const handleClientCorrespondenceAddressEnterValidation = React.useCallback((correspondence) => {
    if (!correspondence?.correspondence_id) {
      return;
    }

    void handleSaveClientCorrespondence(correspondence);
  }, [handleSaveClientCorrespondence]);

  function handleStartEditingClientCorrespondence(correspondence, focusKey = CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY) {
    if (!canWriteActiveTab) return;
    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY;

    dismissClientCorrespondenceAddressValidationToast();

    setClientCorrespondenceDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingClientCorrespondenceId && editingClientCorrespondenceId !== correspondence.correspondence_id) {
        delete nextDrafts[editingClientCorrespondenceId];
      }

      nextDrafts[correspondence.correspondence_id] = nextDrafts[correspondence.correspondence_id] || createClientCorrespondenceDraft(correspondence);
      return nextDrafts;
    });

    setEditingClientCorrespondenceId(correspondence.correspondence_id);
    setEditingClientCorrespondenceFocusKey(resolvedFocusKey);
  }

  function handleStartEditingEmployee(employee, focusKey = EMPLOYEE_DEFAULT_FOCUS_KEY) {
    if (!canWriteActiveTab) return;
    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : EMPLOYEE_DEFAULT_FOCUS_KEY;

    setEmployeeDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingEmployeeId && editingEmployeeId !== employee.employee_id) {
        delete nextDrafts[editingEmployeeId];
      }

      nextDrafts[employee.employee_id] = nextDrafts[employee.employee_id] || createEmployeeDraft(employee);
      return nextDrafts;
    });

    setEditingEmployeeId(employee.employee_id);
    setEditingEmployeeFocusKey(resolvedFocusKey);
  }

  function handleStartEditingProduct(product, focusKey = PRODUCT_DEFAULT_FOCUS_KEY) {
    if (!canWriteActiveTab) return;
    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : PRODUCT_DEFAULT_FOCUS_KEY;

    setProductDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingProductId && editingProductId !== product.product_id) {
        delete nextDrafts[editingProductId];
      }

      nextDrafts[product.product_id] = nextDrafts[product.product_id] || createProductDraft(product);
      return nextDrafts;
    });

    setEditingProductId(product.product_id);
    setEditingProductFocusKey(resolvedFocusKey);
  }

  function handleCancelClientCorrespondenceEdit(correspondenceId) {
    dismissClientCorrespondenceAddressValidationToast();
    setClientCorrespondenceDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[correspondenceId];
      return nextDrafts;
    });
    setEditingClientCorrespondenceId((currentId) => (currentId === correspondenceId ? null : currentId));
    setEditingClientCorrespondenceFocusKey(CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY);
  }

  function handleCancelEmployeeEdit(employeeId) {
    setEmployeeDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[employeeId];
      return nextDrafts;
    });
    setEditingEmployeeId((currentId) => (currentId === employeeId ? null : currentId));
    setEditingEmployeeFocusKey(EMPLOYEE_DEFAULT_FOCUS_KEY);
  }

  function handleCancelProductEdit(productId) {
    setProductDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[productId];
      return nextDrafts;
    });
    setEditingProductId((currentId) => (currentId === productId ? null : currentId));
    setEditingProductFocusKey(PRODUCT_DEFAULT_FOCUS_KEY);
  }

  async function handleSaveClientCorrespondence(correspondence) {
    let draft = clientCorrespondenceDrafts[correspondence.correspondence_id] || createClientCorrespondenceDraft(correspondence);
    const addressChanged = hasClientCorrespondenceAddressChanged(correspondence, draft);

    try {
      assertClientCorrespondenceClientCodeIsRequired({
        clientCode: draft.client_code,
        validationKey: getClientCorrespondenceClientCodeValidationKey(correspondence.correspondence_id),
      });
      assertClientCorrespondenceClientCodeIsUnique({
        clientCode: draft.client_code,
        excludeCorrespondenceId: correspondence.correspondence_id,
        validationKey: getClientCorrespondenceClientCodeValidationKey(correspondence.correspondence_id),
      });
    } catch {
      return;
    }

    if (addressChanged) {
      try {
        draft = {
          ...draft,
          address: isCoopOnlyClientCorrespondenceDraft(draft)
            ? draft.address
            : await validateClientCorrespondenceAddressWithCivicCheck({
              validationKey: getClientCorrespondenceAddressValidationKey(correspondence.correspondence_id),
              value: draft.address,
              onResolvedValue: (resolvedValue) => updateClientCorrespondenceDraftValue(correspondence, CLIENT_CORRESPONDENCE_ADDRESS_FIELD_KEY, resolvedValue, { preserveValidationToast: true }),
            }),
        };
      } catch (error) {
        if (isMissingCivicNumberConfirmationCancelledError(error)) {
          return;
        }
        return;
      }
    }

    const updatePayload = buildClientCorrespondencePayload(draft, { expectedUpdatedAt: correspondence.updated_at });
    if (!addressChanged) {
      delete updatePayload.address;
    }
    const newName = String(draft.client_name || "").trim();
    if (newName && newName !== String(correspondence.client_name || "").trim()) {
      // Un client utilisé est renommé partout : la fenêtre compte d'abord ce qui change.
      const preview = await previewClientCorrespondenceRename(correspondence.correspondence_id).catch(() => null);
      if (isClientInUse(preview)) {
        setClientRenameCandidate({ correspondence, name: newName, preview, payload: updatePayload });
        return;
      }
    }
    await saveClientCorrespondence(correspondence, updatePayload);
  }

  async function saveClientCorrespondence(correspondence, updatePayload) {
    await updateClientCorrespondence(
      correspondence.correspondence_id,
      updatePayload
    );
    dismissClientCorrespondenceAddressValidationToast();
    setClientCorrespondenceDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[correspondence.correspondence_id];
      return nextDrafts;
    });
    setEditingClientCorrespondenceId((currentId) => (currentId === correspondence.correspondence_id ? null : currentId));
    setEditingClientCorrespondenceFocusKey(CLIENT_CORRESPONDENCE_DEFAULT_FOCUS_KEY);
  }

  // « Supprimer » d'une ligne : même confirmation que « Supprimer la sélection », pour cette seule ligne.
  function confirmRowDeletion(section, setSelectedIds, rowId) {
    setSelectedIds({ [rowId]: true });
    setDeleteSelectionDialogSection(section);
  }

  async function handleSaveEmployee(employee) {
    const draft = employeeDrafts[employee.employee_id] || createEmployeeDraft(employee);
    await updateEmployee(
      employee.employee_id,
      buildEmployeePayload(draft, { expectedUpdatedAt: employee.updated_at }),
      { driversView: isDriversView },
    );
    setEmployeeDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[employee.employee_id];
      return nextDrafts;
    });
    setEditingEmployeeId((currentId) => (currentId === employee.employee_id ? null : currentId));
    setEditingEmployeeFocusKey(EMPLOYEE_DEFAULT_FOCUS_KEY);
  }

  async function handleSaveProduct(product) {
    const draft = productDrafts[product.product_id] || createProductDraft(product);
    await updateProduct(
      product.product_id,
      buildProductPayload(draft, { expectedUpdatedAt: product.updated_at })
    );
    setProductDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[product.product_id];
      return nextDrafts;
    });
    setEditingProductId((currentId) => (currentId === product.product_id ? null : currentId));
    setEditingProductFocusKey(PRODUCT_DEFAULT_FOCUS_KEY);
  }

  function handleStartEditingTruck(truck, focusKey = VEHICLE_DEFAULT_FOCUS_KEY) {
    if (!canWriteActiveTab) return;
    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : VEHICLE_DEFAULT_FOCUS_KEY;

    setTruckDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingTruckId && editingTruckId !== truck.truck_id) {
        delete nextDrafts[editingTruckId];
      }

      nextDrafts[truck.truck_id] = nextDrafts[truck.truck_id] || createTruckDraft(truck);
      return nextDrafts;
    });

    setEditingTruckId(truck.truck_id);
    setEditingTruckFocusKey(resolvedFocusKey);
  }

  function handleStartEditingCompartmentTruck(truck, focusKey = COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY) {
    if (!canWriteActiveTab) return;
    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY;

    setCompartmentMatrixDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingCompartmentTruckId && editingCompartmentTruckId !== truck.truck_id) {
        delete nextDrafts[editingCompartmentTruckId];
      }

      nextDrafts[truck.truck_id] = nextDrafts[truck.truck_id] || createCompartmentMatrixDraft(truck, compartmentMatrixPositions);
      return nextDrafts;
    });

    setEditingCompartmentTruckId(truck.truck_id);
    setEditingCompartmentTruckFocusKey(resolvedFocusKey);
  }

  function handleCancelTruckEdit(truckId) {
    setTruckDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[truckId];
      return nextDrafts;
    });
    setEditingTruckId((currentTruckId) => (currentTruckId === truckId ? null : currentTruckId));
    setEditingTruckFocusKey(VEHICLE_DEFAULT_FOCUS_KEY);
  }

  function handleCancelCompartmentTruckEdit(truckId) {
    setCompartmentMatrixDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[truckId];
      return nextDrafts;
    });
    setEditingCompartmentTruckId((currentTruckId) => (currentTruckId === truckId ? null : currentTruckId));
    setEditingCompartmentTruckFocusKey(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY);
  }

  async function handleSaveTruck(truck) {
    const draft = truckDrafts[truck.truck_id] || createTruckDraft(truck);
    const payload = buildTruckPayloadForSave(draft, { expectedUpdatedAt: truck.updated_at });
    if (!payload) {
      return;
    }
    const typeChange = truckTypeChangeNotice(truck, draft);
    if (typeChange && !(await confirmTruckTypeChange(typeChange))) {
      return;
    }
    await updateTruck(truck.truck_id, payload, { prepareDispatch: true });
    setTruckDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[truck.truck_id];
      return nextDrafts;
    });
    setEditingTruckId((currentTruckId) => (currentTruckId === truck.truck_id ? null : currentTruckId));
    setEditingTruckFocusKey(VEHICLE_DEFAULT_FOCUS_KEY);
  }

  function updateCompartmentMatrixDraftValue(truck, position, value) {
    setCompartmentMatrixDrafts((currentDrafts) => {
      const existingDraft = currentDrafts[truck.truck_id] || createCompartmentMatrixDraft(truck, compartmentMatrixPositions);
      return {
        ...currentDrafts,
        [truck.truck_id]: {
          ...existingDraft,
          valuesByPosition: {
            ...(existingDraft.valuesByPosition || {}),
            [position]: value,
          },
        },
      };
    });
  }

  function updateCompartmentMatrixTruckFieldValue(truck, field, value) {
    setCompartmentMatrixDrafts((currentDrafts) => {
      const existingDraft = currentDrafts[truck.truck_id] || createCompartmentMatrixDraft(truck, compartmentMatrixPositions);
      return {
        ...currentDrafts,
        [truck.truck_id]: {
          ...existingDraft,
          [field]: value,
        },
      };
    });
  }

  async function handleSaveCompartment(truckId, compartment, draft) {
    await updateCompartment(truckId, compartment.compartment_id, buildCompartmentPayload(draft, { expectedUpdatedAt: compartment.updated_at }));
    setCompartmentDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[`${truckId}:${compartment.compartment_id}`];
      return nextDrafts;
    });
  }
  async function handleSaveCompartmentTruck(truck) {
    const draft = compartmentMatrixDrafts[truck.truck_id] || createCompartmentMatrixDraft(truck, compartmentMatrixPositions);
    const existingByPosition = new Map(
      truck.compartments.map((compartment) => [Number(compartment.position), compartment])
    );
    const originalTruckDraft = createTruckDraft(truck);
    let truckPayload = null;
    const compartmentMutations = [];

    if (COMPARTMENT_MATRIX_EDITABLE_KEYS.some((key) => String(draft[key] ?? "") !== String(originalTruckDraft[key] ?? ""))) {
      truckPayload = buildTruckPayloadForSave(
        { ...originalTruckDraft, ...Object.fromEntries(COMPARTMENT_MATRIX_EDITABLE_KEYS.map((key) => [key, draft[key]])) },
        { expectedUpdatedAt: truck.updated_at }
      );
      if (!truckPayload) {
        return;
      }
    }

    for (const position of compartmentMatrixPositions) {
      const existingCompartment = existingByPosition.get(position) || null;
      // « 4,5 » (clavier décimal d'un téléphone) vaut 4.5 : seule une case vide retire le compartiment.
      const nextCapacity = normalizeOptionalNumber(String(draft.valuesByPosition?.[position] ?? "").replace(",", "."));

      if (nextCapacity === null) {
        if (existingCompartment) {
          compartmentMutations.push({
            action: "delete",
            compartmentId: existingCompartment.compartment_id,
            payload: { expected_updated_at: existingCompartment.updated_at },
          });
        }
        continue;
      }

      if (existingCompartment) {
        const currentCapacity = normalizeOptionalNumber(existingCompartment.capacity_tons);

        if (currentCapacity !== nextCapacity) {
          compartmentMutations.push({
            action: "update",
            compartmentId: existingCompartment.compartment_id,
            payload: buildCompartmentPayload(
              {
                capacity_tons: nextCapacity,
                compartment_id: existingCompartment.compartment_id,
                position,
              },
              { expectedUpdatedAt: existingCompartment.updated_at }
            ),
          });
        }
        continue;
      }

      compartmentMutations.push({
        action: "create",
        payload: buildCompartmentPayload({
          capacity_tons: nextCapacity,
          compartment_id: "",
          position,
        }),
      });
    }
    if (truckPayload || compartmentMutations.length > 0) {
      await saveCompartmentTruck(truck.truck_id, { truckPayload, compartmentMutations });
    }

    setCompartmentMatrixDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[truck.truck_id];
      return nextDrafts;
    });
    setEditingCompartmentTruckId((currentTruckId) => (currentTruckId === truck.truck_id ? null : currentTruckId));
    setEditingCompartmentTruckFocusKey(COMPARTMENT_MATRIX_DEFAULT_FOCUS_KEY);
  }

  async function handleCreateCompartment(truckId) {
    const draft = newCompartmentDrafts[truckId] || createCompartmentDraft();
    await createCompartment(truckId, buildCompartmentPayload(draft));
    setNewCompartmentDrafts((currentDrafts) => ({
      ...currentDrafts,
      [truckId]: createCompartmentDraft(),
    }));
  }

  function handleStartCreatingTrailer() {
    const trailer = { unit_kind: "remorque", truck_type: "citerne", is_active: true, evening_is_active: true, compartments: [] };
    // Débits proposés : les plus fréquents des citernes, comme à l'ajout d'un camion (vides, le serveur fait de même).
    const { loading_rate_tons_per_hour, unloading_rate_tons_per_hour } = truckCreationDefaults("citerne", state.trucks);
    setNewTrailerDraft({ ...createCompartmentMatrixDraft(trailer, compartmentMatrixPositions), loading_rate_tons_per_hour, unloading_rate_tons_per_hour });
  }

  // La remorque seule se crée, puis ses compartiments un à un : un refus est signalé, la remorque reste créée.
  async function handleCreateTrailer() {
    const draft = newTrailerDraft;
    const payload = buildTruckPayloadForSave({ ...draft, unit_kind: "remorque", truck_type: "citerne" }, { isCreation: true });
    if (!payload) {
      return;
    }
    let truckId;
    try {
      truckId = (await createTruck(payload))?.truck?.truck_id;
    } catch {
      return;
    }
    setNewTrailerDraft(null);
    for (const position of compartmentMatrixPositions) {
      const capacity = normalizeOptionalNumber(String(draft.valuesByPosition?.[position] ?? "").replace(",", "."));
      if (capacity === null) {
        continue;
      }
      try {
        await createCompartment(truckId, buildCompartmentPayload({ capacity_tons: capacity, compartment_id: "", position }));
      } catch {
        showToast({ duration: 9000, type: "warning", title: "Remorque créée",
          message: `Le compartiment ${position} a été refusé ; la remorque ${payload.compartment_name || ""} reste créée. Corrigez ses compartiments sur sa ligne.` });
        return;
      }
    }
  }

  async function handleDownloadExport(kind) {
    const url = DATA_MANAGER_EXPORT_URLS[kind];
    const fileName = DATA_MANAGER_EXPORT_FILE_NAMES[kind];
    if (!url || !fileName) {
      return;
    }

    if (kind === "dispatch_saves" && contextualDispatchDate) {
      await downloadExport(`${url}?dispatch_date=${encodeURIComponent(contextualDispatchDate)}`, fileName);
      return;
    }

    await downloadExport(url, fileName);
  }

  async function handleImportDispatchSaves(file) {
    try {
      const items = await readDispatchSavesExportFile(file);
      const preview = await previewDispatchSavesImport(items);
      setDispatchSavesImportDialog({
        candidates: Array.isArray(preview?.items) ? preview.items : [],
        errorMessage: "",
        isOpen: true,
        items,
      });
    } catch (error) {
      showToast({
        title: "Import impossible",
        message: error instanceof Error ? error.message : "Impossible d'analyser le fichier de sauvegardes dispatch.",
        tone: "danger",
      });
    }
  }

  function handleCloseDispatchSavesImportDialog() {
    if (isPending("import:dispatch-saves")) {
      return;
    }
    setDispatchSavesImportDialog({
      candidates: [],
      errorMessage: "",
      isOpen: false,
      items: [],
    });
  }

  async function handleConfirmDispatchSavesImport(selectedImportKeys) {
    try {
      await importDispatchSaves(dispatchSavesImportDialog.items, selectedImportKeys);
      handleCloseDispatchSavesImportDialog();
    } catch (error) {
      setDispatchSavesImportDialog((currentDialog) => ({
        ...currentDialog,
        errorMessage: error instanceof Error ? error.message : "Impossible d'importer les sauvegardes dispatch sélectionnées.",
      }));
    }
  }

  function isExportCardBusy(kind) {
    return isPending(`export:${DATA_MANAGER_EXPORT_URLS[kind]}`);
  }

  const panelToolbar = (
    <div className="-mx-3.5 mb-2 px-3.5">
      <div className="data-manager-tabs-bar flex flex-wrap items-center justify-between gap-2">
        <div className={mobile ? "w-full min-w-0" : "flex flex-wrap items-center gap-1.5"}>
          {mobile ? <label className="grid gap-1 text-sm font-semibold">Table à consulter
            <select className="w-full rounded-[4px] border border-line bg-white px-3 py-2" value={selectedTabKey} onChange={(event) => handleSelectTab(event.target.value)}>
              {PRIMARY_TAB_ITEMS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </label> : null}
          {!mobile ? PRIMARY_TAB_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => handleSelectTab(item.key)}
              className={[
                COMPACT_TAB_BUTTON_CLASS_NAME,
                item.key === selectedTabKey
                  ? "border-accent bg-accent text-white hover:bg-accent-strong"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950",
              ].join(" ")}
            >
              {item.label}
            </button>
          )) : null}
        </div>

        {activeTab !== "orders" ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-slate-500">
              {activeTab === "orders"
                ? `${filteredOrders.length} commande(s)`
                : activeTab === "vehicles"
                  ? `${visibleTrucks.length} camion(s)`
                  : activeTab === "employees"
                    ? `${filteredEmployees.length} chauffeur(s)`
                    : activeTab === "products"
                      ? `${filteredProducts.length} code(s) produit`
                    : activeTab === "product_catalog"
                      ? `${state.product_catalog.filter((entry) => entry.name.toLowerCase().includes(deferredSearchQuery.trim().toLowerCase())).length} produit(s)`
                  : activeTab === "compartments"
                    ? `${filteredTrucks.length} remorque(s) · ${compartmentSummary.total} compartiment(s)`
                    : `${filteredClientCorrespondences.length} client(s)`}
            </span>
          </div>
        ) : null}
      </div>

      {activeTab !== "orders" && activeTab !== "product_catalog" ? (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-accent text-white">
              {activeTab === "vehicles" ? <Truck size={14} /> : activeTab === "compartments" ? <Package size={14} /> : activeTab === "employees" ? <ShieldCheck size={14} /> : activeTab === "products" ? <Tag size={14} /> : <Database size={14} />}
            </span>
            <h3 className="text-[0.95rem] font-semibold tracking-[-0.02em] text-slate-950">
              {activeTab === "vehicles" ? "Camions" : activeTab === "compartments" ? "Remorques" : activeTab === "employees" ? "Chauffeurs" : activeTab === "products" ? "Codes produit" : "Clients"}
            </h3>
          </div>

          {!canWriteActiveTab ? null : activeTab === "vehicles" ? (
            vehiclePeriod === "evening" ? null : isCreatingTruck ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <DataManagerNewTruckKind draft={newTruckDraft} onChange={handleNewTruckKindChange} disabled={isPending("truck:create")} />
                <button
                  type="button"
                  onClick={handleCreateTruck}
                  disabled={isPending("truck:create")}
                  className={PRIMARY_ACTION_CLASS_NAME}
                >
                  <Save size={14} />
                  {isPending("truck:create") ? "Sauvegarde..." : "Sauvegarder"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreatingTruck}
                  disabled={isPending("truck:create")}
                  className={SECONDARY_ACTION_CLASS_NAME}
                >
                  Fermer
                </button>
              </div>
            ) : (
              <button type="button" onClick={handleStartCreatingTruck} disabled={isPreparingTruck} className={PRIMARY_ACTION_CLASS_NAME}>
                <Plus size={14} />
                {isPreparingTruck ? "Chargement..." : "Ajouter un camion"}
              </button>
            )
          ) : activeTab === "employees" ? (
            isCreatingEmployee ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleCreateEmployee}
                  disabled={isPending("employee:create")}
                  className={PRIMARY_ACTION_CLASS_NAME}
                >
                  <Save size={14} />
                  {isPending("employee:create") ? "Sauvegarde..." : "Sauvegarder"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreatingEmployee}
                  disabled={isPending("employee:create")}
                  className={SECONDARY_ACTION_CLASS_NAME}
                >
                  Fermer
                </button>
              </div>
            ) : (
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                <p className="min-w-0 flex-1 text-[11px] text-slate-500">Une heure vide reprend la valeur par défaut des Réglages. Une journée planifiée avant les heures par chauffeur garde les heures des camions jusqu'à son premier changement de chauffeur, de camion, de remorque ou d'heures dans le Gantt.</p>
                <button type="button" onClick={handleStartCreatingEmployee} className={PRIMARY_ACTION_CLASS_NAME}>
                  <Plus size={14} />
                  Ajouter un chauffeur
                </button>
              </div>
            )
          ) : activeTab === "products" ? (
            isCreatingProduct ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleCreateProduct}
                  disabled={isPending("product:create")}
                  className={PRIMARY_ACTION_CLASS_NAME}
                >
                  <Save size={14} />
                  {isPending("product:create") ? "Sauvegarde..." : "Sauvegarder"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreatingProduct}
                  disabled={isPending("product:create")}
                  className={SECONDARY_ACTION_CLASS_NAME}
                >
                  Fermer
                </button>
              </div>
            ) : (
              <button type="button" onClick={handleStartCreatingProduct} className={PRIMARY_ACTION_CLASS_NAME}>
                <Plus size={14} />
                Ajouter un code produit
              </button>
            )
          ) : activeTab === "correspondences" ? (
            isCreatingClientCorrespondence ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleCreateClientCorrespondence}
                  disabled={isPending("client-correspondence:create")}
                  className={PRIMARY_ACTION_CLASS_NAME}
                >
                  <Save size={14} />
                  {isPending("client-correspondence:create") ? "Sauvegarde..." : "Sauvegarder"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreatingClientCorrespondence}
                  disabled={isPending("client-correspondence:create")}
                  className={SECONDARY_ACTION_CLASS_NAME}
                >
                  Fermer
                </button>
              </div>
            ) : (
              <button type="button" onClick={handleStartCreatingClientCorrespondence} className={PRIMARY_ACTION_CLASS_NAME}>
                <Plus size={14} />
                Ajouter un client
              </button>
            )
          ) : newTrailerDraft ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" onClick={handleCreateTrailer} disabled={isPending("truck:create")} className={PRIMARY_ACTION_CLASS_NAME}>
                <Save size={14} />
                {isPending("truck:create") ? "Sauvegarde..." : "Sauvegarder"}
              </button>
              <button type="button" onClick={() => setNewTrailerDraft(null)} disabled={isPending("truck:create")} className={SECONDARY_ACTION_CLASS_NAME}>
                Fermer
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" onClick={handleStartCreatingTrailer} className={PRIMARY_ACTION_CLASS_NAME}>
                <Plus size={14} />
                Ajouter une remorque
              </button>
              <button
                type="button"
                onClick={handleStartAddingCompartment}
                disabled={!firstAvailableCompartmentAddTarget}
                className={SECONDARY_ACTION_CLASS_NAME}
                title={!firstAvailableCompartmentAddTarget ? "Aucun compartiment ne peut être ajouté dans la sélection visible." : "Ajouter un compartiment sur le premier emplacement libre visible."}
              >
                <Plus size={14} />
                Ajouter un compartiment
              </button>
            </div>
          )}
        </div>
      ) : null}

      {activeTab !== "orders" ? (
        <div className="mt-2 grid gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative min-w-70 flex-[1_1_320px]">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  startTransition(() => {
                    setSearchQuery(nextValue);
                  });
                }}
                placeholder={searchPlaceholder}
                className="h-9 w-full rounded-sm border border-slate-200 bg-white py-2 pl-9 pr-3 text-[12px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent"
              />
            </label>
          </div>

          {activeTab !== "product_catalog" && !(activeTab === "vehicles" && vehiclePeriod === "evening") ? <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-700">
              {activeSelectionSummaryLabel}
            </span>
            {canWriteActiveTab ? <div className="flex flex-wrap items-center gap-2">
            {activeTab === "vehicles" || activeTab === "compartments" ? <DataManagerFleetRetirementButton
              retired={Boolean(retirementCandidate && retirementDateInTab(retirementCandidate, activeTab))} truck={retirementCandidate}
              onOpen={(truck) => setFleetRetirementDialog({ truck, tab: activeTab, mode: retirementDateInTab(truck, activeTab) ? "reinstate" : "retire" })} /> : null}
            <button
              type="button"
              onClick={() => setDeleteSelectionDialogSection(activeTab)}
              disabled={activeSelectionCount === 0 || isDeletingSelection}
              className={DANGER_ACTION_CLASS_NAME}
            >
              <Trash2 size={13} />
              {isDeletingSelection ? "Suppression..." : "Supprimer la sélection"}
            </button>
            </div> : null}
          </div> : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <BillToCompaniesContext.Provider value={billToCompanyNames}>
    <section
      data-mobile-workspace="data-manager"
      data-manager-viewport={status === "ready" && historyView !== "full" && collapsedHistorySections[activeTab] !== false ? "true" : undefined}
      className={`mx-auto grid w-full ${PAGE_WIDTH_CLASS_NAME} gap-2.5 px-4 pb-4 pt-0 min-[1281px]:pr-16 min-[1600px]:pr-20 max-[720px]:px-4`}>
      <WorkspacePageHeader
        className="data-manager-hero"
        icon={Database}
        title="Data Manager"
        titleAs="h1"
        description={<span className="data-manager-hero-subtitle">Consultez et gérez vos données.</span>}
      />
      <OrderDuplicateConfirmationDialog />
      {status === "loading" ? (
        <article className={PANEL_CLASS_NAME}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Chargement</p>
          <p className="mt-2 text-[13px] text-slate-600">Lecture des sections nécessaires au Data Manager actif...</p>
        </article>
      ) : null}

      {status === "error" ? (
        <article className={`${PANEL_CLASS_NAME} border-amber-200 bg-amber-50/70`}>
          <div className="flex items-start gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-[14px] bg-amber-100 text-amber-700"><AlertTriangle size={16} /></span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Lecture impossible</p>
              <p className="mt-1 text-[13px] leading-6 text-amber-900">{errorMessage || "Le Data Manager n'a pas pu être chargé."}</p>
            </div>
          </div>
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "vehicles" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          <DataManagerTruckPeriodControls period={vehiclePeriod}
            disabled={isCreatingTruck || Boolean(editingTruckId) || isDeletingSelection}
            onPeriodChange={(value) => { setSelectedTruckIds({}); setSearchParams(current => {
              const next = new URLSearchParams(current); next.set("truck_period", value); return next;
            }); }}>
            <DataManagerFleetFilter value={fleetFilters.vehicles} counts={fleetFilterCounts(searchedTrucks, "vehicles", fleetToday)}
              disabled={isCreatingTruck || Boolean(editingTruckId) || isDeletingSelection}
              onChange={(value) => { setSelectedTruckIds({}); setFleetFilters((current) => ({ ...current, vehicles: value })); }} />
          </DataManagerTruckPeriodControls>
          {visibleTrucks.length === 0 && !isCreatingTruck ? (
            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Aucun résultat</p>
              <p className="mt-2 text-[13px] text-slate-600">Aucun camion ne correspond à la recherche courante.</p>
            </div>
          ) : (
            <div key={vehiclePeriod} ref={vehicleScrollRef} className={mobile ? "mobile-record-scroll" : DATA_MANAGER_GRID_SCROLL_CONTAINER_CLASS_NAME}>
              <DataManagerOrdersColumnContextMenu {...vehicleFrozenPanes.menuProps} />
              <VehicleTable className={mobile ? "mobile-record-grid" : "data-manager-frozen-grid border-collapse text-[11px] text-slate-700"} style={mobile ? undefined : vehicleTableStyle}>
                {!mobile ? <thead className={DATA_MANAGER_GRID_STICKY_HEADER_CLASS_NAME}>
                  <tr>
                    {vehiclePeriod !== "evening" ? <DataManagerGridSelectionHeaderCell
                      onResizePointerDown={vehicleColumnPrefs.handleResizePointerDown}
                      style={{ ...vehicleSelectionColumnStyle, ...vehicleFrozenPanes.selectionHeaderStyle }}
                    >
                      <input
                        type="checkbox"
                        aria-label="Sélectionner tous les camions supprimables visibles"
                        checked={allVisibleTrucksSelected}
                        disabled={deletableVisibleTrucks.length === 0 || isDeletingSelection}
                        onChange={(event) => handleToggleAllVisibleTruckSelections(event.target.checked)}
                        className={GRID_SELECTION_INPUT_CLASS_NAME}
                      />
                    </DataManagerGridSelectionHeaderCell> : null}
                    {vehicleFrozenPanes.columns.map((column) => (
                      <DataManagerSortableHeaderCell
                        key={column.key}
                        className={column.widthClassName}
                        columnKey={column.key}
                        columnWidth={column.width}
                        defaultColumnWidth={column.defaultWidth}
                        draggable={!column.fixed}
                        label={column.label}
                        frozenLayoutEntry={column.frozenLayoutEntry}
                        onColumnContextMenu={vehicleFrozenPanes.onColumnContextMenu}
                        onDrop={vehicleColumnPrefs.handleColumnMove}
                        onResizePointerDown={column.fixed ? null : vehicleColumnPrefs.handleResizePointerDown}
                        onSort={handleVehicleSort}
                        sortState={vehicleSortState}
                        title={column.title}
                      />
                    ))}
                    <th className={GRID_HEADER_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>Actions</th>
                  </tr>
                </thead> : null}
                <VehicleBody className={mobile ? "mobile-record-grid" : "bg-white"}>
                  {isCreatingTruck ? (
                    <NewTruckGridRow
                      mobile={mobile}
                      pending={isPending("truck:create")}
                      draft={newTruckDraft}
                      employees={employees}
                      onCancel={handleCancelCreatingTruck}
                      onChange={(field, value) => setNewTruckDraft((currentDraft) => applyNewTruckDraftChange(currentDraft, field, value, state.trucks))}
                      onCreate={handleCreateTruck}
                      resolvedColumns={vehicleFrozenPanes.columns}
                      selectionColumnStyle={{ ...vehicleSelectionColumnStyle, ...vehicleFrozenPanes.selectionStyle }}
                    />
                  ) : null}
                  {sortedTrucks.map((truck) => (
                    <TruckGridRow
                      period={vehiclePeriod}
                      mobile={mobile}
                      key={truck.truck_id}
                      draft={truckDrafts[truck.truck_id] || createTruckDraft(truck)}
                      editingFocusKey={editingTruckFocusKey}
                      employees={employees}
                      hasDraft={Boolean(truckDrafts[truck.truck_id])}
                      isEditing={editingTruckId === truck.truck_id}
                      isFocused={focusedTruckId === truck.truck_id}
                      isPending={Boolean(
                        isDeletingSelection
                        || isPending(`truck:update:${truck.truck_id}`)
                        || isPending(`truck:delete:${truck.truck_id}`)
                      )}
                      isSelected={Boolean(selectedTruckIds[truck.truck_id])}
                      onCancel={() => handleCancelTruckEdit(truck.truck_id)}
                      onChange={(field, value) => updateTruckDraftValue(truck, field, value)}
                      onDelete={() => confirmRowDeletion("vehicles", setSelectedTruckIds, truck.truck_id)}
                      onFocusRow={() => setFocusedTruckId(truck.truck_id)}
                      onSave={() => handleSaveTruck(truck)}
                      onSelectionChange={(isSelected) => handleTruckSelectionChange(truck.truck_id, isSelected)}
                      onStartEdit={canWriteActiveTab ? (focusKey) => handleStartEditingTruck(truck, focusKey) : null}
                      resolvedColumns={vehicleFrozenPanes.columns}
                      selectionColumnStyle={{ ...vehicleSelectionColumnStyle, ...vehicleFrozenPanes.selectionStyle }}
                      selectionTitle={truck.has_historical_assignments ? "Ce camion a roulé : il ne se supprime pas ; « Retirer de la flotte » le sort des prochains Dispatchs." : "Sélectionner ce camion"}
                      truck={truck}
                    />
                  ))}
                </VehicleBody>
              </VehicleTable>
            </div>
          )}
          {isCreatingTruck ? <DataManagerNewUnitNote kind={isDompeurTruck(newTruckDraft) ? "dompeur" : "camion"} /> : null}
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "orders" ? (
        <article className={PANEL_CLASS_NAME}>
          <DataManagerOrdersTab
            restoredConsolidationContext={consolidationReturn?.table}
            onOpenConsolidation={consolidationNavigation.open}
            canOpenConsolidation={hasAccessPermission("billing.consolidation.read")}
            toolbar={panelToolbar}
            productCatalog={state.product_catalog}
            productCodes={state.products}
            canBulkDeleteOrders={canBulkDeleteOrders}
            canClearDeliveredOrders={canClearDeliveredOrders}
            canDeleteDeliveredOrders={canDeleteDeliveredOrders}
            canEditOrders={canEditOrders}
            canExportOrders={canExportTable("orders")}
            canMarkOrdersDelivered={canMarkOrdersDelivered}
            canWriteOrderDeliveryDriver={canWriteOrderDeliveryDriver}
            canWriteOrderActualTonnage={canWriteOrderActualTonnage}
            assignOrdersEndOfDayLoad={assignOrdersEndOfDayLoad}
            removeOrdersEndOfDayLoad={removeOrdersEndOfDayLoad}
            clearOrderDeliveredStatus={clearOrderDeliveredStatus}
            clientCorrespondences={clientCorrespondences}
            createOrder={createOrder}
            createOrders={createOrders}
            currentDispatchDates={effectiveDispatchDates}
            currentDispatchOrderCount={dispatchDisplayOrders.length}
            contextualDispatchPeriod={contextualDispatchPeriod}
            deleteOrder={deleteOrder}
            ensureSectionsLoaded={ensureSectionsLoaded}
            isPending={isPending}
            isBrowseMode={orderScope === "browse"}
            isPendingBillingApprovalFilter={isPendingBillingApprovalFilter}
            isOrderBrowserOpen={isOrderBrowserOpen}
            isRecentBrowseLimited={isRecentBrowseWithoutDate && !normalizedDeferredOrdersSearchQuery && !requireFullOrdersCatalog}
            onApplyOrderBrowser={handleApplyOrderBrowser}
            onCloseOrderBrowser={handleCloseOrderBrowser}
            onOpenOrderBrowser={handleOpenOrderBrowser}
            onQuickSelectOrderBrowseDate={handleQuickSelectOrderBrowseDate}
            onTogglePendingBillingApprovalFilter={handleTogglePendingBillingApprovalFilter}
            onRequireFullOrdersCatalog={handleRequireFullOrdersCatalog}
            onResetOrderScope={handleResetOrderScope}
            onSearchQueryChange={handleChangeOrdersSearchQuery}
            orderBrowseDateFilter={orderBrowseDateFilter}
            orders={filteredOrders}
            pendingBillingApprovalCount={state.pending_billing_approval_count}
            refreshState={refreshState}
            saveColumnPreferences={saveColumnPreferencesFn}
            savedColumnPrefs={savedColumnPrefs}
            searchQuery={ordersSearchQuery}
            totalOrderCount={orders.length}
            trucks={state.trucks}
            updateOrder={updateOrder}
            updateOrderExecution={updateOrderExecution}
            validateOrderAddress={validateOrderAddress}
          />
        </article>
      ) : null}

      {consolidationNavigation.dialog}
      {status === "ready" && historyView !== "full" && activeTab === "correspondences" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          <DataManagerClientTypeFilter value={clientTypeFilter} counts={clientTypeCounts}
            disabled={isCreatingClientCorrespondence || Boolean(editingClientCorrespondenceId) || isDeletingSelection}
            onChange={(value) => { setSelectedClientCorrespondenceIds({}); setSearchParams(current => {
              const next = new URLSearchParams(current); if (value !== "all") next.set(CLIENT_TYPE_URL_PARAM, value); else next.delete(CLIENT_TYPE_URL_PARAM); return next;
            }); }} />
          {filteredClientCorrespondences.length === 0 && !isCreatingClientCorrespondence ? (
            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Aucun résultat</p>
              <p className="mt-2 text-[13px] text-slate-600">Aucun client ne correspond à la recherche courante.</p>
            </div>
          ) : (
            <>
              <div ref={clientCorrespondenceScrollRef} className={DATA_MANAGER_GRID_SCROLL_CONTAINER_CLASS_NAME}>
                <DataManagerOrdersColumnContextMenu {...clientCorrespondenceFrozenPanes.menuProps} />
                <table className="data-manager-frozen-grid border-collapse text-[11px] text-slate-700" style={clientCorrespondenceTableStyle}>
                  <thead className={DATA_MANAGER_GRID_STICKY_HEADER_CLASS_NAME}>
                    <tr>
                      <DataManagerGridSelectionHeaderCell
                        onResizePointerDown={clientCorrespondenceColumnPrefs.handleResizePointerDown}
                        style={{ ...clientCorrespondenceSelectionColumnStyle, ...clientCorrespondenceFrozenPanes.selectionHeaderStyle }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Sélectionner tous les clients visibles"
                          checked={allVisibleClientCorrespondencesSelected}
                          disabled={deletableVisibleClientCorrespondences.length === 0 || isDeletingSelection}
                          onChange={(event) => handleToggleAllVisibleClientCorrespondenceSelections(event.target.checked)}
                          className={GRID_SELECTION_INPUT_CLASS_NAME}
                        />
                      </DataManagerGridSelectionHeaderCell>
                      {clientCorrespondenceFrozenPanes.columns.map((column) => (
                        <DataManagerSortableHeaderCell
                          key={column.key}
                          className={column.widthClassName}
                          columnKey={column.key}
                          columnWidth={column.width}
                          defaultColumnWidth={column.defaultWidth}
                          draggable={!column.fixed}
                          label={column.label}
                          frozenLayoutEntry={column.frozenLayoutEntry}
                          onColumnContextMenu={clientCorrespondenceFrozenPanes.onColumnContextMenu}
                          onDrop={clientCorrespondenceColumnPrefs.handleColumnMove}
                          onResizePointerDown={column.fixed ? null : clientCorrespondenceColumnPrefs.handleResizePointerDown}
                          onSort={handleClientCorrespondenceSort}
                          sortState={clientCorrespondenceSortState}
                        />
                      ))}
                      <th className={GRID_HEADER_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {isCreatingClientCorrespondence ? (
                      <NewClientCorrespondenceGridRow
                        draft={newClientCorrespondenceDraft}
                        onCancel={handleCancelCreatingClientCorrespondence}
                        onChange={updateNewClientCorrespondenceDraftValue}
                        onCreate={handleCreateClientCorrespondence}
                        onAddressEnterValidation={handleCreateClientCorrespondenceAddressEnterValidation}
                        registerAddressValidationFieldRef={registerClientCorrespondenceAddressValidationFieldRef}
                        resolvedColumns={clientCorrespondenceFrozenPanes.columns}
                        selectionColumnStyle={{ ...clientCorrespondenceSelectionColumnStyle, ...clientCorrespondenceFrozenPanes.selectionStyle }}
                      />
                    ) : null}
                    {sortedClientCorrespondences.map((correspondence) => (
                      <ClientCorrespondenceGridRow
                        key={correspondence.correspondence_id}
                        correspondence={correspondence}
                        draft={clientCorrespondenceDrafts[correspondence.correspondence_id] || createClientCorrespondenceDraft(correspondence)}
                        editingFocusKey={editingClientCorrespondenceFocusKey}
                        hasDraft={Boolean(clientCorrespondenceDrafts[correspondence.correspondence_id])}
                        isEditing={editingClientCorrespondenceId === correspondence.correspondence_id}
                        isFocused={focusedClientCorrespondenceId === correspondence.correspondence_id}
                        isPending={Boolean(
                          isDeletingSelection
                          || isPending(`client-correspondence:update:${correspondence.correspondence_id}`)
                          || isPending(`client-correspondence:delete:${correspondence.correspondence_id}`)
                        )}
                        isSelected={Boolean(selectedClientCorrespondenceIds[correspondence.correspondence_id])}
                        onCancel={() => handleCancelClientCorrespondenceEdit(correspondence.correspondence_id)}
                        onChange={(field, value) => updateClientCorrespondenceDraftValue(correspondence, field, value)}
                        onDelete={() => confirmRowDeletion("correspondences", setSelectedClientCorrespondenceIds, correspondence.correspondence_id)}
                        onAddressEnterValidation={() => handleClientCorrespondenceAddressEnterValidation(correspondence)}
                        onFocusRow={() => setFocusedClientCorrespondenceId(correspondence.correspondence_id)}
                        onSave={() => handleSaveClientCorrespondence(correspondence)}
                        onSelectionChange={(isSelected) => handleClientCorrespondenceSelectionChange(correspondence.correspondence_id, isSelected)}
                        onStartEdit={canWriteActiveTab ? (focusKey) => handleStartEditingClientCorrespondence(correspondence, focusKey) : null}
                        registerAddressValidationFieldRef={registerClientCorrespondenceAddressValidationFieldRef}
                        resolvedColumns={clientCorrespondenceFrozenPanes.columns}
                        selectionColumnStyle={{ ...clientCorrespondenceSelectionColumnStyle, ...clientCorrespondenceFrozenPanes.selectionStyle }}
                        validateAddress={validateOrderAddress}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {draftedCorrespondenceCount > 0 ? (
                <p className="mt-2 text-[10.5px] leading-4 text-slate-500">{draftedCorrespondenceCount} ligne(s) Client en brouillon local.</p>
              ) : null}
            </>
          )}
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "employees" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          {filteredEmployees.length === 0 && !isCreatingEmployee ? (
            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Aucun résultat</p>
              <p className="mt-2 text-[13px] text-slate-600">Aucun chauffeur ne correspond à la recherche courante.</p>
            </div>
          ) : (
            <>
              <div ref={employeeScrollRef} className={DATA_MANAGER_GRID_SCROLL_CONTAINER_CLASS_NAME}>
                <DataManagerOrdersColumnContextMenu {...employeeFrozenPanes.menuProps} />
                <table className="data-manager-frozen-grid border-collapse text-[11px] text-slate-700" style={employeeTableStyle}>
                  <thead className={DATA_MANAGER_GRID_STICKY_HEADER_CLASS_NAME}>
                    <tr>
                      <DataManagerGridSelectionHeaderCell
                        onResizePointerDown={employeeColumnPrefs.handleResizePointerDown}
                        style={{ ...employeeSelectionColumnStyle, ...employeeFrozenPanes.selectionHeaderStyle }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Sélectionner tous les chauffeurs visibles"
                          checked={allVisibleEmployeesSelected}
                          disabled={deletableVisibleEmployees.length === 0 || isDeletingSelection}
                          onChange={(event) => handleToggleAllVisibleEmployeeSelections(event.target.checked)}
                          className={GRID_SELECTION_INPUT_CLASS_NAME}
                        />
                      </DataManagerGridSelectionHeaderCell>
                      {employeeFrozenPanes.columns.map((column) => (
                        <DataManagerSortableHeaderCell
                          key={column.key}
                          className={column.widthClassName}
                          columnKey={column.key}
                          columnWidth={column.width}
                          defaultColumnWidth={column.defaultWidth}
                          draggable={!column.fixed}
                          label={column.label}
                          frozenLayoutEntry={column.frozenLayoutEntry}
                          onColumnContextMenu={employeeFrozenPanes.onColumnContextMenu}
                          onDrop={employeeColumnPrefs.handleColumnMove}
                          onResizePointerDown={column.fixed ? null : employeeColumnPrefs.handleResizePointerDown}
                          onSort={handleEmployeeSort}
                          sortState={employeeSortState}
                        />
                      ))}
                      <th className={GRID_HEADER_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {isCreatingEmployee ? (
                      <NewEmployeeGridRow
                        draft={newEmployeeDraft}
                        onCancel={handleCancelCreatingEmployee}
                        onChange={updateNewEmployeeDraftValue}
                        onCreate={handleCreateEmployee}
                        resolvedColumns={employeeFrozenPanes.columns}
                        selectionColumnStyle={{ ...employeeSelectionColumnStyle, ...employeeFrozenPanes.selectionStyle }}
                      />
                    ) : null}
                    {sortedEmployees.map((employee) => (
                      <EmployeeGridRow
                        key={employee.employee_id}
                        draft={employeeDrafts[employee.employee_id] || createEmployeeDraft(employee)}
                        editingFocusKey={editingEmployeeFocusKey}
                        employee={employee}
                        hasDraft={Boolean(employeeDrafts[employee.employee_id])}
                        isEditing={editingEmployeeId === employee.employee_id}
                        isFocused={focusedEmployeeId === employee.employee_id}
                        isPending={Boolean(
                          isDeletingSelection
                          || isPending(`employee:update:${employee.employee_id}`)
                          || isPending(`employee:delete:${employee.employee_id}`)
                        )}
                        isSelected={Boolean(selectedEmployeeIds[employee.employee_id])}
                        onCancel={() => handleCancelEmployeeEdit(employee.employee_id)}
                        onChange={(field, value) => updateEmployeeDraftValue(employee, field, value)}
                        onDelete={() => confirmRowDeletion("employees", setSelectedEmployeeIds, employee.employee_id)}
                        onFocusRow={() => setFocusedEmployeeId(employee.employee_id)}
                        onSave={() => handleSaveEmployee(employee)}
                        onSelectionChange={(isSelected) => handleEmployeeSelectionChange(employee.employee_id, isSelected)}
                        onStartEdit={canWriteActiveTab ? (focusKey) => handleStartEditingEmployee(employee, focusKey) : null}
                        resolvedColumns={employeeFrozenPanes.columns}
                        selectionColumnStyle={{ ...employeeSelectionColumnStyle, ...employeeFrozenPanes.selectionStyle }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {draftedEmployeeCount > 0 ? (
                <p className="mt-2 text-[10.5px] leading-4 text-slate-500">{draftedEmployeeCount} ligne(s) Chauffeur en brouillon local.</p>
              ) : null}
            </>
          )}
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "product_catalog" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          <DataManagerProductCatalogTab products={state.product_catalog} searchQuery={deferredSearchQuery}
            canWrite={hasPermission(access, "data_manager.products.write")} isPending={isPending}
            onCreate={createCatalogProduct} onUpdate={updateCatalogProduct} onDelete={deleteCatalogProduct} />
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "products" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          {filteredProducts.length === 0 && !isCreatingProduct ? (
            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Aucun résultat</p>
              <p className="mt-2 text-[13px] text-slate-600">Aucun code produit ne correspond à la recherche courante.</p>
            </div>
          ) : (
            <>
              <div ref={productScrollRef} className={DATA_MANAGER_GRID_SCROLL_CONTAINER_CLASS_NAME}>
                <DataManagerOrdersColumnContextMenu {...productFrozenPanes.menuProps} />
                <table className="data-manager-frozen-grid border-collapse text-[11px] text-slate-700" style={productTableStyle}>
                  <thead className={DATA_MANAGER_GRID_STICKY_HEADER_CLASS_NAME}>
                    <tr>
                      <DataManagerGridSelectionHeaderCell
                        onResizePointerDown={productColumnPrefs.handleResizePointerDown}
                        style={{ ...productSelectionColumnStyle, ...productFrozenPanes.selectionHeaderStyle }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Sélectionner tous les codes produit visibles"
                          checked={allVisibleProductsSelected}
                          disabled={deletableVisibleProducts.length === 0 || isDeletingSelection}
                          onChange={(event) => handleToggleAllVisibleProductSelections(event.target.checked)}
                          className={GRID_SELECTION_INPUT_CLASS_NAME}
                        />
                      </DataManagerGridSelectionHeaderCell>
                      {productFrozenPanes.columns.map((column) => (
                        <DataManagerSortableHeaderCell
                          key={column.key}
                          className={column.widthClassName}
                          columnKey={column.key}
                          columnWidth={column.width}
                          defaultColumnWidth={column.defaultWidth}
                          draggable={!column.fixed}
                          label={column.label}
                          frozenLayoutEntry={column.frozenLayoutEntry}
                          onColumnContextMenu={productFrozenPanes.onColumnContextMenu}
                          onDrop={productColumnPrefs.handleColumnMove}
                          onResizePointerDown={column.fixed ? null : productColumnPrefs.handleResizePointerDown}
                          onSort={handleProductSort}
                          sortState={productSortState}
                        />
                      ))}
                      <th className={GRID_HEADER_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {isCreatingProduct ? (
                      <NewProductGridRow
                        productCatalog={state.product_catalog}
                        draft={newProductDraft}
                        onCancel={handleCancelCreatingProduct}
                        onChange={updateNewProductDraftValue}
                        onCreate={handleCreateProduct}
                        resolvedColumns={productFrozenPanes.columns}
                        selectionColumnStyle={{ ...productSelectionColumnStyle, ...productFrozenPanes.selectionStyle }}
                      />
                    ) : null}
                    {sortedProducts.map((product) => (
                      <ProductGridRow
                        productCatalog={state.product_catalog}
                        key={product.product_id}
                        draft={productDrafts[product.product_id] || createProductDraft(product)}
                        editingFocusKey={editingProductFocusKey}
                        hasDraft={Boolean(productDrafts[product.product_id])}
                        isEditing={editingProductId === product.product_id}
                        isFocused={focusedProductId === product.product_id}
                        isPending={Boolean(
                          isDeletingSelection
                          || isPending(`product:update:${product.product_id}`)
                          || isPending(`product:delete:${product.product_id}`)
                        )}
                        isSelected={Boolean(selectedProductIds[product.product_id])}
                        onCancel={() => handleCancelProductEdit(product.product_id)}
                        onChange={(field, value) => updateProductDraftValue(product, field, value)}
                        onDelete={() => confirmRowDeletion("products", setSelectedProductIds, product.product_id)}
                        onFocusRow={() => setFocusedProductId(product.product_id)}
                        onSave={() => handleSaveProduct(product)}
                        onSelectionChange={(isSelected) => handleProductSelectionChange(product.product_id, isSelected)}
                        onStartEdit={canWriteActiveTab ? (focusKey) => handleStartEditingProduct(product, focusKey) : null}
                        product={product}
                        resolvedColumns={productFrozenPanes.columns}
                        selectionColumnStyle={{ ...productSelectionColumnStyle, ...productFrozenPanes.selectionStyle }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {draftedProductCount > 0 ? (
                <p className="mt-2 text-[10.5px] leading-4 text-slate-500">{draftedProductCount} ligne(s) Code produit en brouillon local.</p>
              ) : null}
            </>
          )}
        </article>
      ) : null}

      {status === "ready" && historyView !== "full" && activeTab === "compartments" ? (
        <article className={PANEL_CLASS_NAME}>
          {panelToolbar}
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            <DataManagerFleetFilter value={fleetFilters.compartments} counts={fleetFilterCounts(searchedTrucks, "compartments", fleetToday)}
              retiredLabel="Retirées" disabled={Boolean(newTrailerDraft) || Boolean(editingCompartmentTruckId) || isDeletingSelection}
              onChange={(value) => { setSelectedCompartmentTruckIds({}); setFleetFilters((current) => ({ ...current, compartments: value })); }} />
            <p className="m-0 text-xs text-slate-500">Une remorque seule attend son camion : au Gantt, « Ajouter » › « Une remorque », puis « Changer de camion ». Les dompeurs se règlent dans Camions.</p>
          </div>
          {filteredTrucks.length === 0 && !newTrailerDraft ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Aucun résultat</p>
              <p className="mt-2 text-[13px] text-slate-600">Aucune remorque ou compartiment ne correspond à la recherche courante.</p>
            </div>
          ) : (
            <>
              <div ref={compartmentScrollRef} className={GRID_SCROLL_WRAP_CLASS_NAME}>
                <div className={GRID_SCROLL_INNER_CLASS_NAME}>
                  <DataManagerOrdersColumnContextMenu {...compartmentFrozenPanes.menuProps} />
                  <table className="data-manager-frozen-grid border-collapse text-[11px] text-slate-700" style={compartmentTableStyle}>
                    <thead className="bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.95))]">
                      <tr>
                        <DataManagerGridSelectionHeaderCell
                          onResizePointerDown={compartmentColumnPrefs.handleResizePointerDown}
                          style={{ ...compartmentSelectionColumnStyle, ...compartmentFrozenPanes.selectionHeaderStyle }}
                        >
                          <input
                            type="checkbox"
                            aria-label="Sélectionner toutes les lignes de compartiments visibles"
                            checked={allVisibleCompartmentTrucksSelected}
                            disabled={deletableVisibleCompartmentTrucks.length === 0 || isDeletingSelection}
                            onChange={(event) => handleToggleAllVisibleCompartmentTruckSelections(event.target.checked)}
                            className={GRID_SELECTION_INPUT_CLASS_NAME}
                          />
                        </DataManagerGridSelectionHeaderCell>
                        {compartmentFrozenPanes.columns.map((column) => (
                          <DataManagerSortableHeaderCell
                            key={column.key}
                            align={column.position ? "center" : "left"}
                            className={column.position ? "text-center" : column.widthClassName}
                            columnKey={column.key}
                            columnWidth={column.width}
                            defaultColumnWidth={column.defaultWidth}
                            draggable={!column.fixed}
                            label={column.label}
                            frozenLayoutEntry={column.frozenLayoutEntry}
                            onColumnContextMenu={compartmentFrozenPanes.onColumnContextMenu}
                            onDrop={compartmentColumnPrefs.handleColumnMove}
                            onResizePointerDown={column.fixed ? null : compartmentColumnPrefs.handleResizePointerDown}
                            onSort={handleCompartmentSort}
                            sortState={compartmentSortState}
                          />
                        ))}
                        <th className={GRID_HEADER_CELL_CLASS_NAME} style={DATA_MANAGER_GRID_ACTIONS_COLUMN_STYLE}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {newTrailerDraft ? (
                        <NewTrailerMatrixRow
                          draft={newTrailerDraft}
                          onCancel={() => setNewTrailerDraft(null)}
                          onChange={(field, value) => setNewTrailerDraft((current) => ({ ...current, [field]: value }))}
                          onChangePosition={(position, value) => setNewTrailerDraft((current) => ({ ...current, valuesByPosition: { ...current.valuesByPosition, [position]: value } }))}
                          onCreate={handleCreateTrailer}
                          productCatalog={state.product_catalog}
                          resolvedColumns={compartmentFrozenPanes.columns}
                          selectionColumnStyle={{ ...compartmentSelectionColumnStyle, ...compartmentFrozenPanes.selectionStyle }}
                        />
                      ) : null}
                      {sortedCompartmentTrucks.map((truck) => (
                        <CompartmentMatrixRow
                          key={truck.truck_id}
                          draft={compartmentMatrixDrafts[truck.truck_id] || createCompartmentMatrixDraft(truck, compartmentMatrixPositions)}
                          editingFocusKey={editingCompartmentTruckFocusKey}
                          hasDraft={Boolean(compartmentMatrixDrafts[truck.truck_id])}
                          isEditing={editingCompartmentTruckId === truck.truck_id}
                          isFocused={focusedCompartmentTruckId === truck.truck_id}
                          isPending={Boolean(
                            isDeletingSelection
                            || isPending(`compartment-truck:update:${truck.truck_id}`)
                            || isPending(`truck:update:${truck.truck_id}`)
                            || isPending(`compartment:create:${truck.truck_id}`)
                            || truck.compartments.some((compartment) => (
                              isPending(`compartment:update:${truck.truck_id}:${compartment.compartment_id}`)
                              || isPending(`compartment:delete:${truck.truck_id}:${compartment.compartment_id}`)
                            ))
                          )}
                          isSelected={Boolean(selectedCompartmentTruckIds[truck.truck_id])}
                          onCancel={() => handleCancelCompartmentTruckEdit(truck.truck_id)}
                          onChangeTruckField={(field, value) => updateCompartmentMatrixTruckFieldValue(truck, field, value)}
                          onChangePosition={(position, value) => updateCompartmentMatrixDraftValue(truck, position, value)}
                          onFocusRow={() => setFocusedCompartmentTruckId(truck.truck_id)}
                          onSave={() => handleSaveCompartmentTruck(truck)}
                          onSelectionChange={(isSelected) => handleCompartmentTruckSelectionChange(truck.truck_id, isSelected)}
                          onStartEdit={canWriteActiveTab ? (focusKey) => handleStartEditingCompartmentTruck(truck, focusKey) : null}
                          productCatalog={state.product_catalog}
                          resolvedColumns={compartmentFrozenPanes.columns}
                          selectionColumnStyle={{ ...compartmentSelectionColumnStyle, ...compartmentFrozenPanes.selectionStyle }}
                          selectionTitle={truck.compartments.length > 0 ? "Sélectionner cette ligne de compartiments" : "Aucun compartiment à supprimer sur cette ligne."}
                          truck={truck}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {draftedCompartmentTruckCount > 0 ? (
                <p className="mt-2 text-[10.5px] leading-4 text-slate-500">{draftedCompartmentTruckCount} remorque(s) en brouillon local.</p>
              ) : null}
            </>
          )}
        </article>
      ) : null}

      {status === "ready" && historyView === "full" ? (
        <HistoryPanel
          activeTab={activeTab}
          history={state.history}
          isFullPage
          isLoaded={isHistoryLoaded}
          isLoading={isHistoryLoading}
          onCloseFullPage={handleCloseFullHistory}
          onLoadAuditDetails={activeTab === "orders" ? loadHistoryAuditDetails : null}
          searchQuery={activeHistorySearchQuery}
        />
      ) : null}

      {employeeIdentityConfirmation ? <ConfirmationModal {...employeeIdentityConfirmation} cornerRadius="grain" contentDensity="compact" /> : null}
      {fleetRetirementDialog ? <DataManagerFleetRetirementDialog {...fleetRetirementDialog}
        isBusy={isPending(`truck:${fleetRetirementDialog.mode}:${fleetRetirementDialog.truck.truck_id}`)}
        onCancel={() => setFleetRetirementDialog(null)}
        onConfirm={async (payload) => {
          const { truck, mode } = fleetRetirementDialog;
          try {
            await retireTruck(truck.truck_id, payload, { reinstate: mode === "reinstate" });
          } catch {
            return; // Le refus est affiché ; la fenêtre reste ouverte.
          }
          setFleetRetirementDialog(null);
          setSelectedTruckIds({});
          setSelectedCompartmentTruckIds({});
        }} /> : null}
      {dompeurConversionConfirmation ? <ConfirmationModal {...dompeurConversionConfirmation} cornerRadius="grain" contentDensity="compact" /> : null}
      {truckTypeConfirmation ? <ConfirmationModal {...truckTypeConfirmation} cornerRadius="grain" contentDensity="compact" /> : null}
      {pmTargetSuspensionConfirmation ? <ConfirmationModal {...pmTargetSuspensionConfirmation} /> : null}
      {compartmentSharingConfirmation ? <ConfirmationModal {...compartmentSharingConfirmation} cornerRadius="grain" contentDensity="compact" /> : null}
      {clientRenameCandidate ? (
        <ConfirmationModal
          confirmLabel="Renommer partout"
          description="Le nouveau nom s’applique tout de suite, sans recalcul des tournées."
          eyebrow="Renommer un client"
          impactItems={buildClientRenameImpactItems(clientRenameCandidate.preview)}
          isBusy={isPending(`client-correspondence:update:${clientRenameCandidate.correspondence.correspondence_id}`)}
          onCancel={() => setClientRenameCandidate(null)}
          onClose={() => setClientRenameCandidate(null)}
          onConfirm={async () => {
            const { correspondence, payload } = clientRenameCandidate;
            setClientRenameCandidate(null);
            await saveClientCorrespondence(correspondence, payload);
          }}
          panelSize="compact"
          title={`Renommer « ${clientRenameCandidate.correspondence.client_name} » en « ${clientRenameCandidate.name} » partout ?`}
          tone="accent"
        />
      ) : null}
      {deleteSelectionDialogModel ? (
        <ConfirmationModal
          confirmLabel={isDeletingSelection ? "Suppression..." : "Supprimer"}
          description={deleteSelectionDialogModel.description}
          eyebrow="Suppression"
          impactItems={deleteSelectionDialogModel.impactItems}
          onCancel={() => setDeleteSelectionDialogSection("")}
          onClose={() => setDeleteSelectionDialogSection("")}
          onConfirm={handleConfirmDeleteSelection}
          summaryItems={deleteSelectionDialogModel.summaryItems}
          title={deleteSelectionDialogModel.title}
          tone="danger"
          warningMessage={deleteSelectionDialogModel.warningMessage}
        />
      ) : null}

      {missingCivicNumberDialog}

      <DataManagerDispatchSavesImportDialog
        candidates={dispatchSavesImportDialog.candidates}
        errorMessage={dispatchSavesImportDialog.errorMessage}
        isOpen={dispatchSavesImportDialog.isOpen}
        isSubmitting={isPending("import:dispatch-saves")}
        onCancel={handleCloseDispatchSavesImportDialog}
        onConfirm={handleConfirmDispatchSavesImport}
      />

      {canUseAdminTransferPanel && historyView !== "full" ? (
        <details className={`${PANEL_CLASS_NAME} data-manager-admin-transfer`}>
          <summary>Exports et imports admin</summary>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="mt-1 text-[1rem] font-semibold tracking-[-0.03em] text-slate-950">Transférer les tables métier et les sauvegardes dispatch</h3>
              <p className="mt-1 text-[12.5px] leading-6 text-slate-600">
                Exportez les données courantes du Data Manager ou importez un fichier JSON de sauvegardes dispatch depuis un autre environnement.
                {contextualDispatchDate ? ` Les sauvegardes dispatch sont filtrées sur la journée ${contextualDispatchDate}.` : " Les sauvegardes dispatch exportent toutes les journées disponibles."}
              </p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] bg-accent text-white"><Database size={17} /></span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {canExportTable("orders") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("orders")}
                onExport={() => handleDownloadExport("orders")}
                title="Commandes"
              />
            ) : null}
            {canExportTable("trucks") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("trucks")}
                onExport={() => handleDownloadExport("trucks")}
                title="Camions"
              />
            ) : null}
            {canExportTable("compartments") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("compartments")}
                onExport={() => handleDownloadExport("compartments")}
                title="Remorques"
              />
            ) : null}
            {canExportTable("client_correspondences") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("client_correspondences")}
                onExport={() => handleDownloadExport("client_correspondences")}
                title="Clients"
              />
            ) : null}
            {canExportTable("employees") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("employees")}
                onExport={() => handleDownloadExport("employees")}
                title="Chauffeurs"
              />
            ) : null}
            {canExportTable("products") ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("products")}
                onExport={() => handleDownloadExport("products")}
                title="Codes produit"
              />
            ) : null}
            {canExportTable("dispatch_saves") || canManageImports ? (
              <AdminTransferCard
                disabledExport={isExportCardBusy("dispatch_saves")}
                disabledImport={isPending("import:dispatch-saves")}
                onExport={canExportTable("dispatch_saves") ? () => handleDownloadExport("dispatch_saves") : null}
                onImport={canManageImports ? handleImportDispatchSaves : null}
                title="Sauvegardes dispatch"
              />
            ) : null}
          </div>
        </details>
      ) : null}

      {status === "ready" && historyView !== "full" ? (
        <HistoryPanel
          activeTab={activeTab}
          history={state.history}
          isCollapsed={Boolean(collapsedHistorySections[activeTab])}
          isLoaded={isHistoryLoaded}
          isLoading={isHistoryLoading}
          onOpenFullPage={handleOpenFullHistory}
          onToggleCollapsed={() => handleToggleHistoryPanel(activeTab)}
          searchQuery={activeHistorySearchQuery}
        />
      ) : null}
    </section>
    </BillToCompaniesContext.Provider>
  );
}
